import axios from 'axios';

import various from '../../../General/various';
import { requestYowanWithAuthRetry } from './yowanAuthClient';
import { readYowanStoredSnapshot } from './yowanAuthStorage';

export const YOWAN_CONTENT_ADMIN_PAGE_SIZE = 30;
export const YOWAN_CONTENT_PUBLIC_PAGE_SIZE = 30;
export const YOWAN_PUBLIC_READ_CACHE_TTL_MS = 5 * 60 * 1000;
export const YOWAN_IMAGE_UPLOAD_MAX_BYTES = 5 * 1024 * 1024;
export const YOWAN_IMAGE_UPLOAD_MAX_LABEL = '5MB';
export const YOWAN_IMAGE_UPLOAD_ACCEPT_LABEL = 'JPEG / PNG / WebPなど、ブラウザが画像として認識できる形式';
export const YOWAN_IMAGE_UPLOAD_HELP_TEXT = `アップロード画像は${YOWAN_IMAGE_UPLOAD_MAX_LABEL}以下、${YOWAN_IMAGE_UPLOAD_ACCEPT_LABEL}を選択してください。`;
export const YOWAN_IMAGE_UPLOAD_SIZE_ERROR_MESSAGE = `画像ファイルは${YOWAN_IMAGE_UPLOAD_MAX_LABEL}以下にしてください。`;
export const YOWAN_IMAGE_UPLOAD_TYPE_ERROR_MESSAGE =
  '画像ファイル形式を確認してください。JPEG / PNG / WebPなど、ブラウザが画像として認識できる形式を選択してください。';

const yowanPublicReadCache = new Map();

const stripLeadingSlash = (value) => value.replace(/^\/+/, '');

const buildYowanApiUrl = (path) => {
  const normalizedPath = stripLeadingSlash(path);
  const apiPath = normalizedPath.startsWith('api/yowan/')
    ? normalizedPath
    : `api/yowan/${normalizedPath}`;

  return `${various.currentDomain}${apiPath}`;
};

const readAccessToken = () => readYowanStoredSnapshot().accessToken;

const buildHeaders = ({ auth = false, accessToken: accessTokenOverride } = {}) => {
  const headers = {
    'Content-Type': 'application/json; charset=utf-8',
  };
  const accessToken = accessTokenOverride || readAccessToken();

  if (auth && accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  return headers;
};

const getJson = async (path, { params, auth = false } = {}) => {
  const execute = (accessToken) =>
    axios.get(buildYowanApiUrl(path), {
      headers: buildHeaders({ auth, accessToken }),
      params,
    });
  const response = auth ? await requestYowanWithAuthRetry(execute) : await execute();

  return response.data;
};

const buildPublicReadCacheKey = (path, params = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params || {})
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
    .forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((entry) => searchParams.append(key, String(entry)));
        return;
      }

      searchParams.set(key, String(value));
    });

  const queryString = searchParams.toString();

  return queryString ? `${path}?${queryString}` : path;
};

const getPublicCachedJson = async (path, { params, bypassCache = false } = {}) => {
  const cacheKey = buildPublicReadCacheKey(path, params);
  const now = Date.now();
  const cached = yowanPublicReadCache.get(cacheKey);

  if (!bypassCache && cached && cached.expiresAt > now) {
    return cached.data;
  }

  const data = await getJson(path, { params });

  yowanPublicReadCache.set(cacheKey, {
    data,
    expiresAt: now + YOWAN_PUBLIC_READ_CACHE_TTL_MS,
  });

  return data;
};

export const fetchYowanPublicRecaptchaConfig = async ({ bypassCache = false } = {}) => {
  const payload = await getPublicCachedJson('/api/yowan/public/recaptcha/config/', { bypassCache });
  const actions = payload?.actions && typeof payload.actions === 'object' ? payload.actions : {};

  return {
    enabled: payload?.enabled === true,
    siteKey: payload?.site_key || payload?.siteKey || '',
    actions: {
      contactInquiry: actions.contact_inquiry || actions.contactInquiry || 'yowan_contact_inquiry',
      newsSignup: actions.news_signup || actions.newsSignup || 'yowan_news_signup',
      seminarApplication: actions.seminar_application || actions.seminarApplication || 'yowan_seminar_application',
      eventApplication: actions.event_application || actions.eventApplication || actions.seminar_application || actions.seminarApplication || 'yowan_seminar_application',
      classApplication: actions.class_application || actions.classApplication || 'yowan_class_application',
    },
  };
};

export const invalidateYowanPublicReadCache = (matcher) => {
  if (typeof matcher !== 'function') {
    yowanPublicReadCache.clear();
    return;
  }

  Array.from(yowanPublicReadCache.keys()).forEach((key) => {
    if (matcher(key)) {
      yowanPublicReadCache.delete(key);
    }
  });
};

const postJson = async (path, payload, { auth = true } = {}) => {
  const execute = (accessToken) =>
    axios.post(buildYowanApiUrl(path), payload, {
      headers: buildHeaders({ auth, accessToken }),
    });
  const response = auth ? await requestYowanWithAuthRetry(execute) : await execute();

  return response.data;
};

const patchJson = async (path, payload, { auth = true } = {}) => {
  const execute = (accessToken) =>
    axios.patch(buildYowanApiUrl(path), payload, {
      headers: buildHeaders({ auth, accessToken }),
    });
  const response = auth ? await requestYowanWithAuthRetry(execute) : await execute();

  return response.data;
};

const postFormData = async (path, formData, { auth = true } = {}) => {
  const execute = (accessToken) => {
    const headers = {};
    const token = accessToken || readAccessToken();

    if (auth && token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return axios.post(buildYowanApiUrl(path), formData, {
      headers,
    });
  };
  const response = auth ? await requestYowanWithAuthRetry(execute) : await execute();

  return response.data;
};

const normalizeBoolean = (value) => value === true || value === 'true' || value === 'on';

const normalizeIsActiveQueryParam = (value) => {
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  if (value === 'all') {
    return 'all';
  }

  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }

  return String(value);
};

const normalizeNullableId = (value) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  return value;
};

const hasOwnProperty = (source = {}, key) => Object.prototype.hasOwnProperty.call(source, key);

const resolveYowanPublicSlugPayload = (values = {}) => {
  const fieldNames = ['publicSlug', 'public_slug', 'seriesPublicSlug', 'series_public_slug'];
  const fieldName = fieldNames.find((candidate) => hasOwnProperty(values, candidate));

  if (!fieldName) {
    return undefined;
  }

  return String(values[fieldName] ?? '').trim();
};

export const getYowanApiFieldErrorMessage = (error, fieldNames = []) => {
  const responseData = error?.response?.data || {};

  for (const fieldName of fieldNames) {
    const value = responseData[fieldName];

    if (Array.isArray(value)) {
      return value.filter(Boolean).join(' ');
    }

    if (typeof value === 'string') {
      return value;
    }

    if (value && typeof value === 'object' && typeof value.detail === 'string') {
      return value.detail;
    }
  }

  return '';
};

export const getYowanImageUploadErrorMessage = (error) => {
  if (error?.isYowanImageUploadSizeError) {
    return YOWAN_IMAGE_UPLOAD_SIZE_ERROR_MESSAGE;
  }

  if (error?.isYowanImageUploadTypeError) {
    return YOWAN_IMAGE_UPLOAD_TYPE_ERROR_MESSAGE;
  }

  const imageMessage = getYowanApiFieldErrorMessage(error, [
    'image',
    'primary_image_upload',
    'primaryImageUpload',
    'detailTopImage',
    'detail_top_image',
    'seriesImage',
    'series_image',
  ]);

  if (!imageMessage) {
    return '';
  }

  if (/5242880|bytes or smaller|file must be/i.test(imageMessage)) {
    return YOWAN_IMAGE_UPLOAD_SIZE_ERROR_MESSAGE;
  }

  if (/unsupported image content type|unsupported image/i.test(imageMessage)) {
    return YOWAN_IMAGE_UPLOAD_TYPE_ERROR_MESSAGE;
  }

  return imageMessage;
};

export const getYowanAdminMutationErrorMessage = (
  error,
  fallbackMessage = '保存できませんでした。入力内容とログイン状態を確認してください。',
) => getYowanImageUploadErrorMessage(error) || fallbackMessage;

const normalizeYowanPublicPath = (value) => {
  const rawValue = String(value || '').trim();

  if (!rawValue) {
    return '';
  }

  try {
    const parsedUrl = new URL(rawValue);
    return `${parsedUrl.pathname}${parsedUrl.search}` || '';
  } catch (error) {
    return rawValue.startsWith('/') ? rawValue : `/${rawValue}`;
  }
};

const YOWAN_DISPLAY_TIME_ZONE = 'Asia/Tokyo';
const yowanTokyoDateTimeFormatter = new Intl.DateTimeFormat('ja-JP-u-ca-gregory', {
  timeZone: YOWAN_DISPLAY_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

const getYowanDateTimePartsFromDate = (date) => {
  const parts = yowanTokyoDateTimeFormatter
    .formatToParts(date)
    .reduce((accumulator, part) => {
      if (['year', 'month', 'day', 'hour', 'minute'].includes(part.type)) {
        accumulator[part.type] = part.value;
      }

      return accumulator;
    }, {});

  if (!parts.year || !parts.month || !parts.day || !parts.hour || !parts.minute) {
    return null;
  }

  return {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour: parts.hour,
    minute: parts.minute,
  };
};

const getYowanTokyoDateTimeParts = (value) => {
  if (!value) {
    return null;
  }

  const normalizedValue = String(value).trim().replace(' ', 'T');
  const dateOnlyMatch = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const localInputMatch = normalizedValue.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?$/,
  );

  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;

    return { year, month, day, hour: '00', minute: '00' };
  }

  if (localInputMatch) {
    const [, year, month, day, hour, minute] = localInputMatch;

    return { year, month, day, hour, minute };
  }

  const date = new Date(normalizedValue);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return getYowanDateTimePartsFromDate(date);
};

const toDateInputValue = (value) => {
  const parts = getYowanTokyoDateTimeParts(value);

  if (!parts) {
    return '';
  }

  return `${parts.year}-${parts.month}-${parts.day}`;
};

export const toDateTimeLocalInputValue = (value) => {
  const parts = getYowanTokyoDateTimeParts(value);

  if (!parts) {
    return '';
  }

  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

export const formatYowanAdminDateTime = (value) => {
  if (!value) {
    return '-';
  }

  const parts = getYowanTokyoDateTimeParts(value);

  if (!parts) {
    const fallback = String(value).replace('T', ' ').slice(0, 16);
    return fallback || '-';
  }

  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
};

const normalizePaginationEnvelope = (payload = {}) => ({
  count: Number.isFinite(payload.count) ? payload.count : 0,
  next: payload.next || null,
  previous: payload.previous || null,
  results: Array.isArray(payload.results) ? payload.results : [],
});

const getMediaAssetFileName = (asset = {}) => {
  const source = asset.file_name || asset.filename || asset.image_name || asset.url || asset.thumbnail_url || '';
  const fileName = String(source).split(/[?#]/)[0].split('/').filter(Boolean).pop() || '';

  try {
    return decodeURIComponent(fileName);
  } catch (error) {
    return fileName;
  }
};

export const getYowanImagePreviewSrc = (image = {}) =>
  image?.preview_url || image?.previewUrl || image?.thumbnail_url || image?.thumbnailUrl || image?.url || '';

export const getYowanImageDisplayName = (image = {}) =>
  image?.caption || image?.alt_text || image?.altText || image?.fileName || getMediaAssetFileName(image) || '';

export const normalizeContentMediaAsset = (asset = {}) => ({
  id: asset.id || '',
  url: asset.url || '',
  thumbnail_url: asset.thumbnail_url || asset.url || '',
  preview_url: asset.preview_url || asset.thumbnail_url || asset.url || '',
  fileName: getMediaAssetFileName(asset),
  alt_text: asset.alt_text || '',
  caption: asset.caption || '',
  usage_scope: asset.usage_scope || 'shared',
  is_active: asset.is_active !== false,
});

export const buildContentMediaAssetOptions = (assets = [], currentAsset = null) => {
  const normalizedAssets = assets.map(normalizeContentMediaAsset);
  const currentAssetId = currentAsset?.id || '';
  const normalizedCurrentAsset = currentAssetId ? normalizeContentMediaAsset(currentAsset) : null;
  const mergedAssets = currentAssetId
    ? [
        normalizedCurrentAsset,
        ...normalizedAssets.filter((asset) => asset.id !== currentAssetId),
      ]
    : normalizedAssets;

  return mergedAssets.map((asset) => ({
    value: asset.id,
    label: asset.caption || asset.fileName || asset.alt_text || asset.id,
    description: asset.alt_text,
    url: asset.url,
    thumbnail_url: asset.thumbnail_url,
    preview_url: asset.preview_url,
    fileName: asset.fileName,
    isActive: asset.is_active,
    isCurrent: Boolean(currentAssetId && asset.id === currentAssetId),
  }));
};

export const normalizeYowanPerson = (item = null) => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const usageSource = item.person_usage_summary || item.personUsageSummary || item.usage_summary || item.usageSummary || {};
  const relatedRoles = Array.isArray(item.person_related_roles || item.personRelatedRoles)
    ? item.person_related_roles || item.personRelatedRoles
    : [];

  return {
    id: item.id || '',
    displayName: item.display_name || item.displayName || '',
    nameKana: item.name_kana || item.nameKana || item.kana || item.furigana || '',
    email: item.email || '',
    phone: item.phone || item.phone_number || item.phoneNumber || '',
    organization: item.organization || '',
    roleTitle: item.role_title || item.roleTitle || '',
    internalNote: item.internal_note || item.internalNote || '',
    isActive: item.is_active !== false && item.isActive !== false,
    usageSummary: {
      instructorsCount: Number(usageSource.instructors_count || usageSource.instructorsCount || 0),
      organizersCount: Number(usageSource.organizers_count || usageSource.organizersCount || 0),
      blogAuthorsCount: Number(usageSource.blog_authors_count || usageSource.blogAuthorsCount || 0),
      totalCount: Number(usageSource.total_count || usageSource.totalCount || 0),
      roles: Array.isArray(usageSource.roles) ? usageSource.roles : [],
    },
    relatedRoles: relatedRoles.map((role) => ({
      role: role.role || '',
      id: role.id || '',
      label: role.label || '',
      detailHref: role.detail_href || role.detailHref || '',
      isActive: role.is_active !== false && role.isActive !== false,
    })),
    createdAt: formatYowanAdminDateTime(item.created_at || item.createdAt),
    updatedAt: formatYowanAdminDateTime(item.updated_at || item.updatedAt),
    raw: item,
  };
};

const normalizeBlogAuthorSummary = (item = null) => {
  if (!item) {
    return null;
  }

  if (typeof item === 'string') {
    return {
      id: '',
      displayName: item,
      profileUrl: '',
      isActive: true,
    };
  }

  if (typeof item !== 'object') {
    return null;
  }

  const personSource =
    item.person ||
    item.person_ledger ||
    item.personLedger ||
    item.person_profile ||
    item.personProfile ||
    null;

  return {
    id: item.id || '',
    displayName: item.display_name || item.displayName || item.name || '',
    profileUrl: item.profile_url || item.profileUrl || '',
    personId: item.person_id || item.personId || personSource?.id || '',
    person: normalizeYowanPerson(personSource),
    isActive: item.is_active === true || item.isActive === true,
  };
};

const normalizeBlogAuthorDisplayName = (author = null) => {
  if (!author) {
    return '';
  }

  if (typeof author === 'string') {
    return author;
  }

  return author.displayName || author.display_name || author.name || '';
};

export const normalizeAdminPostRow = (item = {}, resource) => ({
  id: item.id || '',
  title: item.title || '',
  slug: item.slug || '',
  excerpt: item.excerpt || '',
  author: normalizeBlogAuthorSummary(item.author || item.blog_author),
  authorId: item.author?.id || item.blog_author?.id || item.author_id || item.blog_author_id || '',
  authorName: normalizeBlogAuthorDisplayName(item.author || item.blog_author),
  isPublished: item.is_published === true,
  publishedAt: toDateTimeLocalInputValue(item.published_at),
  notificationScheduledAt: toDateTimeLocalInputValue(item.notification_scheduled_at),
  notificationSentAt: toDateTimeLocalInputValue(item.notification_sent_at),
  notificationDispatchId: item.notification_dispatch_id || item.notification_dispatch?.id || '',
  notificationDispatch: item.notification_dispatch || null,
  createdAt: item.created_at || '',
  updatedAt: item.updated_at || '',
  publicHref: resource === 'blog' && item.slug && item.is_published ? `/${resource}/${item.slug}` : null,
  editHref: `/admin/content/${resource}/${item.id}/edit`,
  primary_image_id: item.primary_image?.id || '',
  primary_image: item.primary_image || null,
  notify_on_publish: item.notify_on_publish === true,
});

export const normalizeAdminPostDetail = (item = {}, resource) => ({
  ...normalizeAdminPostRow(item, resource),
  body: item.body || '',
});

export const normalizePublicBlogEntry = (item = {}) => ({
  id: item.id || item.slug || '',
  date: toDateInputValue(item.published_at),
  author: normalizeBlogAuthorSummary(item.author || item.blog_author),
  imageLabel: item.primary_image?.alt_text || item.primary_image?.caption || '',
  primary_image_id: item.primary_image?.id || '',
  primary_image: item.primary_image || null,
  title: item.title || '',
  showReadMore: true,
  excerpt: item.excerpt || '',
  slug: item.slug || '',
  href: item.slug ? `/blog/${item.slug}` : '/blog',
});

export const normalizePublicBlogDetail = (item = {}) => ({
  title: item.title || '',
  slug: item.slug || '',
  date: toDateInputValue(item.published_at),
  author: normalizeBlogAuthorSummary(item.author || item.blog_author),
  imageLabel: item.primary_image?.alt_text || item.primary_image?.caption || '',
  primary_image_id: item.primary_image?.id || '',
  primary_image: item.primary_image || null,
  excerpt: item.excerpt || '',
  body: item.body ? String(item.body).split(/\n{2,}/).filter(Boolean) : [],
});

export const normalizeFounderCassette = (item = {}) => ({
  id: item.id || '',
  pageKey: item.page_key || 'founder',
  title: item.title || '',
  body: item.body || '',
  displayOrder: Number(item.display_order || 0),
  isPublished: item.is_published === true,
  primary_image_id: item.primary_image?.id || '',
  primary_image: item.primary_image || null,
  createdAt: item.created_at || item.createdAt || item.created || '',
  updatedAt: item.updated_at || item.updatedAt || item.updated || '',
  editHref: `/admin/content/founder/${item.id}/edit`,
});

export const normalizeReviewEntry = (item = {}, { defaultPublished = true } = {}) => ({
  id: item.id || '',
  title: item.title || '',
  reviewerName: item.reviewer_name || item.reviewerName || '',
  reviewerTitle: item.reviewer_title || item.reviewerTitle || '',
  reviewerUrl: item.reviewer_url || item.reviewerUrl || '',
  body: item.body || '',
  paragraphs: item.body ? String(item.body).split(/\n{2,}/).filter(Boolean) : [],
  displayOrder: Number(item.display_order || 0),
  isPublished: item.is_published === undefined ? defaultPublished : item.is_published === true,
  createdAt: item.created_at || item.createdAt || item.created || '',
  updatedAt: item.updated_at || item.updatedAt || item.updated || '',
  editHref: `/admin/content/reviews/${item.id}/edit`,
});

export const normalizeReviewsHeader = (item = {}) => {
  if (!item) {
    return null;
  }

  return {
    id: item.id || '',
    pageKey: item.page_key || 'reviews',
    slotKey: item.slot_key || 'header',
    title: item.title || '',
    body: item.body || '',
    paragraphs: item.body ? String(item.body).split(/\n{2,}/).filter(Boolean) : [],
    displayOrder: Number(item.display_order || 0),
    isPublished: item.is_published === true,
    primary_image_id: item.primary_image?.id || '',
    primary_image: item.primary_image || null,
    createdAt: item.created_at || '',
    updatedAt: item.updated_at || '',
  };
};

const normalizeYowanEventMediaAsset = (asset = {}) => {
  if (!asset) {
    return null;
  }

  return {
    id: asset.id || '',
    url: asset.url || '',
    thumbnail_url: asset.thumbnail_url || asset.thumbnailUrl || asset.url || '',
    preview_url: asset.preview_url || asset.previewUrl || asset.thumbnail_url || asset.thumbnailUrl || asset.url || '',
    fileName: getMediaAssetFileName(asset),
    alt_text: asset.alt_text || asset.altText || '',
    caption: asset.caption || '',
  };
};

const normalizeYowanAdminEventMemberSummary = (item = {}) => ({
  id: item.id || '',
  name: item.name || '',
  email: item.email || '',
});

export const normalizeYowanAdminFacility = (item = {}) => ({
  id: item.id || '',
  facilityName: item.facility_name || item.facilityName || '',
  shortName: item.short_name || item.shortName || '',
  address: item.address || '',
  phone: item.phone || '',
  gmap: item.gmap || '',
  url: item.url || '',
  accessNote: item.access_note || item.accessNote || '',
  publicLocationLabel: item.public_location_label || item.publicLocationLabel || '',
  privateLocationNote: item.private_location_note || item.privateLocationNote || '',
  primary_image: normalizeYowanEventMediaAsset(item.primary_image),
  primary_image_id: item.primary_image?.id || item.primaryImage?.id || '',
  image: item.primary_image?.preview_url || item.primary_image?.url || '',
  isActive: item.is_active !== false && item.isActive !== false,
  roomCount: Number(item.rooms_count ?? item.roomCount ?? item.rooms?.length ?? 0),
  rooms: Array.isArray(item.rooms) ? item.rooms.map(normalizeYowanAdminRoom) : [],
  remarks: item.remarks || '',
  createdAt: formatYowanAdminDateTime(item.created_at || item.createdAt),
  updatedAt: formatYowanAdminDateTime(item.updated_at || item.updatedAt),
  editHref: `/admin/events/facilities/${item.id}/edit`,
});

export const normalizeYowanAdminRoom = (item = {}) => {
  const facility = item.facility || {};
  const facilityId = item.facility_id || item.facilityId || facility.id || '';

  return {
    id: item.id || '',
    roomName: item.room_name || item.roomName || '',
    facilityId,
    facilityName: facility.facility_name || facility.facilityName || item.facilityName || '',
    capacity: Number(item.capacity || 0),
    isActive: item.is_active !== false && item.isActive !== false,
    remarks: item.remarks || '',
    createdAt: formatYowanAdminDateTime(item.created_at || item.createdAt),
    updatedAt: formatYowanAdminDateTime(item.updated_at || item.updatedAt),
    editHref: `/admin/events/rooms/${item.id}/edit`,
  };
};

const normalizeYowanStakeholderAccess = (item = null) => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const authSubject = item.auth_subject || item.authSubject || {};
  const loginEmail =
    item.login_email ||
    item.loginEmail ||
    item.auth_subject_email ||
    item.authSubjectEmail ||
    authSubject.login_email ||
    authSubject.loginEmail ||
    authSubject.email ||
    item.email ||
    '';
  const isActive = item.is_active !== false && item.isActive !== false && item.stakeholder_access_active !== false;
  const passwordSet =
    item.password_set === true ||
    item.passwordSet === true ||
    item.has_password === true ||
    item.hasPassword === true ||
    authSubject.password_set === true ||
    authSubject.passwordSet === true;
  const lastInvitedRaw =
    item.last_invited_at ||
    item.lastInvitedAt ||
    item.invited_at ||
    item.invitedAt ||
    item.updated_at ||
    item.updatedAt ||
    '';
  const inviteExpiresRaw =
    item.invite_expires_at ||
    item.inviteExpiresAt ||
    item.password_setup_expires_at ||
    item.passwordSetupExpiresAt ||
    '';
  const id = item.id || item.stakeholder_access_id || item.stakeholderAccessId || '';
  const authSubjectId = item.auth_subject_id || item.authSubjectId || authSubject.id || '';

  if (!id && !authSubjectId && !loginEmail) {
    return null;
  }

  return {
    id,
    authSubjectId,
    loginEmail,
    isActive,
    passwordSet,
    passwordSetLabel: passwordSet ? '設定済み' : '未設定',
    statusLabel: isActive ? '有効' : '停止中',
    lastInvitedAt: formatYowanAdminDateTime(lastInvitedRaw),
    inviteExpiresAt: formatYowanAdminDateTime(inviteExpiresRaw),
    createdAt: formatYowanAdminDateTime(item.created_at || item.createdAt),
    updatedAt: formatYowanAdminDateTime(item.updated_at || item.updatedAt),
    raw: item,
  };
};

const stakeholderRecommendedActionLabels = {
  send_auth_invite: '認証招待を送信',
  grant_access: '閲覧権限を付与',
  resend_invite: '招待メールを再送',
  deactivate_access: 'ログイン停止',
  reactivate_access: 'ログイン再開',
  none: '',
};

const stakeholderRecommendedActionReasonLabels = {
  stakeholder_access_missing: '閲覧権限がまだ付与されていません。必要に応じて権限を付与してください。',
  auth_subject_missing: '認証主体がまだ作成されていません。必要に応じて認証招待を送信してください。',
  password_setup_required: 'パスワードが未設定です。必要に応じて認証招待を送信してください。',
  stakeholder_access_inactive: '閲覧権限は停止中です。必要に応じてログインを再開してください。',
};

const normalizeYowanStakeholderActionReasonLabel = (value = '') =>
  stakeholderRecommendedActionReasonLabels[value] || '';

const normalizeYowanStakeholderAuthSummary = (item = null, fallbackAccess = null, roleKey = '') => {
  if (!item || typeof item !== 'object') {
    if (!fallbackAccess) {
      return {
        roleKey,
        loginEmail: '',
        authSubjectExists: false,
        authSubjectId: '',
        passwordSetupRequired: null,
        passwordSetLabel: '-',
        stakeholderAccessExists: false,
        stakeholderAccessId: '',
        stakeholderAccessActive: false,
        accessStatusLabel: '未付与',
        invitedAt: '',
        lastInvitedAt: '',
        latestOnboardingRequestId: '',
        latestOnboardingRequestStatus: '',
        recommendedAction: 'send_auth_invite',
        recommendedActionLabel: stakeholderRecommendedActionLabels.send_auth_invite,
        recommendedActionReason: '',
        recommendedActionReasonLabel: '',
        raw: null,
      };
    }

    return {
      roleKey,
      loginEmail: fallbackAccess.loginEmail || '',
      authSubjectExists: Boolean(fallbackAccess.authSubjectId),
      authSubjectId: fallbackAccess.authSubjectId || '',
      passwordSetupRequired: fallbackAccess.passwordSet === false,
      passwordSetLabel: fallbackAccess.passwordSetLabel || '-',
      stakeholderAccessExists: Boolean(fallbackAccess.id),
      stakeholderAccessId: fallbackAccess.id || '',
      stakeholderAccessActive: fallbackAccess.isActive === true,
      accessStatusLabel: fallbackAccess.statusLabel || (fallbackAccess.isActive ? '有効' : '停止中'),
      invitedAt: fallbackAccess.lastInvitedAt || '',
      lastInvitedAt: fallbackAccess.lastInvitedAt || '',
      latestOnboardingRequestId: '',
      latestOnboardingRequestStatus: '',
      recommendedAction: fallbackAccess.id
        ? (fallbackAccess.passwordSet === false ? 'resend_invite' : 'none')
        : 'send_auth_invite',
      recommendedActionLabel: stakeholderRecommendedActionLabels[
        fallbackAccess.id
          ? (fallbackAccess.passwordSet === false ? 'resend_invite' : 'none')
          : 'send_auth_invite'
      ],
      recommendedActionReason: '',
      recommendedActionReasonLabel: '',
      raw: null,
    };
  }

  const recommendedAction = item.recommended_action || item.recommendedAction || 'none';
  const passwordSetupRequired =
    item.password_setup_required ?? item.passwordSetupRequired ?? (fallbackAccess ? fallbackAccess.passwordSet === false : null);
  const stakeholderAccessExists =
    item.stakeholder_access_exists ?? item.stakeholderAccessExists ?? Boolean(item.stakeholder_access_id || item.stakeholderAccessId || fallbackAccess?.id);
  const stakeholderAccessActive =
    item.stakeholder_access_active ?? item.stakeholderAccessActive ?? fallbackAccess?.isActive ?? false;
  const recommendedActionReason = item.recommended_action_reason || item.recommendedActionReason || '';

  return {
    roleKey: item.role_key || item.roleKey || roleKey,
    loginEmail: item.login_email || item.loginEmail || fallbackAccess?.loginEmail || '',
    authSubjectExists: item.auth_subject_exists ?? item.authSubjectExists ?? Boolean(item.auth_subject_id || item.authSubjectId || fallbackAccess?.authSubjectId),
    authSubjectId: item.auth_subject_id || item.authSubjectId || fallbackAccess?.authSubjectId || '',
    passwordSetupRequired,
    passwordSetLabel: passwordSetupRequired === null || passwordSetupRequired === undefined
      ? fallbackAccess?.passwordSetLabel || '-'
      : passwordSetupRequired ? '未設定' : '設定済み',
    stakeholderAccessExists,
    stakeholderAccessId: item.stakeholder_access_id || item.stakeholderAccessId || fallbackAccess?.id || '',
    stakeholderAccessActive,
    accessStatusLabel: stakeholderAccessExists
      ? (stakeholderAccessActive ? '有効' : '停止中')
      : '未付与',
    invitedAt: formatYowanAdminDateTime(item.invited_at || item.invitedAt),
    lastInvitedAt: formatYowanAdminDateTime(item.last_invited_at || item.lastInvitedAt || item.invited_at || item.invitedAt) || fallbackAccess?.lastInvitedAt || '',
    latestOnboardingRequestId: item.latest_onboarding_request_id || item.latestOnboardingRequestId || '',
    latestOnboardingRequestStatus: item.latest_onboarding_request_status || item.latestOnboardingRequestStatus || '',
    recommendedAction,
    recommendedActionLabel: stakeholderRecommendedActionLabels[recommendedAction] || '',
    recommendedActionReason,
    recommendedActionReasonLabel: normalizeYowanStakeholderActionReasonLabel(recommendedActionReason),
    raw: item,
  };
};

const normalizeYowanStakeholderActionPayload = (payload = {}, accessKeys = [], roleKey = '') => {
  const accessSource = accessKeys.reduce((current, key) => current || payload[key], null) || payload.access || payload;
  const access = normalizeYowanStakeholderAccess(accessSource);
  const authSummary = normalizeYowanStakeholderAuthSummary(
    payload.auth_access_summary || payload.authAccessSummary || accessSource?.auth_access_summary || accessSource?.authAccessSummary || null,
    access,
    roleKey,
  );

  return { access, authSummary };
};

export const normalizeYowanAdminInstructor = (item = {}) => {
  const member = normalizeYowanAdminEventMemberSummary(item.member || {});
  const specialties = Array.isArray(item.specialties)
    ? item.specialties.join(' / ')
    : item.specialties || '';
  const webLinks = normalizeYowanInstructorWebLinks(item.web_links || item.webLinks);
  const webLinkFields = mapYowanInstructorWebLinksToFields(webLinks);
  const personalUrl = item.personal_url || item.personalUrl || webLinkFields.webLinkWebsite || '';
  const instructorAccess = normalizeYowanStakeholderAccess(item.instructor_access || item.instructorAccess || item.access || null);
  const instructorAuthSummary = normalizeYowanStakeholderAuthSummary(item.auth_access_summary || item.authAccessSummary || null, instructorAccess, 'instructor');
  webLinkFields.webLinkWebsite = webLinkFields.webLinkWebsite || personalUrl;

  return {
    id: item.id || '',
    instructorName: item.instructor_name || item.instructorName || '',
    personId: item.person_id || item.personId || item.person?.id || '',
    person: normalizeYowanPerson(item.person),
    memberId: item.member_id || item.memberId || member.id || '',
    member,
    memberName: member.name || '',
    memberEmail: member.email || '',
    instructorEmail: item.instructor_email || item.instructorEmail || '',
    instructorAccess,
    instructorAuthSummary,
    personalUrl,
    webLinks,
    ...webLinkFields,
    roleLabel: item.role_label || item.roleLabel || '',
    biography: item.biography || '',
    career: item.career || '',
    primary_image: normalizeYowanEventMediaAsset(item.primary_image),
    primary_image_id: item.primary_image?.id || item.primaryImage?.id || '',
    image: item.primary_image?.preview_url || item.primary_image?.url || '',
    specialties,
    isActive: item.is_active !== false && item.isActive !== false,
    publicProfileEnabled: item.public_profile_enabled === true || item.publicProfileEnabled === true,
    remarks: item.remarks || '',
    createdAt: formatYowanAdminDateTime(item.created_at || item.createdAt),
    updatedAt: formatYowanAdminDateTime(item.updated_at || item.updatedAt),
    editHref: `/admin/events/instructors/${item.id}/edit`,
  };
};

const organizerTypeLabels = {
  individual: '個人',
  group: '団体',
};

export const normalizeYowanAdminOrganizer = (item = {}) => {
  const member = normalizeYowanAdminEventMemberSummary(item.member || {});
  const organizerType = item.organizer_type || item.organizerType || 'individual';
  const organizerAccess = normalizeYowanStakeholderAccess(item.organizer_access || item.organizerAccess || item.access || null);
  const organizerAuthSummary = normalizeYowanStakeholderAuthSummary(item.auth_access_summary || item.authAccessSummary || null, organizerAccess, 'organizer');

  return {
    id: item.id || '',
    organizerName: item.organizer_name || item.organizerName || '',
    personId: item.person_id || item.personId || item.person?.id || '',
    person: normalizeYowanPerson(item.person),
    organizerType,
    organizerTypeLabel: organizerTypeLabels[organizerType] || organizerType,
    memberId: item.member_id || item.memberId || member.id || '',
    member,
    memberName: member.name || '',
    memberEmail: member.email || '',
    representativeName: item.representative_name || item.representativeName || '',
    contactEmail: item.contact_email || item.contactEmail || '',
    contactPhone: item.contact_phone || item.contactPhone || '',
    organizerAccess,
    organizerAuthSummary,
    websiteUrl: item.website_url || item.websiteUrl || '',
    description: item.description || '',
    primary_image: normalizeYowanEventMediaAsset(item.primary_image),
    primary_image_id: item.primary_image?.id || item.primaryImage?.id || '',
    image: item.primary_image?.preview_url || item.primary_image?.url || '',
    publicProfileEnabled: item.public_profile_enabled === true || item.publicProfileEnabled === true,
    isActive: item.is_active !== false && item.isActive !== false,
    remarks: item.remarks || '',
    createdAt: formatYowanAdminDateTime(item.created_at || item.createdAt),
    updatedAt: formatYowanAdminDateTime(item.updated_at || item.updatedAt),
    editHref: `/admin/events/organizers/${item.id}/edit`,
  };
};

const weekdayLabels = {
  monday: '月曜日',
  tuesday: '火曜日',
  wednesday: '水曜日',
  thursday: '木曜日',
  friday: '金曜日',
  saturday: '土曜日',
  sunday: '日曜日',
};

const weekdayShortLabels = ['日', '月', '火', '水', '木', '金', '土'];

const formatYowanTime = (value) => {
  if (!value) {
    return '';
  }

  const normalizedValue = String(value).replace(' ', 'T');

  if (/^\d{2}:\d{2}/.test(normalizedValue)) {
    return normalizedValue.slice(0, 5);
  }

  const parts = getYowanTokyoDateTimeParts(value);

  if (parts) {
    return `${parts.hour}:${parts.minute}`;
  }

  return '';
};

const formatYowanPublicDateTimeLabel = (value) => {
  const parts = getYowanTokyoDateTimeParts(value);

  if (!parts) {
    return '';
  }

  const weekdayIndex = new Date(Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day))).getUTCDay();
  const weekdayLabel = weekdayShortLabels[weekdayIndex] || '';

  return `${parts.year}年${parts.month}月${parts.day}日（${weekdayLabel}） ${parts.hour}:${parts.minute}`;
};

const getYowanDateKey = (value) => {
  const parts = getYowanTokyoDateTimeParts(value);

  if (!parts) {
    return '';
  }

  return `${parts.year}-${parts.month}-${parts.day}`;
};

const formatYowanPublicDateTimeRangeLabel = (startAt, endAt) => {
  const startLabel = formatYowanPublicDateTimeLabel(startAt);
  const endLabel = formatYowanPublicDateTimeLabel(endAt);

  if (!startLabel) {
    return endLabel;
  }

  if (!endLabel || endLabel === startLabel) {
    return startLabel;
  }

  if (getYowanDateKey(startAt) && getYowanDateKey(startAt) === getYowanDateKey(endAt)) {
    const endTime = formatYowanTime(endAt);

    return endTime ? `${startLabel} - ${endTime}` : startLabel;
  }

  return `${startLabel} - ${endLabel}`;
};

const formatYowanFeeLabel = (value) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  const numberValue = Number(value);

  if (Number.isNaN(numberValue)) {
    return String(value);
  }

  return `${numberValue.toLocaleString('ja-JP')}円`;
};

const normalizePublicInstructorSpecialties = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }

  return String(value || '')
    .split(/[、,/]/)
    .map((item) => item.trim())
    .filter(Boolean);
};

const instructorWebLinkPlatformLabels = {
  website: 'Website',
  facebook: 'Facebook',
  x: 'X',
  instagram: 'Instagram',
  youtube: 'YouTube',
  tiktok: 'TikTok',
  other: 'Link',
};

const instructorWebLinkFormFields = [
  { platformKey: 'website', fieldName: 'webLinkWebsite', order: 10 },
  { platformKey: 'facebook', fieldName: 'webLinkFacebook', order: 20 },
  { platformKey: 'x', fieldName: 'webLinkX', order: 30 },
  { platformKey: 'instagram', fieldName: 'webLinkInstagram', order: 40 },
  { platformKey: 'youtube', fieldName: 'webLinkYoutube', order: 50 },
  { platformKey: 'tiktok', fieldName: 'webLinkTiktok', order: 60 },
  { platformKey: 'other', fieldName: 'webLinkOther', order: 70 },
];

const normalizeYowanInstructorWebLinks = (items = []) => (
  Array.isArray(items)
    ? items
      .map((item, index) => {
        const platformKey = item.platform_key || item.platformKey || 'other';
        const url = item.url || '';

        return {
          id: item.id || '',
          platformKey,
          label: item.label || instructorWebLinkPlatformLabels[platformKey] || 'Link',
          url,
          displayOrder: Number(item.display_order ?? item.displayOrder ?? index),
          isActive: item.is_active !== false && item.isActive !== false,
        };
      })
      .filter((item) => item.url)
      .sort((a, b) => a.displayOrder - b.displayOrder)
    : []
);

const mapYowanInstructorWebLinksToFields = (webLinks = []) => (
  instructorWebLinkFormFields.reduce((accumulator, { platformKey, fieldName }) => {
    const link = webLinks.find((item) => item.platformKey === platformKey);
    accumulator[fieldName] = link?.url || '';
    return accumulator;
  }, {})
);

const buildYowanInstructorWebLinksPayload = (values = {}) => (
  instructorWebLinkFormFields
    .map(({ platformKey, fieldName, order }) => ({
      platform_key: platformKey,
      label: instructorWebLinkPlatformLabels[platformKey],
      url: String(values[fieldName] || '').trim(),
      display_order: order,
      is_active: true,
    }))
    .filter((item) => item.url)
);

const normalizeYowanPublicInstructor = (item = {}) => {
  const primaryImage = item.primary_image || item.primaryImage || {};
  const normalizedPrimaryImage = normalizeYowanEventMediaAsset(primaryImage);

  return {
    id: item.id || '',
    instructorName: item.instructor_name || item.instructorName || item.name || '',
    roleLabel: item.role_label || item.roleLabel || '',
    specialties: normalizePublicInstructorSpecialties(item.specialties),
    biography: item.biography || '',
    career: item.career || '',
    primary_image: normalizedPrimaryImage,
    image: normalizedPrimaryImage.preview_url || normalizedPrimaryImage.url || '',
    webLinks: normalizeYowanInstructorWebLinks(item.web_links || item.webLinks),
  };
};

const normalizeYowanPublicInstructors = (items = []) => (
  Array.isArray(items) ? items.map(normalizeYowanPublicInstructor).filter((item) => item.id || item.instructorName) : []
);

const getRoomFacilityName = (room = {}) => {
  const facility = room.facility || {};

  return facility.facility_name || facility.facilityName || '';
};

const getRoomDisplayName = (room = {}) => {
  const facilityName = getRoomFacilityName(room);
  const roomName = room.room_name || room.roomName || '';

  return [facilityName, roomName].filter(Boolean).join(' / ');
};

const normalizeOptionalActiveFlag = (source = {}) => {
  if (source.is_active === false || source.isActive === false) {
    return false;
  }

  if (source.is_active === true || source.isActive === true) {
    return true;
  }

  return undefined;
};

const resolveOptionalActiveFlag = (...sources) => {
  for (const source of sources) {
    const activeFlag = normalizeOptionalActiveFlag(source || {});

    if (activeFlag !== undefined) {
      return activeFlag;
    }
  }

  return undefined;
};

const getPublicLocationLabel = (item = {}) => {
  const publicLocation = item.public_location || item.publicLocation || {};

  return publicLocation.label || publicLocation.room_name || publicLocation.address || '';
};

const resolveClassScheduleLocationIsPublic = (item = {}) => {
  if (item.location_is_public !== undefined && item.location_is_public !== null) {
    return normalizeBoolean(item.location_is_public);
  }

  if (item.locationIsPublic !== undefined && item.locationIsPublic !== null) {
    return normalizeBoolean(item.locationIsPublic);
  }

  return true;
};

export const normalizeYowanClassSchedule = (item = {}) => {
  const room = item.room || {};
  const facility = item.facility || room.facility || {};
  const instructor = item.instructor || {};
  const weekday = item.weekday || '';
  const scheduleKind = item.schedule_kind || item.scheduleKind || 'weekly';
  const scheduleLabel = item.schedule_label || item.scheduleLabel || '';
  const startTime = formatYowanTime(item.start_time || item.startTime);
  const endTime = formatYowanTime(item.end_time || item.endTime);
  const timeRange = [startTime, endTime].filter(Boolean).join(' - ');
  const weekdayLabel = weekdayLabels[weekday] || weekday;
  const datetimeLabel = [scheduleLabel || weekdayLabel, timeRange].filter(Boolean).join(' ');
  const locationIsPublic = resolveClassScheduleLocationIsPublic(item);
  const publicLocationLabel = item.public_location_label || item.publicLocationLabel || '';
  const publicLocation = item.public_location || item.publicLocation || null;
  const publicLocationText = getPublicLocationLabel(item) || publicLocationLabel;
  const fallbackLocationText = getRoomDisplayName(room) || item.room_name || item.roomName || '';
  const facilityIsActive = normalizeOptionalActiveFlag(facility);
  const roomIsActive = normalizeOptionalActiveFlag(room);
  const instructorIsActive = resolveOptionalActiveFlag(
    instructor,
    {
      is_active: item.instructor_is_active,
      isActive: item.instructorIsActive,
    },
  );

  return {
    id: item.id || '',
    scheduleKind,
    scheduleLabel,
    weekday,
    weekdayLabel,
    startTime,
    endTime,
    datetimeLabel,
    facilityId: item.facility_id || item.facilityId || facility.id || '',
    facility,
    facilityName: facility.facility_name || facility.facilityName || '',
    roomId: item.room_id || item.roomId || room.id || '',
    room,
    roomName: getRoomDisplayName(room) || item.room_name || item.roomName || '',
    facilityIsActive,
    roomIsActive,
    locationIsPublic,
    publicLocationLabel,
    publicLocationNote: item.public_location_note || item.publicLocationNote || '',
    privateLocationNote: item.private_location_note || item.privateLocationNote || '',
    publicLocation,
    privateLocation: item.private_location || item.privateLocation || null,
    locationLabel: locationIsPublic ? publicLocationText || fallbackLocationText : publicLocationText,
    capacity: Number(item.capacity || 0),
    fee: item.fee ?? '',
    feeLabel: formatYowanFeeLabel(item.fee),
    instructorId: item.instructor_id || item.instructorId || instructor.id || '',
    instructor,
    instructorIsActive,
    instructorName: instructor.instructor_name || instructor.instructorName || item.instructor || '',
    isActive: item.is_active !== false && item.isActive !== false,
    acceptsApplications: item.accepts_applications !== false && item.acceptsApplications !== false,
    remarks: item.remarks || '',
    createdAt: formatYowanAdminDateTime(item.created_at || item.createdAt),
    updatedAt: formatYowanAdminDateTime(item.updated_at || item.updatedAt),
  };
};

export const normalizeYowanClass = (item = {}) => {
  const schedules = Array.isArray(item.schedules)
    ? item.schedules.map(normalizeYowanClassSchedule)
    : [];
  const publicInstructors = normalizeYowanPublicInstructors(item.public_instructors || item.publicInstructors);
  const firstSchedule = schedules.find((schedule) => schedule.isActive) || schedules[0] || null;
  const organizer = item.organizer || {};
  const organizerIsActive = resolveOptionalActiveFlag(
    organizer,
    {
      is_active: item.organizer_is_active,
      isActive: item.organizerIsActive,
    },
  );
  const detailTopImage = normalizeYowanEventMediaAsset(item.detail_top_image || item.detailTopImage);
  const publicSlug = item.public_slug || item.publicSlug || item.slug || '';
  const publicPath = normalizeYowanPublicPath(item.public_path || item.publicPath);
  const canonicalUrl = item.canonical_url || item.canonicalUrl || '';

  return {
    id: item.id || '',
    kind: 'class',
    publicSlug,
    publicPath,
    canonicalUrl,
    title: item.title || '',
    summary: item.summary || '',
    guidance: item.guidance || '',
    calendarUrl: item.calendar_url || item.calendarUrl || '',
    showInstructorProfiles: item.show_instructor_profiles === true || item.showInstructorProfiles === true,
    primary_image: normalizeYowanEventMediaAsset(item.primary_image),
    primary_image_id: item.primary_image?.id || item.primaryImage?.id || item.primary_image_id || item.primaryImageId || '',
    image: item.primary_image?.preview_url || item.primary_image?.url || '',
    detail_top_image: detailTopImage,
    detail_top_image_id: item.detail_top_image?.id || item.detailTopImage?.id || item.detail_top_image_id || item.detailTopImageId || '',
    detailTopImage: detailTopImage,
    detailTopImageUrl: detailTopImage?.preview_url || detailTopImage?.url || '',
    organizerId: item.organizer_id || item.organizerId || organizer.id || '',
    organizer,
    organizerIsActive,
    organizerName: organizer.organizer_name || organizer.organizerName || '',
    isPublished: item.is_published === true || item.isPublished === true,
    isActive: item.is_active !== false && item.isActive !== false,
    acceptsApplications: item.accepts_applications !== false && item.acceptsApplications !== false,
    schedulesCount: Number(item.schedules_count ?? item.schedulesCount ?? schedules.length ?? 0),
    applicationsCount: Number(item.applications_count ?? item.applicationsCount ?? item.class_applications_count ?? 0),
    schedules,
    publicInstructors,
    standardInstructor: firstSchedule?.instructorName || '',
    nextSlotLabel: firstSchedule?.datetimeLabel || '',
    area: firstSchedule?.locationLabel || (firstSchedule?.locationIsPublic ? firstSchedule?.roomName : '') || '',
    datetime: firstSchedule?.datetimeLabel || '開催日時は詳細で確認',
    feeLabel: firstSchedule?.feeLabel || '',
    updatedAt: formatYowanAdminDateTime(item.updated_at || item.updatedAt),
    createdAt: formatYowanAdminDateTime(item.created_at || item.createdAt),
    href: publicPath || `/classes/${publicSlug || item.id}`,
    editHref: `/admin/events/${item.id}/edit?kind=class`,
    applicationsHref: `/admin/events/classes/${item.id}/applications`,
    slotsHref: `/admin/events/${item.id}/edit#schedules`,
  };
};

const compactText = (value = '') => String(value || '').replace(/\s+/g, ' ').trim();

export const summarizeYowanClassSchedules = (record = {}, { limit = 2 } = {}) => {
  const schedules = Array.isArray(record.schedules)
    ? record.schedules.filter((schedule) => schedule && schedule.isActive !== false)
    : [];
  const visibleSchedules = (schedules.length ? schedules : record.schedules || []).slice(0, limit);

  return {
    items: visibleSchedules.map((schedule) => ({
      id: schedule.id || `${schedule.datetimeLabel || ''}-${schedule.locationLabel || ''}`,
      datetime: schedule.datetimeLabel || '',
      location: schedule.locationLabel || '',
      instructor: schedule.instructorName || '',
      fee: schedule.feeLabel || '',
    })),
    remainingCount: Math.max(0, (schedules.length || record.schedulesCount || 0) - visibleSchedules.length),
  };
};

export const getYowanClassIntroText = (record = {}) => compactText(record.summary || record.guidance || '');

const classApplicationStatusLabels = {
  received: '受付済み',
  applied: '受付済み',
  pending: '確認待ち',
  confirmed: '確認済み',
  canceled: 'キャンセル',
  rejected: '不受理',
};

const buildYowanClassApplicationScheduleLabel = (schedule = {}, item = {}) => {
  const normalizedSchedule = schedule?.id || schedule?.schedule_label || schedule?.scheduleLabel
    ? normalizeYowanClassSchedule(schedule)
    : {};
  const scheduleLabel = item.schedule_label || item.scheduleLabel || normalizedSchedule.scheduleLabel || '';
  const datetimeLabel = item.datetime_label || item.datetimeLabel || normalizedSchedule.datetimeLabel || '';
  const locationLabel = item.location_label || item.locationLabel || normalizedSchedule.locationLabel || '';

  return [scheduleLabel || datetimeLabel, locationLabel]
    .filter(Boolean)
    .join(' / ');
};

const normalizeChangedFields = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).map(String);
  }

  if (value && typeof value === 'object') {
    return Object.keys(value).filter((key) => value[key]);
  }

  if (typeof value === 'string') {
    return value
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
  }

  return [];
};

const classApplicationChangedFieldLabels = {
  name: '氏名',
  name_kana: 'ふりがな',
  email: 'メールアドレス',
  phone: '電話番号',
};

const normalizeYowanClassApplicationIdentityObservation = (item = {}) => {
  const changedFields = normalizeChangedFields(
    item.changed_fields || item.changedFields || item.changed_fields_json || item.changedFieldsJson,
  );
  const classApplicationId = item.class_application_id || item.classApplicationId || '';
  const seminarApplicationId = item.seminar_application_id || item.seminarApplicationId || '';

  return {
    id: item.id || `${item.observed_at || item.observedAt || ''}-${item.observed_email || item.observedEmail || ''}`,
    observedAt: formatYowanAdminDateTime(item.observed_at || item.observedAt || item.created_at || item.createdAt),
    sourceKind: item.source_kind || item.sourceKind || '',
    sourceId: item.source_id || item.sourceId || '',
    sourceLabel: item.source_label || item.sourceLabel || '',
    classApplicationId,
    classApplicationHref: classApplicationId
      ? `/admin/events/classes/applications/${classApplicationId}`
      : '',
    seminarApplicationId,
    seminarApplicationHref: seminarApplicationId
      ? `/admin/events/seminars/applications/${seminarApplicationId}`
      : '',
    observedName: item.observed_name || item.observedName || item.name || '',
    observedNameKana: item.observed_name_kana || item.observedNameKana || item.name_kana || item.nameKana || '',
    observedEmail: item.observed_email || item.observedEmail || item.email || '',
    observedPhone: item.observed_phone || item.observedPhone || item.phone || '',
    changedFields,
    changedFieldsLabel: changedFields.length
      ? changedFields.map((field) => classApplicationChangedFieldLabels[field] || field).join(' / ')
      : '-',
    note: item.note || '',
  };
};

const getClassApplicationObservationSource = (item = {}, applicant = {}) => {
  const direct = item.identity_observations || item.identityObservations || item.applicant_identity_observations || item.applicantIdentityObservations;
  const nested = applicant.identity_observations || applicant.identityObservations || applicant.applicant_identity_observations || applicant.applicantIdentityObservations;

  if (Array.isArray(direct)) {
    return direct;
  }

  if (Array.isArray(nested)) {
    return nested;
  }

  return [];
};

const getYowanSeminarApplicationSnapshot = (item = {}) =>
  item.source_snapshot ||
  item.sourceSnapshot ||
  item.submitted ||
  item.snapshot ||
  item.submission ||
  {};

const normalizeYowanSameEmailMemberGuard = (item = {}) => {
  const member = item.member || {};
  const reason = item.reason || '';
  const memberId = item.member_id || item.memberId || member.id || '';
  const memberName = item.member_name || item.memberName || member.name || '';
  const memberEmail = item.member_email || item.memberEmail || member.email || '';
  const guardEmail = item.email || '';
  const hasExplicitMatch = item.exists === true || item.has_match === true || item.hasMatch === true;
  const hasMemberPayload = Boolean(memberId);
  const hasGuard = Boolean(
    Object.keys(item || {}).length ||
    memberId ||
    memberName ||
    memberEmail ||
    guardEmail ||
    reason ||
    item.detail,
  );

  return {
    hasGuard,
    exists: hasExplicitMatch || hasMemberPayload,
    canLink: item.can_link === true || item.canLink === true,
    email: guardEmail,
    memberId,
    memberName,
    memberEmail,
    reason,
    reasonLabel: item.reason_label || item.reasonLabel || reason || '',
    detail: item.detail || '',
    status: item.status || '',
    statusLabel: item.status_label || item.statusLabel || item.status || '',
    identityProofLabel: '本人証明ではありません',
  };
};

const buildYowanSeminarApplicationCurrentApplicant = (item = {}, applicant = {}) => {
  const currentApplicant = item.current_applicant || item.currentApplicant || applicant || {};

  return {
    id: item.current_applicant_id || item.currentApplicantId || currentApplicant.id || applicant.id || '',
    name: item.current_applicant_name || item.currentApplicantName || currentApplicant.name || applicant.name || '',
    nameKana: item.current_applicant_name_kana || item.currentApplicantNameKana || currentApplicant.name_kana || currentApplicant.nameKana || applicant.name_kana || '',
    email: item.current_applicant_email || item.currentApplicantEmail || currentApplicant.email || applicant.email || '',
    phone: item.current_applicant_phone || item.currentApplicantPhone || currentApplicant.phone || applicant.phone || '',
  };
};

const buildYowanSeminarApplicationCurrentApplicantDifferences = ({
  applicantName,
  applicantNameKana,
  applicantEmail,
  applicantPhone,
  currentApplicant = {},
} = {}) => ([
  {
    field: 'name',
    label: '氏名',
    submittedValue: applicantName,
    currentValue: currentApplicant.name,
  },
  {
    field: 'nameKana',
    label: 'ふりがな',
    submittedValue: applicantNameKana,
    currentValue: currentApplicant.nameKana,
  },
  {
    field: 'email',
    label: 'メールアドレス',
    submittedValue: applicantEmail,
    currentValue: currentApplicant.email,
  },
  {
    field: 'phone',
    label: '電話番号',
    submittedValue: applicantPhone,
    currentValue: currentApplicant.phone,
  },
]).filter(({ submittedValue, currentValue }) =>
  Boolean(currentValue) &&
  String(submittedValue || '') !== String(currentValue || ''),
);

const buildYowanSeminarApplicationMemberStatus = ({
  directMemberLink = {},
  directMemberId = '',
  sameEmailMemberGuard = {},
} = {}) => {
  const directMemberHref =
    directMemberLink.href ||
    directMemberLink.detail_href ||
    directMemberLink.detailHref ||
    (directMemberId ? `/admin/members/${directMemberId}/edit` : '');
  const sameEmailMemberId = sameEmailMemberGuard.memberId || '';
  const sameEmailMemberHref = sameEmailMemberId ? `/admin/members/${sameEmailMemberId}/edit` : '';

  if (directMemberId || directMemberHref) {
    return {
      variant: 'member',
      label: '会員',
      description: '会員情報と紐づいた申込です。',
      href: directMemberHref,
    };
  }

  if (sameEmailMemberGuard.exists) {
    return {
      variant: 'candidate',
      label: '同一メールアドレスの会員候補',
      description: '申込メールアドレスと一致する会員登録があります。',
      href: sameEmailMemberHref,
    };
  }

  return {
    variant: 'none',
    label: '会員紐づきなし',
    description: 'この申込に会員紐づきはありません。',
    href: '',
  };
};

export const normalizeYowanClassApplication = (item = {}) => {
  const applicant = item.applicant || {};
  const yowanClass = item.yowan_class || item.yowanClass || item.class || {};
  const schedule = item.schedule || item.yowan_class_schedule || item.yowanClassSchedule || item.class_schedule || {};
  const submitted = item.submitted || item.snapshot || item.submission || {};
  const observations = getClassApplicationObservationSource(item, applicant)
    .map(normalizeYowanClassApplicationIdentityObservation);
  const statusValue = item.status || '';
  const classId = item.class_id || item.yowan_class_id || item.yowanClassId || yowanClass.id || schedule.class_id || schedule.classId || '';
  const scheduleId = item.schedule_id || item.yowan_class_schedule_id || item.yowanClassScheduleId || schedule.id || '';
  const submittedName = item.submitted_name || item.submittedName || submitted.name || submitted.submitted_name || submitted.submittedName || '';
  const submittedNameKana = item.submitted_name_kana || item.submittedNameKana || submitted.name_kana || submitted.nameKana || submitted.submitted_name_kana || submitted.submittedNameKana || '';
  const submittedEmail = item.submitted_email || item.submittedEmail || submitted.email || submitted.submitted_email || submitted.submittedEmail || '';
  const submittedPhone = item.submitted_phone || item.submittedPhone || submitted.phone || submitted.submitted_phone || submitted.submittedPhone || '';
  const currentApplicantName = item.current_applicant_name || item.currentApplicantName || applicant.name || '';
  const currentApplicantNameKana = item.current_applicant_name_kana || item.currentApplicantNameKana || applicant.name_kana || '';
  const currentApplicantEmail = item.current_applicant_email || item.currentApplicantEmail || applicant.email || '';
  const currentApplicantPhone = item.current_applicant_phone || item.currentApplicantPhone || applicant.phone || '';
  const applicantName = submittedName || item.name || item.applicant_name || item.applicantName || currentApplicantName;
  const applicantNameKana = submittedNameKana || item.name_kana || item.applicant_name_kana || item.applicantNameKana || currentApplicantNameKana;
  const applicantEmail = submittedEmail || item.email || item.applicant_email || item.applicantEmail || currentApplicantEmail;
  const applicantPhone = submittedPhone || item.phone || item.applicant_phone || item.applicantPhone || currentApplicantPhone;
  const appliedAtSource = item.applied_at || item.appliedAt || item.created_at || item.createdAt;
  const hasDistinctCurrentApplicant = Boolean(
    applicant.id &&
    [currentApplicantName, currentApplicantNameKana, currentApplicantEmail, currentApplicantPhone].some(Boolean) &&
    (
      (currentApplicantName && applicantName && currentApplicantName !== applicantName) ||
      (currentApplicantNameKana && applicantNameKana && currentApplicantNameKana !== applicantNameKana) ||
      (currentApplicantEmail && applicantEmail && currentApplicantEmail !== applicantEmail) ||
      (currentApplicantPhone && applicantPhone && currentApplicantPhone !== applicantPhone)
    ),
  );

  return {
    id: item.id || '',
    classId,
    classTitle: item.class_title || item.classTitle || yowanClass.title || '',
    scheduleId,
    scheduleLabel: buildYowanClassApplicationScheduleLabel(schedule, item),
    applicantId: item.applicant_id || item.applicantId || applicant.id || '',
    applicantName,
    applicantNameKana,
    applicantEmail,
    applicantPhone,
    currentApplicantName,
    currentApplicantNameKana,
    currentApplicantEmail,
    currentApplicantPhone,
    hasDistinctCurrentApplicant,
    identityObservations: observations,
    hasIdentityObservations: observations.length > 0 || item.has_identity_observations === true || item.hasIdentityObservations === true,
    message: item.message || item.applicant_message || item.applicantMessage || '',
    desiredDate: item.desired_date || item.desiredDate || '',
    status: statusValue,
    statusLabel: classApplicationStatusLabels[statusValue] || statusValue || '-',
    note: item.note || '',
    appliedAt: formatYowanAdminDateTime(appliedAtSource),
    createdAt: formatYowanAdminDateTime(appliedAtSource),
    updatedAt: formatYowanAdminDateTime(item.updated_at || item.updatedAt),
    applicantHref: applicant.id ? `/admin/applicants/${applicant.id}` : '',
    classHref: classId ? `/admin/events/${classId}/edit?kind=class` : '',
    detailHref: item.id ? `/admin/events/classes/applications/${item.id}` : '',
  };
};

const seminarSlotApplicationStatusLabels = {
  open: '受付中',
  closed: '受付停止中',
  ended: '開催終了',
  full: '満席',
};

export const normalizeYowanSeminarSlot = (item = {}) => {
  const room = item.room || {};
  const facility = item.facility || room.facility || {};
  const instructor = item.instructor || {};
  const rawInstructorProfile = item.instructor_profile || item.instructorProfile || {};
  const instructorProfile = normalizeYowanPublicInstructor(rawInstructorProfile);
  const startAt = item.start_at || item.startAt || '';
  const endAt = item.end_at || item.endAt || '';
  const startLabel = formatYowanPublicDateTimeLabel(startAt);
  const endTime = formatYowanTime(endAt);
  const instructorName = item.instructor_name ||
    item.instructorName ||
    instructor.instructor_name ||
    instructor.instructorName ||
    (typeof item.instructor === 'string' ? item.instructor : '') ||
    instructorProfile.instructorName ||
    '';

  const publicLocation = item.public_location || item.publicLocation || null;
  const fallbackFacilityName = facility.facility_name || facility.facilityName || facility.short_name || facility.shortName || '';
  const fallbackRoomName = item.room_name || item.roomName || room.room_name || room.roomName || '';
  const facilityRoomLocationLabel = getRoomDisplayName(room) ||
    [fallbackFacilityName, fallbackRoomName].filter(Boolean).join(' / ') ||
    item.address ||
    '';
  const locationDisplayMode = item.location_display_mode || item.locationDisplayMode || publicLocation?.mode || 'facility';
  const publicLocationLabel = getPublicLocationLabel(item);
  const locationLabel = locationDisplayMode === 'facility'
    ? facilityRoomLocationLabel || publicLocationLabel
    : publicLocationLabel || facilityRoomLocationLabel;
  const isOpen = item.is_open === true || item.isOpen === true;
  const applicationStatus = item.application_status || item.applicationStatus || (isOpen ? 'open' : 'closed');
  const applicationStatusLabel = item.application_status_label ||
    item.applicationStatusLabel ||
    seminarSlotApplicationStatusLabels[applicationStatus] ||
    (isOpen ? '受付中' : '受付停止中');
  const eventAcceptsApplications = item.event_accepts_applications === undefined && item.eventAcceptsApplications === undefined
    ? true
    : item.event_accepts_applications === true || item.eventAcceptsApplications === true;
  const canApply = item.can_apply === undefined && item.canApply === undefined
    ? isOpen
    : item.can_apply === true || item.canApply === true;
  const rawAppliedCount = item.applied_count ?? item.appliedCount;
  const appliedCount = Number(rawAppliedCount ?? 0);
  const remainingCapacitySource = item.remaining_capacity ?? item.remainingCapacity;
  const isFull = item.is_full === true || item.isFull === true;
  const capacityStatus = item.capacity_status || item.capacityStatus || '';

  const rawApplicationsCount = item.applications_count ?? item.applicationsCount ?? rawAppliedCount;
  const hasApplicationsCount = rawApplicationsCount !== undefined && rawApplicationsCount !== null && rawApplicationsCount !== '';
  const facilityIsActive = normalizeOptionalActiveFlag(facility);
  const roomIsActive = normalizeOptionalActiveFlag(room);
  const instructorIsActive = resolveOptionalActiveFlag(
    instructor,
    rawInstructorProfile,
    {
      is_active: item.instructor_is_active,
      isActive: item.instructorIsActive,
    },
  );

  return {
    id: item.id || '',
    title: item.title || item.slot_title || item.slotTitle || '',
    startAt,
    endAt,
    startLabel,
    datetimeLabel: endTime && startLabel ? `${startLabel} - ${endTime}` : startLabel,
    address: item.address || facility.address || '',
    roomName: getRoomDisplayName(room) || item.room_name || item.roomName || '',
    facilityId: item.facility_id || item.facilityId || facility.id || '',
    roomId: item.room_id || item.roomId || room.id || '',
    facility,
    room,
    facilityIsActive,
    roomIsActive,
    locationDisplayMode,
    publicLocationLabel: item.public_location_label || item.publicLocationLabel || '',
    publicLocationNote: item.public_location_note || item.publicLocationNote || '',
    privateLocationNote: item.private_location_note || item.privateLocationNote || '',
    thingsToBring: item.things_to_bring || item.thingsToBring || '',
    publicLocation,
    privateLocation: item.private_location || item.privateLocation || null,
    locationLabel,
    capacity: Number(item.capacity || 0),
    appliedCount,
    remainingCapacity: remainingCapacitySource === null || remainingCapacitySource === undefined || remainingCapacitySource === ''
      ? null
      : Number(remainingCapacitySource),
    isFull,
    capacityStatus,
    slotFee: item.slot_fee ?? item.slotFee ?? '',
    slotFeeLabel: formatYowanFeeLabel(item.slot_fee ?? item.slotFee),
    feeLabel: formatYowanFeeLabel(item.slot_fee ?? item.slotFee),
    isOpen,
    isActive: item.is_active !== false && item.isActive !== false,
    eventAcceptsApplications,
    applicationStatus,
    applicationStatusLabel,
    canApply,
    instructor,
    instructorIsActive,
    instructorProfileId: item.instructor_profile_id || item.instructorProfileId || instructorProfile.id || '',
    showInstructorProfile: item.show_instructor_profile === true || item.showInstructorProfile === true,
    instructorProfile,
    instructorName,
    hasApplicationsCount,
    applicationsCount: Number(rawApplicationsCount ?? 0),
  };
};

const hasYowanSeminarScheduleDisplayData = (slot = {}) => (
  Boolean(slot.datetimeLabel || slot.locationLabel || slot.instructorName || (
    slot.feeLabel && slot.feeLabel !== '未定'
  ))
);

const normalizeYowanSeminarRepresentativeSlot = (item = {}, displaySlots = []) => {
  const normalizedSlot = item.nextSlot || item.firstSlot || null;

  if (normalizedSlot && hasYowanSeminarScheduleDisplayData(normalizedSlot)) {
    return normalizedSlot;
  }

  const rawSlot = item.next_slot || item.first_slot || item.firstSlot || null;

  if (rawSlot) {
    const slot = normalizeYowanSeminarSlot(rawSlot);

    if (hasYowanSeminarScheduleDisplayData(slot)) {
      return slot;
    }
  }

  const displaySlot = displaySlots.find(hasYowanSeminarScheduleDisplayData) || null;

  if (displaySlot) {
    return displaySlot;
  }

  const startAt = item.first_slot_at || item.firstSlotAt || item.start_at || item.startAt || '';
  const endAt = item.last_slot_at || item.lastSlotAt || item.end_at || item.endAt || '';
  const datetimeLabel = formatYowanPublicDateTimeRangeLabel(startAt, endAt);
  const locationLabel = item.location_label ||
    item.locationLabel ||
    item.area ||
    item.public_location_label ||
    item.publicLocationLabel ||
    '';
  const instructorName = item.instructor_name ||
    item.instructorName ||
    item.standard_instructor ||
    item.standardInstructor ||
    '';
  const slotFee = item.slot_fee ?? item.slotFee ?? item.first_slot_fee ?? item.firstSlotFee ?? '';
  const feeLabel = formatYowanFeeLabel(slotFee);

  if (!datetimeLabel && !locationLabel && !instructorName && !feeLabel) {
    return null;
  }

  return {
    id: item.next_slot_id || item.nextSlotId || item.first_slot_id || item.firstSlotId || `${item.id || 'seminar'}-representative-slot`,
    title: item.next_slot_title || item.nextSlotTitle || item.first_slot_title || item.firstSlotTitle || '',
    startAt,
    endAt,
    startLabel: formatYowanPublicDateTimeLabel(startAt),
    datetimeLabel,
    locationLabel,
    instructorName,
    slotFee,
    slotFeeLabel: feeLabel,
    feeLabel,
    isOpen: item.is_open === true || item.isOpen === true,
    isActive: item.is_active !== false && item.isActive !== false,
  };
};

const seminarSeriesKindLabels = {
  standalone: '単独講習会',
  grouped: '一連の講習会',
};

const normalizeOptionalBoolean = (value) => {
  if (value === true || value === false) {
    return value;
  }
  return null;
};

export const normalizeYowanSeminarPublicVisibility = (item = {}, fallback = {}) => {
  const source = item?.public_visibility || item?.publicVisibility || {};
  const rawBlockers = Array.isArray(source.blockers)
    ? source.blockers
    : Array.isArray(item?.public_visibility_blockers)
      ? item.public_visibility_blockers
      : Array.isArray(item?.publicVisibilityBlockers)
        ? item.publicVisibilityBlockers
        : [];
  const seriesKind = source.series_kind ||
    source.seriesKind ||
    fallback.seriesKind ||
    item.series_kind ||
    item.seriesKind ||
    '';

  return {
    isPubliclyListed: normalizeOptionalBoolean(source.is_publicly_listed ?? source.isPubliclyListed),
    blockers: rawBlockers.map((blocker) => String(blocker || '')).filter(Boolean),
    seriesId: source.series_id || source.seriesId || fallback.seriesId || '',
    seriesKind,
    seriesKindLabel: seminarSeriesKindLabels[seriesKind] || seriesKind || '',
    seriesIsActive: normalizeOptionalBoolean(source.series_is_active ?? source.seriesIsActive),
    eventIsActive: normalizeOptionalBoolean(source.event_is_active ?? source.eventIsActive),
    eventIsPublished: normalizeOptionalBoolean(source.event_is_published ?? source.eventIsPublished),
    acceptsApplications: normalizeOptionalBoolean(source.accepts_applications ?? source.acceptsApplications),
  };
};

const getYowanPublicVisibilityBlockerMessage = (blocker, visibility = {}) => {
  if (blocker === 'series_inactive') {
    if (visibility.seriesKind === 'standalone') {
      return '単独セミナーの内部親シリーズが停止中です。単独セミナーの親シリーズは通常のシリーズ一覧から操作できないため、運用再開処理で復旧します。運用再開後も残る場合はAPI側の状態確認が必要です。';
    }

    return '親シリーズが停止中のため、セミナー自体が公開・運用中でも公開面には表示されません。シリーズ編集画面で「有効にする」を確認してください。';
  }

  if (blocker === 'seminar_inactive') {
    return 'セミナー本体が停止中です。運用再開専用ページで強い停止状態を解除してください。';
  }

  if (blocker === 'seminar_unpublished') {
    return '「公開する」が無効のため、公開面には表示されません。';
  }

  if (blocker === 'slot_inactive') {
    return '開催コマが非表示状態のため、公開面には表示されません。中止後の運用再開では開催コマの表示状態も復旧対象です。';
  }

  if (blocker === 'slot_closed') {
    return '開催コマが受付停止状態のため、申込受付には出ません。中止後の運用再開では開催コマの受付状態も復旧対象です。';
  }

  return `公開面表示を妨げる条件があります（${blocker}）。`;
};

const yowanReactivationBlockerCodes = new Set(['seminar_inactive', 'series_inactive']);
const yowanPublicationBlockerCodes = new Set(['seminar_unpublished']);
const yowanSlotWarningBlockerCodes = new Set(['slot_inactive', 'slot_closed']);

const getYowanPublicVisibilityBlockerLabel = (blocker) => {
  if (blocker === 'series_inactive') {
    return '親シリーズ停止中';
  }
  if (blocker === 'seminar_inactive') {
    return '運用停止中';
  }
  if (blocker === 'seminar_unpublished') {
    return '非公開';
  }
  if (blocker === 'slot_inactive') {
    return '一部コマ非表示';
  }
  if (blocker === 'slot_closed') {
    return '一部コマ受付停止';
  }
  return `公開面注意: ${blocker}`;
};

const buildYowanVisibilityIssue = (blocker, visibility = {}) => ({
  code: blocker,
  label: getYowanPublicVisibilityBlockerLabel(blocker),
  message: getYowanPublicVisibilityBlockerMessage(blocker, visibility),
});

export const getYowanPublicVisibilityIssueGroups = (visibility = {}) => {
  const blockers = Array.isArray(visibility.blockers) ? visibility.blockers : [];
  const groups = {
    reactivation: [],
    publication: [],
    slotWarnings: [],
    other: [],
  };

  blockers.forEach((blocker) => {
    const issue = buildYowanVisibilityIssue(blocker, visibility);

    if (yowanReactivationBlockerCodes.has(blocker)) {
      groups.reactivation.push(issue);
      return;
    }
    if (yowanPublicationBlockerCodes.has(blocker)) {
      groups.publication.push(issue);
      return;
    }
    if (yowanSlotWarningBlockerCodes.has(blocker)) {
      groups.slotWarnings.push(issue);
      return;
    }
    groups.other.push(issue);
  });

  return groups;
};

export const getYowanPublicVisibilityMessages = (visibility = {}) => {
  const blockers = Array.isArray(visibility.blockers) ? visibility.blockers : [];
  const messages = blockers.map((blocker) => getYowanPublicVisibilityBlockerMessage(blocker, visibility));

  if (!messages.length && visibility.isPubliclyListed === false) {
    return ['公開面表示条件を満たしていません。公開状態、運用状態、親シリーズの状態を確認してください。'];
  }

  return messages;
};

export const getYowanPublicVisibilityStatusUi = (visibility = {}) => {
  if (visibility.isPubliclyListed === true) {
    return {
      label: '公開面表示中',
      tone: 'published',
    };
  }

  if (visibility.isPubliclyListed === false) {
    return {
      label: '公開面未表示',
      tone: 'draft',
    };
  }

  return {
    label: '公開面判定未取得',
    tone: 'review',
  };
};

export const getYowanAdminPublicVisibilityStatusUi = (visibility = {}, options = {}) => {
  const statusUi = getYowanPublicVisibilityStatusUi(visibility);
  const groups = getYowanPublicVisibilityIssueGroups(visibility);
  const hasOnlySlotWarnings = visibility.isPubliclyListed === false &&
    groups.slotWarnings.length > 0 &&
    !groups.reactivation.length &&
    !groups.publication.length &&
    !groups.other.length;

  if (hasOnlySlotWarnings) {
    return {
      label: '公開面注意',
      tone: 'review',
    };
  }

  if (visibility.isPubliclyListed !== true && visibility.isPubliclyListed !== false && options.unknownLabel) {
    return {
      label: options.unknownLabel,
      tone: options.unknownTone || statusUi.tone,
    };
  }

  return statusUi;
};

const uniqueYowanIssues = (issues = []) => {
  const seen = new Set();

  return issues.filter((issue) => {
    const key = issue.code || issue.label || issue.message;
    if (!key || seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

export const getYowanSeminarSlotWarningIssues = (record = {}) => {
  const visibilityGroups = getYowanPublicVisibilityIssueGroups(record.publicVisibility || {});
  const issues = [...visibilityGroups.slotWarnings];
  const slots = Array.isArray(record.slots) ? record.slots : [];

  if (slots.some((slot) => slot.isActive === false)) {
    issues.push({
      code: 'slot_inactive',
      label: '一部コマ非表示',
      message: '一部の開催コマが非表示です。公開面に出すコマは編集画面で「開催コマを表示する」を確認してください。',
    });
  }

  if (slots.some((slot) => slot.isOpen === false || slot.applicationStatus === 'closed')) {
    issues.push({
      code: 'slot_closed',
      label: '一部コマ受付停止',
      message: '一部の開催コマが受付停止中です。公開面では該当コマの申込や通し参加申込が制限されます。',
    });
  }

  return uniqueYowanIssues(issues);
};

export const getYowanSeminarReactivateIssues = (record = {}) => {
  const groups = getYowanPublicVisibilityIssueGroups(record.publicVisibility || {});
  const issues = [...groups.reactivation];

  if (record.isActive === false) {
    issues.push({
      code: 'seminar_inactive',
      label: '運用停止中',
      message: 'セミナー本体が停止中です。運用再開専用ページで復旧してください。',
    });
  }

  return uniqueYowanIssues(issues);
};

export const canReactivateYowanSeminarRecord = (record = {}) => Boolean(
  record &&
  (
    record.canReactivate === true ||
    Boolean(record.reactivateHref) ||
    Boolean(record.links?.adminReactivate) ||
    Boolean(record.links?.admin_reactivate)
  ) &&
  getYowanSeminarReactivateIssues(record).length > 0
);

export const getYowanSeriesAdminWarningIssues = (record = {}) => {
  const seminars = Array.isArray(record.seminars) ? record.seminars : [];
  const issues = [];
  const seminarsCount = Number(record.seminarsCount ?? record.eventsCount ?? seminars.length ?? 0);
  const publishedSeminarsCount = Number(record.publishedSeminarsCount ?? record.publishedEventsCount ?? 0);
  const hasPublishedSeminarsCount = record.hasPublishedSeminarsCount === true ||
    record.publishedSeminarsCount !== undefined ||
    record.publishedEventsCount !== undefined;
  const hasInactiveChildSeminar = seminars.some((seminar) => seminar.isActive === false);

  if (record.isActive === false) {
    issues.push({
      code: 'series_inactive',
      label: 'シリーズ停止中',
      message: 'シリーズ本体が停止中です。公開面に出す場合はシリーズ編集画面で「有効にする」を確認してください。',
    });
  }

  if (!seminars.length && hasPublishedSeminarsCount && seminarsCount > 0 && publishedSeminarsCount === 0) {
    issues.push({
      code: 'series_no_published_children',
      label: '公開セミナーなし',
      message: 'シリーズ内に公開中のセミナーがありません。公開面に出す場合は配下セミナーの公開状態を確認してください。',
    });
  } else if (!seminars.length && hasPublishedSeminarsCount && seminarsCount > 0 && publishedSeminarsCount < seminarsCount) {
    issues.push({
      code: 'series_some_unpublished_children',
      label: '一部セミナー非公開',
      message: 'シリーズ内に非公開のセミナーがあります。公開面に出す場合は該当セミナーの「公開する」を確認してください。',
    });
  }

  if (!hasInactiveChildSeminar && seminars.some((seminar) => seminar.isPublished === false)) {
    issues.push({
      code: 'series_child_unpublished',
      label: '一部セミナー非公開',
      message: 'シリーズ内に非公開のセミナーがあります。公開面に出す場合は該当セミナーの「公開する」を確認してください。',
    });
  }

  if (hasInactiveChildSeminar) {
    issues.push({
      code: 'series_child_inactive',
      label: '一部セミナー停止中',
      message: 'シリーズ内に停止中のセミナーがあります。必要に応じて該当セミナーを運用再開してください。',
    });
  }

  if (!hasInactiveChildSeminar && seminars.some((seminar) => getYowanSeminarSlotWarningIssues(seminar).some((issue) => issue.code === 'slot_inactive'))) {
    issues.push({
      code: 'series_child_slot_inactive',
      label: '一部コマ非表示',
      message: 'シリーズ内の一部セミナーに非表示の開催コマがあります。',
    });
  }

  if (!hasInactiveChildSeminar && seminars.some((seminar) => getYowanSeminarSlotWarningIssues(seminar).some((issue) => issue.code === 'slot_closed'))) {
    issues.push({
      code: 'series_child_slot_closed',
      label: '一部コマ受付停止',
      message: 'シリーズ内の一部セミナーに受付停止中の開催コマがあります。通し参加申込が制限される場合があります。',
    });
  }

  return uniqueYowanIssues(issues);
};

const formatYowanVisibilityBoolean = (value, trueLabel, falseLabel) => {
  if (value === true) {
    return trueLabel;
  }
  if (value === false) {
    return falseLabel;
  }
  return '未取得';
};

export const getYowanPublicVisibilityDiagnostics = (visibility = {}) => {
  const rows = [];

  if (visibility.seriesKind || visibility.seriesKindLabel) {
    rows.push({
      label: 'シリーズ種別',
      value: visibility.seriesKindLabel || visibility.seriesKind,
    });
  }

  rows.push(
    {
      label: '親シリーズ',
      value: formatYowanVisibilityBoolean(visibility.seriesIsActive, '有効', '停止中'),
    },
    {
      label: 'セミナー運用',
      value: formatYowanVisibilityBoolean(visibility.eventIsActive, '有効', '停止中'),
    },
    {
      label: '公開設定',
      value: formatYowanVisibilityBoolean(visibility.eventIsPublished, '公開', '非公開'),
    },
    {
      label: '申込受付',
      value: formatYowanVisibilityBoolean(visibility.acceptsApplications, '受付中', '受付停止中'),
    },
  );

  return rows.filter((row) => row.value !== '未取得');
};

const normalizeYowanSeminarSeries = (item = {}) => {
  if (!item || typeof item !== 'object') {
    return null;
  }

  const organizer = item.organizer || {};
  const organizerIsActive = resolveOptionalActiveFlag(
    organizer,
    {
      is_active: item.organizer_is_active,
      isActive: item.organizerIsActive,
    },
  );
  const seriesKind = item.series_kind || item.seriesKind || 'standalone';
  const fullFee = item.series_full_participation_fee ??
    item.seriesFullParticipationFee ??
    item.full_participation_fee ??
    item.fullParticipationFee ??
    '';
  const seminarsSource = Array.isArray(item.seminars)
    ? item.seminars
    : Array.isArray(item.events)
      ? item.events
      : [];
  const seminars = seminarsSource.map((seminar) => normalizeYowanSeminar(seminar));
  const seriesId = item.id || item.event_series_id || item.eventSeriesId || '';
  const primaryImage = item.primary_image || item.primaryImage || {};
  const publicSlug = item.public_slug || item.publicSlug || item.slug || '';
  const publicPath = normalizeYowanPublicPath(item.public_path || item.publicPath);
  const canonicalUrl = item.canonical_url || item.canonicalUrl || '';
  const applicationOptions = item.application_options || item.applicationOptions || {};
  const seriesFullOption = applicationOptions.series_full || applicationOptions.seriesFull || {};
  const canApplyFullSeriesParticipation =
    seriesFullOption.can_apply_full_series_participation === true ||
    seriesFullOption.canApplyFullSeriesParticipation === true ||
    seriesFullOption.available === true;
  const publishedSeminarsCountSource = item.published_seminars_count ??
    item.publishedSeminarsCount ??
    item.published_events_count ??
    item.publishedEventsCount ??
    item.public_seminars_count ??
    item.publicSeminarsCount ??
    item.public_events_count ??
    item.publicEventsCount;

  const publicVisibility = normalizeYowanSeminarPublicVisibility(item, {
    seriesKind,
    seriesId,
  });

  return {
    id: seriesId,
    publicSlug,
    publicPath,
    canonicalUrl,
    title: item.title || item.series_title || item.seriesTitle || '',
    subtitle: item.subtitle || item.series_subtitle || item.seriesSubtitle || '',
    summary: item.summary || item.series_summary || item.seriesSummary || '',
    seriesKind,
    seriesKindLabel: seminarSeriesKindLabels[seriesKind] || seriesKind || '-',
    organizerId: item.organizer_id || item.organizerId || organizer.id || '',
    organizer,
    organizerIsActive,
    organizerName: organizer.organizer_name || organizer.organizerName || item.organizer_name || item.organizerName || '',
    fullParticipationFee: fullFee,
    fullParticipationFeeLabel: formatYowanFeeLabel(fullFee),
    applicationOptions,
    canApplyFullSeriesParticipation,
    fullParticipationUnavailableSlotIds: Array.isArray(seriesFullOption.unavailable_slot_ids)
      ? seriesFullOption.unavailable_slot_ids
      : Array.isArray(seriesFullOption.unavailableSlotIds)
        ? seriesFullOption.unavailableSlotIds
        : [],
    seminars,
    seminarsCount: Number(item.seminars_count ?? item.seminarsCount ?? item.events_count ?? item.eventsCount ?? seminars.length ?? 0),
    eventsCount: Number(item.events_count ?? item.eventsCount ?? item.event_count ?? item.eventCount ?? seminars.length ?? 0),
    hasPublishedSeminarsCount: publishedSeminarsCountSource !== undefined && publishedSeminarsCountSource !== null,
    publishedSeminarsCount: Number(publishedSeminarsCountSource ?? 0),
    applicationsCount: Number(item.applications_count ?? item.applicationsCount ?? 0),
    firstSlotAt: formatYowanAdminDateTime(item.first_slot_start_at || item.firstSlotStartAt || item.first_slot_at || item.firstSlotAt),
    lastSlotAt: formatYowanAdminDateTime(item.last_slot_end_at || item.lastSlotEndAt || item.last_slot_at || item.lastSlotAt),
    isPublished: item.is_published === true || item.isPublished === true,
    isActive: item.is_active !== false && item.isActive !== false,
    publicVisibility,
    updatedAt: formatYowanAdminDateTime(item.updated_at || item.updatedAt),
    createdAt: formatYowanAdminDateTime(item.created_at || item.createdAt),
    image: primaryImage?.preview_url || primaryImage?.previewUrl || primaryImage?.url || '',
    primary_image_id: primaryImage?.id || item.primary_image_id || item.primaryImageId || '',
    primary_image: normalizeYowanEventMediaAsset(primaryImage),
    publicHref: publicPath || `/seminars/${publicSlug || seriesId}`,
    detailHref: `/admin/events/series/${seriesId}`,
    editHref: `/admin/events/series/${seriesId}/edit`,
    addSeminarHref: `/admin/events/new?seriesId=${seriesId}`,
  };
};

export const normalizeYowanSeminar = (item = {}, options = {}) => {
  const organizer = item.organizer || {};
  const organizerIsActive = resolveOptionalActiveFlag(
    organizer,
    {
      is_active: item.organizer_is_active,
      isActive: item.organizerIsActive,
    },
  );
  const publicInstructors = normalizeYowanPublicInstructors(item.public_instructors || item.publicInstructors);
  const series = normalizeYowanSeminarSeries(item.series || item.event_series || item.eventSeries || {});
  const seriesKind = item.series_kind || item.seriesKind || series?.seriesKind || 'standalone';
  const slots = Array.isArray(item.slots) ? item.slots.map(normalizeYowanSeminarSlot) : [];
  const events = Array.isArray(item.events) ? item.events.map((eventItem) => normalizeYowanSeminar(eventItem)) : [];
  const firstEvent = item.first_event || item.firstEvent ? normalizeYowanSeminar(item.first_event || item.firstEvent) : null;
  const isSeriesDetail = events.length > 0;
  const isStandaloneSeries = isSeriesDetail && seriesKind !== 'grouped';
  const preferredEvent = options.preferredEventId
    ? events.find((eventItem) => eventItem.id === options.preferredEventId) || null
    : null;
  const standaloneEvent = isStandaloneSeries
    ? preferredEvent || (events.length === 1 ? events[0] : null) || firstEvent
    : null;
  const sourceForFallback = standaloneEvent || firstEvent || null;
  const displaySlots = standaloneEvent?.slots?.length
    ? standaloneEvent.slots.map((slot) => ({
        ...slot,
        eventId: standaloneEvent.id,
        eventTitle: standaloneEvent.title,
        eventSubtitle: standaloneEvent.subtitle,
      }))
    : slots.length
      ? slots
      : events.flatMap((eventItem) => (
          (eventItem.slots || []).map((slot) => ({
            ...slot,
            eventId: eventItem.id,
            eventTitle: eventItem.title,
            eventSubtitle: eventItem.subtitle,
          }))
        ));
  const seminarSections = isStandaloneSeries
    ? [
        {
          id: standaloneEvent?.id || item.id || '',
          title: '',
          subtitle: '',
          seminarFullParticipationFee: standaloneEvent?.seminarFullParticipationFee || '',
          seminarFullParticipationFeeLabel: standaloneEvent?.seminarFullParticipationFeeLabel || '',
          fullParticipationFeeLabel: standaloneEvent?.fullParticipationFeeLabel || '',
          slots: standaloneEvent?.slots?.length ? standaloneEvent.slots : displaySlots,
        },
      ]
    : events.length
    ? events.map((eventItem) => ({
        id: eventItem.id,
        title: eventItem.title,
        subtitle: eventItem.subtitle,
        seminarFullParticipationFee: eventItem.seminarFullParticipationFee,
        seminarFullParticipationFeeLabel: eventItem.seminarFullParticipationFeeLabel,
        fullParticipationFeeLabel: eventItem.fullParticipationFeeLabel,
        slots: eventItem.slots || [],
      }))
    : [
        {
          id: item.id || '',
          title: item.title || '',
          subtitle: item.subtitle || '',
          seminarFullParticipationFee: item.full_participation_fee ?? item.fullParticipationFee ?? '',
          seminarFullParticipationFeeLabel: formatYowanFeeLabel(item.full_participation_fee ?? item.fullParticipationFee),
          fullParticipationFeeLabel: formatYowanFeeLabel(item.full_participation_fee ?? item.fullParticipationFee),
          slots: displaySlots,
        },
      ];
  const nextSlot = normalizeYowanSeminarRepresentativeSlot(item, displaySlots) ||
    (sourceForFallback ? normalizeYowanSeminarRepresentativeSlot(sourceForFallback, sourceForFallback.slots || []) : null) ||
    null;
  const sameSeriesEvents = Array.isArray(item.same_series_events || item.sameSeriesEvents)
    ? (item.same_series_events || item.sameSeriesEvents).map((eventItem) => normalizeYowanSeminar(eventItem))
    : [];
  const eventsGuidance = events
    .map((eventItem) => eventItem.guidance)
    .filter(Boolean)
    .join('\n\n');
  const sourceImage = item.primary_image || item.primaryImage || sourceForFallback?.primary_image || {};
  const rawAcceptsApplications = item.accepts_applications ?? item.acceptsApplications ?? sourceForFallback?.acceptsApplications;
  const acceptsApplications = rawAcceptsApplications === undefined ? true : rawAcceptsApplications !== false;
  const seriesFullParticipationFee = item.series_full_participation_fee ??
    item.seriesFullParticipationFee ??
    series?.fullParticipationFee ??
    '';
  const seminarFullParticipationFee = item.full_participation_fee ??
    item.fullParticipationFee ??
    sourceForFallback?.seminarFullParticipationFee ??
    '';
  const sourceFullParticipationFee = isStandaloneSeries
    ? seminarFullParticipationFee || seriesFullParticipationFee
    : seriesFullParticipationFee ||
      (
        item.full_participation_fee ??
        item.fullParticipationFee ??
        sourceForFallback?.fullParticipationFee ??
        ''
      );
  const publicSlug = item.public_slug || item.publicSlug || item.slug || series?.publicSlug || sourceForFallback?.publicSlug || '';
  const publicPath = normalizeYowanPublicPath(
    item.public_path ||
    item.publicPath ||
    series?.publicPath ||
    sourceForFallback?.publicPath,
  );
  const canonicalUrl = item.canonical_url || item.canonicalUrl || series?.canonicalUrl || sourceForFallback?.canonicalUrl || '';
  const applicationOptions = item.application_options || item.applicationOptions || {};
  const seriesFullOption = applicationOptions.series_full || applicationOptions.seriesFull || {};
  const eventFullOptions = Array.isArray(applicationOptions.event_full)
    ? applicationOptions.event_full
    : Array.isArray(applicationOptions.eventFull)
      ? applicationOptions.eventFull
      : [];
  const getEventFullOption = (eventId) => eventFullOptions.find((option = {}) => (
    String(option.event_id || option.eventId || '') === String(eventId || '')
  )) || {};
  const getEventFullAvailability = (eventItem = {}) => {
    const option = getEventFullOption(eventItem.id);

    return eventItem.acceptsApplications !== false && (
      option.can_apply_full_participation === true ||
      option.canApplyFullParticipation === true ||
      option.available === true ||
      eventItem.canApplyFullParticipation === true
    );
  };
  const getEventFullUnavailableSlotIds = (eventItem = {}) => {
    const option = getEventFullOption(eventItem.id);

    if (Array.isArray(option.unavailable_slot_ids)) {
      return option.unavailable_slot_ids;
    }
    if (Array.isArray(option.unavailableSlotIds)) {
      return option.unavailableSlotIds;
    }
    if (Array.isArray(eventItem.fullParticipationUnavailableSlotIds)) {
      return eventItem.fullParticipationUnavailableSlotIds;
    }
    if (Array.isArray(eventItem.full_participation_unavailable_slot_ids)) {
      return eventItem.full_participation_unavailable_slot_ids;
    }
    return [];
  };
  const canApplyFullParticipation = acceptsApplications && (
    item.can_apply_full_participation === true ||
    item.canApplyFullParticipation === true ||
    sourceForFallback?.canApplyFullParticipation === true
  );
  const fullParticipationUnavailableSlotIds = Array.isArray(item.full_participation_unavailable_slot_ids)
    ? item.full_participation_unavailable_slot_ids
    : Array.isArray(item.fullParticipationUnavailableSlotIds)
      ? item.fullParticipationUnavailableSlotIds
      : Array.isArray(sourceForFallback?.fullParticipationUnavailableSlotIds)
        ? sourceForFallback.fullParticipationUnavailableSlotIds
        : [];
  const links = item.links || {};
  const reactivateApiHref = links.admin_reactivate ||
    links.adminReactivate ||
    item.admin_reactivate ||
    item.adminReactivate ||
    '';
  const canReactivate = item.can_reactivate === true || item.canReactivate === true || Boolean(reactivateApiHref);
  const reactivateHref = canReactivate
    ? (item.id ? `/admin/events/${item.id}/reactivate` : '')
    : '';
  const publicVisibility = normalizeYowanSeminarPublicVisibility(item, {
    seriesId: series?.id || '',
    seriesKind,
  });

  return {
    id: item.id || '',
    kind: 'seminar',
    publicSlug,
    publicPath,
    canonicalUrl,
    title: isStandaloneSeries ? sourceForFallback?.title || item.title || '' : item.title || sourceForFallback?.title || '',
    subtitle: isStandaloneSeries ? sourceForFallback?.subtitle || item.subtitle || '' : item.subtitle || sourceForFallback?.subtitle || '',
    summary: isStandaloneSeries ? sourceForFallback?.summary || item.summary || '' : item.summary || '',
    guidance: isStandaloneSeries
      ? sourceForFallback?.guidance || item.guidance || ''
      : item.guidance || eventsGuidance || sourceForFallback?.guidance || '',
    eventType: item.event_type || item.eventType || sourceForFallback?.eventType || 'spot',
    eventTypeLabel: 'Seminar',
    series,
    seriesKind,
    seriesKindLabel: seminarSeriesKindLabels[seriesKind] || seriesKind || '-',
    events,
    sameSeriesEvents,
    firstEvent,
    image: sourceImage?.preview_url || sourceImage?.url || item.image || sourceForFallback?.image || '',
    primary_image: normalizeYowanEventMediaAsset(sourceImage),
    organizerId: item.organizer_id || item.organizerId || organizer.id || '',
    organizer,
    organizerIsActive,
    organizerName: organizer.organizer_name || organizer.organizerName || '',
    isPublished: item.is_published === true || item.isPublished === true,
    isActive: item.is_active !== false && item.isActive !== false,
    publicVisibility,
    acceptsApplications,
    canReactivate,
    slots: displaySlots,
    seminarSections: seminarSections.map((section) => ({
      ...section,
      acceptsApplications: section.acceptsApplications === undefined ? acceptsApplications : section.acceptsApplications !== false,
      canApplyFullParticipation: getEventFullAvailability(section) ||
        (String(section.id || '') === String(item.id || '') && canApplyFullParticipation),
      fullParticipationUnavailableSlotIds: getEventFullUnavailableSlotIds(section),
    })),
    slotsCount: Number(item.slots_count ?? item.slotsCount ?? displaySlots.length ?? 0),
    applicationsCount: Number(item.applications_count ?? item.applicationsCount ?? 0),
    seriesFullParticipationFee,
    seriesFullParticipationFeeLabel: formatYowanFeeLabel(seriesFullParticipationFee),
    canApplyFullSeriesParticipation: series?.canApplyFullSeriesParticipation === true ||
      seriesFullOption.can_apply_full_series_participation === true ||
      seriesFullOption.canApplyFullSeriesParticipation === true ||
      seriesFullOption.available === true,
    seriesFullParticipationUnavailableSlotIds: Array.isArray(series?.fullParticipationUnavailableSlotIds)
      ? series.fullParticipationUnavailableSlotIds
      : Array.isArray(seriesFullOption.unavailable_slot_ids)
        ? seriesFullOption.unavailable_slot_ids
        : Array.isArray(seriesFullOption.unavailableSlotIds)
          ? seriesFullOption.unavailableSlotIds
          : [],
    seminarFullParticipationFee,
    seminarFullParticipationFeeLabel: formatYowanFeeLabel(seminarFullParticipationFee),
    canApplyFullParticipation,
    fullParticipationUnavailableSlotIds,
    applicationOptions,
    fullParticipationFee: sourceFullParticipationFee,
    fullParticipationFeeLabel: formatYowanFeeLabel(sourceFullParticipationFee),
    publicInstructors,
    nextSlot: nextSlot || null,
    nextSlotLabel: nextSlot?.datetimeLabel || '',
    standardInstructor: nextSlot?.instructorName || '',
    cardDatetime: nextSlot?.datetimeLabel || '',
    cardLocation: nextSlot?.locationLabel || '',
    cardInstructor: nextSlot?.instructorName || '',
    cardFee: nextSlot?.feeLabel && nextSlot.feeLabel !== '未定' ? nextSlot.feeLabel : '',
    primary_image_id: item.primary_image?.id || item.primaryImage?.id || item.primary_image_id || item.primaryImageId || '',
    area: nextSlot?.locationLabel || nextSlot?.roomName || nextSlot?.address || '',
    datetime: nextSlot?.datetimeLabel || '開催日時は詳細で確認',
    updatedAt: formatYowanAdminDateTime(item.updated_at || item.updatedAt),
    createdAt: formatYowanAdminDateTime(item.created_at || item.createdAt),
    href: publicPath || `/seminars/${publicSlug || item.id}`,
    detailHref: `/admin/events/${item.id}`,
    editHref: `/admin/events/${item.id}/edit?kind=seminar`,
    cancelHref: `/admin/events/${item.id}/cancel`,
    reactivateHref,
    reactivateApiHref,
    links: {
      ...links,
      adminReactivate: reactivateHref,
      adminReactivateApi: reactivateApiHref,
    },
    slotsHref: `/admin/events/${item.id}/edit#slots`,
    applicationsHref: `/admin/events/${item.id}/applications`,
  };
};

const PAGE_CONTENT_SLOT_METADATA = {
  'reviews/header': {
    pageLabel: 'Reviews',
    sectionLabel: 'Header',
    adminLabel: 'Reviews Header Image',
    adminDescription: 'Reviews公開画面のヘッダー右側に表示される画像です。',
    usageScope: 'reviews',
    editHref: '/admin/content/page-contents/reviews/header/edit',
  },
  'top/hero': {
    pageLabel: 'top',
    sectionLabel: 'hero',
    adminLabel: 'Top Hero',
    adminDescription: 'Top page hero image slot.',
    usageScope: 'shared',
    editHref: '/admin/content/page-contents/top/hero/edit',
  },
  'top/overview': {
    pageLabel: 'top',
    sectionLabel: 'overview',
    adminLabel: 'Top Overview',
    adminDescription: 'Top page overview image slot.',
    usageScope: 'shared',
    editHref: '/admin/content/page-contents/top/overview/edit',
  },
  'top/updates': {
    pageLabel: 'top',
    sectionLabel: 'updates',
    adminLabel: 'Top Updates',
    adminDescription: 'Top page updates image slot.',
    usageScope: 'shared',
    editHref: '/admin/content/page-contents/top/updates/edit',
  },
  'site/header_logo': {
    pageLabel: 'site',
    sectionLabel: 'header_logo',
    adminLabel: 'Header Logo',
    adminDescription: '公開面Headerで表示するYowan site logoを管理します。',
    usageScope: 'shared',
    editHref: '/admin/content/page-contents/site/header_logo/edit',
  },
};

const buildPageContentSlotKey = (pageKey, slotKey) => `${pageKey}/${slotKey}`;

export const normalizePageContentSlot = (item = {}) => {
  const pageKey = item.page_key || item.pageKey || 'reviews';
  const slotKey = item.slot_key || item.slotKey || 'header';
  const metadata = PAGE_CONTENT_SLOT_METADATA[buildPageContentSlotKey(pageKey, slotKey)] || {};
  const rawPrimaryImage = item.primary_image || item.primaryImage || null;
  const primaryImage = rawPrimaryImage ? normalizeContentMediaAsset(rawPrimaryImage) : null;

  return {
    id: item.id || buildPageContentSlotKey(pageKey, slotKey),
    pageKey,
    slotKey,
    pageLabel: item.page_label || item.pageLabel || metadata.pageLabel || pageKey,
    sectionLabel: item.section_label || item.sectionLabel || metadata.sectionLabel || slotKey,
    adminLabel: item.admin_label || item.adminLabel || metadata.adminLabel || `${pageKey} / ${slotKey}`,
    adminDescription: item.admin_description || item.adminDescription || metadata.adminDescription || '',
    usageScope: item.usage_scope || item.usageScope || metadata.usageScope || 'shared',
    title: item.title || '',
    body: item.body || '',
    displayOrder: Number(item.display_order || item.displayOrder || 0),
    isPublished: item.is_published === true || item.isPublished === true,
    primary_image_id: primaryImage?.id || item.primary_image_id || item.primaryImageId || '',
    primary_image: primaryImage,
    createdAt: item.created_at || item.createdAt || '',
    updatedAt: item.updated_at || item.updatedAt || '',
    editHref: item.edit_href || item.editHref || metadata.editHref || `/admin/content/page-contents/${pageKey}/${slotKey}/edit`,
  };
};

export const normalizePublicPageStaticSlot = (item = {}) => {
  const normalizedSlot = normalizePageContentSlot(item);

  return {
    ...normalizedSlot,
    isPublished: item.is_published === undefined && item.isPublished === undefined
      ? true
      : normalizedSlot.isPublished,
  };
};

const notificationEventDisplay = {
  'content.news_published': {
    categoryLabel: 'Content通知',
    eventLabel: 'News 公開通知',
    sourceLabel: 'News',
    triggerPoint: 'News 公開',
  },
  'content.blog_published': {
    categoryLabel: 'Content通知',
    eventLabel: 'Blog 公開通知',
    sourceLabel: 'Blog',
    triggerPoint: 'Blog 公開',
  },
  'content.review_published': {
    categoryLabel: 'Content通知',
    eventLabel: 'Review 公開通知',
    sourceLabel: 'Reviews',
    triggerPoint: 'Review 公開',
  },
  'content.founder_updated': {
    categoryLabel: 'Content通知',
    eventLabel: 'Founder 更新通知',
    sourceLabel: 'Founder',
    triggerPoint: 'Founder 更新',
  },
  'auth.password_setup_requested': {
    categoryLabel: '認証',
    eventLabel: '初回パスワード設定依頼',
    sourceLabel: '初回設定',
    triggerPoint: '初回パスワード設定依頼の送信',
  },
  'auth.password_setup_completed': {
    categoryLabel: '認証',
    eventLabel: '初回パスワード設定完了',
    sourceLabel: '初回設定',
    triggerPoint: '初回パスワード設定完了',
  },
  'onboarding.request_sent': {
    categoryLabel: '情報入力依頼',
    eventLabel: '情報入力依頼 送信',
    sourceLabel: '情報入力依頼',
    triggerPoint: '情報入力依頼の送信',
  },
  'onboarding.request_failed': {
    categoryLabel: '情報入力依頼',
    eventLabel: '情報入力依頼 送信失敗',
    sourceLabel: '情報入力依頼',
    triggerPoint: '情報入力依頼の送信失敗',
  },
  'onboarding.request_completed': {
    categoryLabel: '情報入力依頼',
    eventLabel: '情報入力依頼 完了',
    sourceLabel: '情報入力依頼',
    triggerPoint: '情報入力依頼の完了',
  },
  'onboarding.request_expired': {
    categoryLabel: '情報入力依頼',
    eventLabel: '情報入力依頼 期限切れ',
    sourceLabel: '情報入力依頼',
    triggerPoint: '情報入力依頼の期限切れ',
  },
  'class.application_organizer_notified': {
    categoryLabel: 'Class',
    eventLabel: 'Class申込 主催者通知',
    sourceLabel: 'Class申込',
    triggerPoint: 'Class申込受付時',
  },
  'applicant.promoted_to_member': {
    categoryLabel: '申込者',
    eventLabel: '会員登録完了 本人通知',
    sourceLabel: '申込者',
    triggerPoint: '会員登録完了時',
  },
  'applicant.promoted_to_member_admin_notified': {
    categoryLabel: '申込者',
    eventLabel: '会員登録完了 運営通知',
    sourceLabel: '申込者',
    triggerPoint: '会員登録完了時',
  },
  'event.canceled': {
    categoryLabel: 'セミナー',
    eventLabel: 'セミナー中止 参加者通知',
    sourceLabel: 'セミナー',
    triggerPoint: '講座中止時',
  },
  'event.canceled_admin': {
    categoryLabel: 'セミナー',
    eventLabel: 'セミナー中止 管理者通知',
    sourceLabel: 'セミナー',
    triggerPoint: '講座中止時',
  },
  'event.canceled_organizer': {
    categoryLabel: 'セミナー',
    eventLabel: 'セミナー中止 主催者通知',
    sourceLabel: 'セミナー',
    triggerPoint: '講座中止時',
  },
  'event.canceled_instructor': {
    categoryLabel: 'セミナー',
    eventLabel: 'セミナー中止 講師通知',
    sourceLabel: 'セミナー',
    triggerPoint: '講座中止時',
  },
  'seo.crawler_html_fallback': {
    categoryLabel: 'SEO',
    eventLabel: 'SEO HTML fallback検知',
    sourceLabel: 'SEO配信',
    triggerPoint: 'Crawler向けHTML fallback時',
  },
  'seo.sitemap_generation_failed': {
    categoryLabel: 'SEO',
    eventLabel: 'Sitemap生成失敗',
    sourceLabel: 'SEO配信',
    triggerPoint: 'Sitemap配信失敗時',
  },
  'seo.cache_repair_failed': {
    categoryLabel: 'SEO',
    eventLabel: 'SEO cache修復失敗',
    sourceLabel: 'SEO配信',
    triggerPoint: 'SEO cache修復失敗時',
  },
};

const notificationCategoryLabels = {
  access: '権限',
  applicant: '申込者',
  auth: '認証',
  class: 'Class',
  content: 'コンテンツ',
  event: 'セミナー',
  onboarding: '情報入力依頼',
  seminar: 'セミナー',
  seo: 'SEO',
};

const notificationPolicyDisplay = {
  'access.member_granted': {
    eventLabel: 'Member権限付与通知',
    timingLabel: 'Member権限付与時',
  },
  'access.member_revoked': {
    eventLabel: 'Member権限解除通知',
    timingLabel: 'Member権限解除時',
  },
  'access.operator_granted': {
    eventLabel: 'Operator権限付与通知',
    timingLabel: 'Operator権限付与時',
  },
  'access.operator_revoked': {
    eventLabel: 'Operator権限解除通知',
    timingLabel: 'Operator権限解除時',
  },
  'applicant.created': {
    eventLabel: '申込者作成通知',
    timingLabel: '申込者作成時',
  },
  'applicant.promoted_to_member': {
    eventLabel: '会員登録完了 本人通知',
    timingLabel: '会員登録完了時',
  },
  'applicant.promoted_to_member_admin_notified': {
    eventLabel: '会員登録完了 運営通知',
    timingLabel: '会員登録完了時',
  },
  'applicant.updated': {
    eventLabel: '申込者更新通知',
    timingLabel: '申込者更新時',
  },
  'applicant.withdrawn': {
    eventLabel: '申込辞退通知',
    timingLabel: '申込辞退時',
  },
  'auth.password_reset_completed': {
    eventLabel: 'パスワード再設定完了通知',
    timingLabel: 'パスワード再設定完了時',
  },
  'auth.password_reset_requested': {
    eventLabel: 'パスワード再設定受付通知',
    timingLabel: 'パスワード再設定受付時',
  },
  'auth.password_setup_completed': {
    eventLabel: '初回パスワード設定完了通知',
    timingLabel: '初回パスワード設定完了時',
  },
  'auth.password_setup_requested': {
    eventLabel: '初回パスワード設定依頼通知',
    timingLabel: '初回パスワード設定依頼時',
  },
  'auth.subject_created': {
    eventLabel: '認証主体作成通知',
    timingLabel: '認証主体作成時',
  },
  'auth.subject_deactivated': {
    eventLabel: '認証主体停止通知',
    timingLabel: '認証主体停止時',
  },
  'content.blog_published': {
    eventLabel: 'Blog 公開通知',
    timingLabel: 'Blog公開時',
  },
  'content.founder_updated': {
    eventLabel: 'Founder 更新通知',
    timingLabel: 'Founder更新時',
  },
  'content.news_published': {
    eventLabel: 'News 公開通知',
    timingLabel: 'News公開時',
  },
  'content.review_published': {
    eventLabel: 'Review 公開通知',
    timingLabel: 'Review公開時',
  },
  'event.application_canceled': {
    eventLabel: 'セミナー申込キャンセル通知',
    timingLabel: 'セミナー申込キャンセル時',
  },
  'event.application_created': {
    eventLabel: 'セミナー申込受付通知',
    timingLabel: 'セミナー申込受付時',
  },
  'event.canceled': {
    eventLabel: 'セミナー中止 参加者通知',
    timingLabel: '講座中止時',
  },
  'event.canceled_admin': {
    eventLabel: 'セミナー中止 管理者通知',
    timingLabel: '講座中止時',
  },
  'event.canceled_organizer': {
    eventLabel: 'セミナー中止 主催者通知',
    timingLabel: '講座中止時',
  },
  'event.canceled_instructor': {
    eventLabel: 'セミナー中止 講師通知',
    timingLabel: '講座中止時',
  },
  'event.created': {
    eventLabel: 'セミナー作成通知',
    timingLabel: 'セミナー作成時',
  },
  'event.slot_updated': {
    eventLabel: 'セミナー開催コマ更新通知',
    timingLabel: 'セミナー開催コマ更新時',
  },
  'event.updated': {
    eventLabel: 'セミナー更新通知',
    timingLabel: 'セミナー更新時',
  },
  'event.waitlist_requested': {
    eventLabel: 'セミナーキャンセル待ち受付通知',
    timingLabel: 'セミナーキャンセル待ち受付時',
  },
  'event.waitlist_requested_admin': {
    eventLabel: 'セミナーキャンセル待ち 管理者通知',
    timingLabel: 'セミナーキャンセル待ち受付時',
  },
  'event.participant_mail': {
    eventLabel: 'セミナー参加者メール',
    timingLabel: 'セミナー参加者メール送信時',
  },
  'class.application_admin_notified': {
    eventLabel: 'Class申込 管理者通知',
    timingLabel: 'Class申込受付時',
  },
  'class.application_organizer_notified': {
    eventLabel: 'Class申込 主催者通知',
    timingLabel: 'Class申込受付時',
  },
  'class.application_received': {
    eventLabel: 'Class申込 受付通知',
    timingLabel: 'Class申込受付時',
  },
  'seminar.application_received': {
    eventLabel: 'セミナー申込 受付通知',
    timingLabel: 'セミナー申込受付時',
  },
  'seminar.application_admin_notified': {
    eventLabel: 'セミナー申込 管理者通知',
    timingLabel: 'セミナー申込受付時',
  },
  'seminar.application_organizer_notified': {
    eventLabel: 'セミナー申込 主催者通知',
    timingLabel: 'セミナー申込受付時',
  },
  'seminar.application_instructor_notified': {
    eventLabel: 'セミナー申込 講師通知',
    timingLabel: 'セミナー申込受付時',
  },
  'seminar.application_canceled_applicant': {
    eventLabel: 'セミナー申込キャンセル 参加者通知',
    timingLabel: 'セミナー申込キャンセル時',
  },
  'seminar.application_canceled_admin': {
    eventLabel: 'セミナー申込キャンセル 管理者通知',
    timingLabel: 'セミナー申込キャンセル時',
  },
  'seminar.application_canceled_organizer': {
    eventLabel: 'セミナー申込キャンセル 主催者通知',
    timingLabel: 'セミナー申込キャンセル時',
  },
  'seminar.application_canceled_instructor': {
    eventLabel: 'セミナー申込キャンセル 講師通知',
    timingLabel: 'セミナー申込キャンセル時',
  },
  'member.created': {
    eventLabel: 'Member作成通知',
    timingLabel: 'Member作成時',
  },
  'member.reactivated': {
    eventLabel: 'Member再開通知',
    timingLabel: 'Member再開時',
  },
  'member.suspended': {
    eventLabel: 'Member休会・停止通知',
    timingLabel: 'Member休会・停止時',
  },
  'member.updated': {
    eventLabel: 'Member更新通知',
    timingLabel: 'Member更新時',
  },
  'member.withdrawn': {
    eventLabel: 'Member退会通知',
    timingLabel: 'Member退会時',
  },
  'onboarding.request_completed': {
    eventLabel: '情報入力依頼完了通知',
    timingLabel: '情報入力依頼完了時',
  },
  'onboarding.request_failed': {
    eventLabel: '情報入力依頼送信失敗通知',
    timingLabel: '情報入力依頼送信失敗時',
  },
  'onboarding.request_expired': {
    eventLabel: '情報入力依頼期限切れ通知',
    timingLabel: '情報入力依頼期限切れ時',
  },
  'onboarding.request_sent': {
    eventLabel: '情報入力依頼送信通知',
    timingLabel: '情報入力依頼送信時',
  },
  'seo.crawler_html_fallback': {
    eventLabel: 'SEO HTML fallback検知通知',
    timingLabel: 'Crawler向けHTML fallback時',
  },
  'seo.sitemap_generation_failed': {
    eventLabel: 'Sitemap生成失敗通知',
    timingLabel: 'Sitemap配信失敗時',
  },
  'seo.cache_repair_failed': {
    eventLabel: 'SEO cache修復失敗通知',
    timingLabel: 'SEO cache修復失敗時',
  },
};

const seminarNotificationEventKeys = new Set([
  'event.application_canceled',
  'event.application_created',
  'event.canceled',
  'event.canceled_admin',
  'event.canceled_organizer',
  'event.canceled_instructor',
  'event.created',
  'event.participant_mail',
  'event.slot_updated',
  'event.updated',
  'event.waitlist_requested',
  'event.waitlist_requested_admin',
]);

const getNotificationDisplayOverride = (eventKey = '') => {
  const policyDisplay = notificationPolicyDisplay[eventKey] || {};
  const eventDisplay = notificationEventDisplay[eventKey] || {};
  const eventLabel = policyDisplay.eventLabel || eventDisplay.eventLabel || '';
  const timingLabel = policyDisplay.timingLabel || eventDisplay.triggerPoint || '';

  if (eventKey.startsWith('seminar.application_')) {
    return {
      categoryLabel: 'セミナー',
      eventLabel,
      timingLabel,
      triggerPoint: timingLabel,
      sourceLabel: 'セミナー申込',
    };
  }

  if (seminarNotificationEventKeys.has(eventKey)) {
    const sourceLabel = eventKey.startsWith('event.application_') ? 'セミナー申込' : 'セミナー';

    return {
      categoryLabel: 'セミナー',
      eventLabel,
      timingLabel,
      triggerPoint: timingLabel,
      sourceLabel,
    };
  }

  return {};
};

const notificationStatusLabels = {
  queued: '送信待ち',
  sent: '送信済み',
  failed: '失敗',
  skipped: 'スキップ',
};

const notificationChannelLabels = {
  email: 'メール',
};

const notificationDeliveryModeLabels = {
  async: '非同期',
  sync: '同期',
};

const notificationSendStageLabels = {
  pending: '未処理',
  resolving_recipients: '宛先解決中',
  rendering: '本文生成中',
  sending: '送信処理中',
  completed: '処理完了',
  skipped: 'スキップ',
  failed: '失敗',
};

const notificationSendResultLabels = {
  accepted: '送信サービス受付済み',
  delivered: '配送成功',
  failed: '送信処理失敗',
  skipped: 'スキップ',
  no_recipients: '宛先なし',
  policy_skipped: 'ルールによりスキップ',
  provider_untracked: 'プロバイダー未追跡',
};

const notificationProviderDeliveryStatusLabels = {
  accepted: 'プロバイダー受付済み',
  delivered: '配送成功',
  hard_bounce: 'ハードバウンス',
  soft_bounce: 'ソフトバウンス',
  blocked: 'ブロック',
  spam: '迷惑メール報告',
  deferred: '遅延',
  error: 'エラー',
  unknown: '不明',
};

const notificationProviderEngagementStatusLabels = {
  untracked: '未追跡',
  not_engaged: '未反応',
  opened: '開封済み',
  clicked: 'クリックあり',
};

const notificationProviderEngagementTypeLabels = {
  opened: '開封',
  clicked: 'クリック',
};

const notificationDecisionStatusLabels = {
  send: '送信する',
  skip: '送信しない',
  undecided: '未決定',
};

const notificationDefaultActionLabels = {
  send: '送信',
  skip: 'スキップ',
  undecided: '未決定',
};

const notificationPreferenceCategoryLabels = {
  news: 'News',
  blog: 'Blog',
};

const notificationRecipientVisibilityLabels = {
  preference_category: '受信設定',
  dynamic_access_role: '権限で動的解決',
  contextual: '通知文脈で解決',
  explicit_config: '明示設定',
};

const onboardingRequestKindLabels = {
  new_candidate_info: '新規候補者 情報入力',
  applicant_onboarding: '申込者 情報入力',
  applicant_member_registration: '会員登録',
  member_profile_completion: '会員情報 補完',
  member_account_setup: '初回設定',
};

const onboardingTargetKindLabels = {
  candidate: '新規候補者',
  applicant: '申込者',
  member: '会員',
  auth_subject: '認証アカウント',
};

const stringifyJsonForDisplay = (value) => {
  if (value === undefined || value === null || value === '') {
    return '';
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
};

const readObjectValue = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value;
  }

  return {};
};

const normalizeEmailList = (value) => (
  Array.isArray(value)
    ? value.map((entry) => String(entry || '').trim()).filter(Boolean)
    : []
);

const readRecipientBreakdown = (item = {}) => {
  const direct = readObjectValue(item.recipient_breakdown || item.recipientBreakdown);
  const context = readObjectValue(item.trigger_context || item.trigger_context_json || item.triggerContext);

  return Object.keys(direct).length
    ? direct
    : readObjectValue(context.recipient_breakdown || context.recipientBreakdown);
};

const countRecipients = (value, fallback = 0) => {
  const count = Number(value);

  return Number.isFinite(count) && count >= 0 ? count : fallback;
};

const normalizeRecipientBreakdownProjection = (item = {}) => {
  const breakdown = readRecipientBreakdown(item);
  const hasBreakdown = Boolean(
    Object.keys(breakdown).length ||
    item.participant_recipients ||
    item.participantRecipients ||
    item.internal_recipients ||
    item.internalRecipients,
  );
  const participants = readObjectValue(breakdown.participants);
  const internal = readObjectValue(breakdown.internal);
  const total = readObjectValue(breakdown.total);
  const participantRecipients = normalizeEmailList(
    item.participant_recipients ||
    item.participantRecipients ||
    participants.recipients,
  );
  const internalRecipients = normalizeEmailList(
    item.internal_recipients ||
    item.internalRecipients ||
    internal.recipients,
  );
  const recipients = normalizeEmailList(
    item.recipients ||
    item.resolved_recipients ||
    item.resolvedRecipients ||
    total.recipients,
  );
  const participantRecipientsCount = countRecipients(
    item.participant_recipients_count ?? item.participantRecipientsCount ?? participants.count,
    participantRecipients.length,
  );
  const internalRecipientsCount = countRecipients(
    item.internal_recipients_count ?? item.internalRecipientsCount ?? internal.count,
    internalRecipients.length,
  );
  const recipientsCount = countRecipients(
    item.recipients_count ?? item.recipientsCount ?? total.count,
    recipients.length,
  );
  const summary = hasBreakdown
    ? `参加者 ${participantRecipientsCount}名 / 内部共有 ${internalRecipientsCount}名 / 合計 ${recipientsCount}名`
    : '';

  return {
    hasBreakdown,
    raw: breakdown,
    recipients,
    recipientsCount,
    participantRecipients,
    participantRecipientsCount,
    internalRecipients,
    internalRecipientsCount,
    summary,
    json: hasBreakdown ? stringifyJsonForDisplay(breakdown) : '',
  };
};

const getNotificationDisplay = (eventKey = '') => {
  if (notificationEventDisplay[eventKey]) {
    return notificationEventDisplay[eventKey];
  }

  const category = eventKey.split('.')[0] || 'notification';

  return {
    categoryLabel: category,
    eventLabel: eventKey || '通知',
    sourceLabel: category,
    triggerPoint: eventKey || '',
  };
};

const getNotificationEventDisplayProjection = (item = {}, fallback = {}, eventKey = '') => {
  const display = item.event_display && typeof item.event_display === 'object'
    ? item.event_display
    : {};

  const projectedDisplay = {
    categoryLabel: item.category_label ||
      display.category_label ||
      display.categoryLabel ||
      fallback.categoryLabel ||
      '',
    eventLabel: item.event_label ||
      display.event_label ||
      display.eventLabel ||
      fallback.eventLabel ||
      item.event_name ||
      '',
    timingLabel: item.timing_label ||
      display.timing_label ||
      display.timingLabel ||
      fallback.timingLabel ||
      item.trigger_point ||
      fallback.triggerPoint ||
      '',
    triggerPoint: item.trigger_point ||
      display.trigger_point ||
      display.triggerPoint ||
      fallback.triggerPoint ||
      '',
    sourceLabel: item.source_label ||
      display.source_label ||
      display.sourceLabel ||
      fallback.sourceLabel ||
      '',
  };

  return {
    ...projectedDisplay,
    ...getNotificationDisplayOverride(eventKey),
  };
};

const buildNotificationRecipientSummary = (item = {}) => {
  const recipients = item.resolved_recipients;

  if (Array.isArray(recipients)) {
    if (!recipients.length) {
      return '宛先なし';
    }

    if (recipients.length <= 2) {
      return recipients.join(' / ');
    }

    return `${recipients.length}件`;
  }

  const count = Number(item.recipients_count || 0);

  return count > 0 ? `${count}件` : '宛先なし';
};

const buildNotificationErrorSummary = ({
  lastErrorMessage,
  providerDeliveryErrorMessage,
} = {}) => {
  const lines = [];

  if (lastErrorMessage) {
    lines.push(`アプリ送信処理: ${lastErrorMessage}`);
  }

  if (providerDeliveryErrorMessage) {
    lines.push(`プロバイダー配送: ${providerDeliveryErrorMessage}`);
  }

  return lines.length ? lines.join('\n') : 'なし';
};

const normalizeNotificationTriggerContext = (item = {}) => {
  const context = readObjectValue(item.trigger_context || item.trigger_context_json);
  const requestKind = context.request_kind || context.requestKind || '';
  const targetKind = context.target_kind || context.targetKind || '';

  return {
    raw: context,
    requestKind,
    requestKindLabel: onboardingRequestKindLabels[requestKind] || requestKind || '',
    targetKind,
    targetKindLabel: onboardingTargetKindLabels[targetKind] || targetKind || '',
    onboardingRequestId: context.onboarding_request_id || context.onboardingRequestId || '',
  };
};

const buildNotificationContextSummary = (triggerContext) => {
  if (!triggerContext.requestKindLabel && !triggerContext.targetKindLabel) {
    return '';
  }

  return [triggerContext.requestKindLabel, triggerContext.targetKindLabel].filter(Boolean).join(' / ');
};

export const normalizeNotificationDispatch = (item = {}) => {
  const eventKey = item.event_key || '';
  const display = getNotificationDisplay(eventKey);
  const eventDisplay = getNotificationEventDisplayProjection(item, display, eventKey);
  const processedAt = item.processed_at || item.created_at || '';
  const policyEventKey = item.policy?.event_key || eventKey;
  const sendStage = item.send_stage || '';
  const sendResult = item.send_result || '';
  const providerDeliveryTracked = item.provider_delivery_tracked === true;
  const providerDeliveryStatus = item.provider_delivery_status || '';
  const providerEngagementTracked = item.provider_engagement_tracked === true;
  const providerEngagementStatus = item.provider_engagement_status || '';
  const providerEngagementLastType = item.provider_engagement_last_type || '';
  const providerEngagementLastAt = item.provider_engagement_last_at || '';
  const providerEngagementSummary = item.provider_engagement_summary || '';
  const lastErrorMessage = item.last_error_message || '';
  const providerDeliveryErrorMessage = item.provider_delivery_error_message || '';
  const triggerContext = normalizeNotificationTriggerContext(item);
  const triggerContextSummary = buildNotificationContextSummary(triggerContext);
  const recipientBreakdown = normalizeRecipientBreakdownProjection(item);
  const providerEngagementLastTypeLabel =
    notificationProviderEngagementTypeLabels[providerEngagementLastType] ||
    providerEngagementLastType ||
    '';
  const providerEngagementLastAtLabel = formatYowanAdminDateTime(providerEngagementLastAt);
  const providerEngagementLastLabel = [providerEngagementLastTypeLabel, providerEngagementLastAtLabel]
    .filter(Boolean)
    .join(' / ');

  return {
    id: item.id || '',
    categoryLabel: eventDisplay.categoryLabel || display.categoryLabel,
    eventLabel: eventDisplay.eventLabel || display.eventLabel,
    eventKey,
    status: item.status || '',
    statusLabel: notificationStatusLabels[item.status] || item.status || '-',
    channelLabel: notificationChannelLabels[item.channel] || item.channel || '-',
    deliveryModeLabel: notificationDeliveryModeLabels[item.delivery_mode] || item.delivery_mode || '-',
    sendStage,
    sendStageLabel: notificationSendStageLabels[sendStage] || sendStage || '-',
    sendResult,
    sendResultLabel: notificationSendResultLabels[sendResult] || sendResult || '-',
    providerDeliveryTracked,
    providerDeliveryTrackedLabel: providerDeliveryTracked ? '追跡あり' : '未追跡',
    providerDeliveryStatus,
    providerDeliveryStatusLabel:
      notificationProviderDeliveryStatusLabels[providerDeliveryStatus] || providerDeliveryStatus || '-',
    providerEngagementTracked,
    providerEngagementTrackedLabel: providerEngagementTracked ? '追跡あり' : '未追跡',
    providerEngagementStatus,
    providerEngagementStatusLabel:
      notificationProviderEngagementStatusLabels[providerEngagementStatus] || providerEngagementStatus || '-',
    providerEngagementLastType,
    providerEngagementLastTypeLabel: providerEngagementLastTypeLabel || '-',
    providerEngagementLastAt,
    providerEngagementLastAtLabel: providerEngagementLastAtLabel || '-',
    providerEngagementLastLabel: providerEngagementLastLabel || '-',
    providerEngagementSummary,
    templateKey: item.template_key || '',
    subject: item.rendered_subject || '',
    recipientSummary: buildNotificationRecipientSummary(item),
    recipientBreakdown,
    hasRecipientBreakdown: recipientBreakdown.hasBreakdown,
    recipientBreakdownSummary: recipientBreakdown.summary,
    recipientBreakdownJson: recipientBreakdown.json,
    sourceLabel: eventDisplay.sourceLabel || display.sourceLabel,
    sourceKind: eventKey,
    triggerPoint: eventDisplay.triggerPoint || display.triggerPoint,
    triggerContext,
    triggerContextSummary,
    onboardingRequestKindLabel: triggerContext.requestKindLabel,
    onboardingTargetKindLabel: triggerContext.targetKindLabel,
    onboardingRequestId: triggerContext.onboardingRequestId,
    processedAt: formatYowanAdminDateTime(processedAt),
    detailHref: item.id ? `/admin/notifications/${item.id}` : '/admin/notifications',
    renderedBody: item.rendered_body || '',
    resolvedRecipientsJson: stringifyJsonForDisplay(item.resolved_recipients),
    triggerContextJson: stringifyJsonForDisplay(item.trigger_context || item.trigger_context_json),
    lastErrorMessage,
    providerDeliveryErrorMessage,
    errorSummary: buildNotificationErrorSummary({
      lastErrorMessage,
      providerDeliveryErrorMessage,
    }),
    policy: item.policy || null,
    policyEventKey,
    policyHref: policyEventKey ? `/admin/notifications/policies/${policyEventKey}` : '',
  };
};

const normalizeYowanSeminarParticipantMailPayload = (item = {}) => {
  const recipientBreakdown = normalizeRecipientBreakdownProjection(item);

  return {
    ...item,
    recipientScope: item.recipient_scope || item.recipientScope || '',
    recipients: recipientBreakdown.recipients,
    recipientsCount: recipientBreakdown.recipientsCount,
    participantRecipients: recipientBreakdown.participantRecipients,
    participantRecipientsCount: recipientBreakdown.participantRecipientsCount,
    internalRecipients: recipientBreakdown.internalRecipients,
    internalRecipientsCount: recipientBreakdown.internalRecipientsCount,
    hasRecipientBreakdown: recipientBreakdown.hasBreakdown,
    recipientBreakdown: recipientBreakdown.raw,
    recipientBreakdownSummary: recipientBreakdown.summary,
    recipientBreakdownJson: recipientBreakdown.json,
    defaultSubject: item.default_subject || item.defaultSubject || '',
    defaultBody: item.default_body || item.defaultBody || '',
    target: item.target || {},
    dispatchId: item.dispatch_id || item.dispatchId || '',
    status: item.status || '',
  };
};

const normalizeNotificationAudience = (item = {}) => {
  const group = item.audience_group || item.audienceGroup || {};

  return {
    id: item.id || item.audience_id || group.group_key || '',
    audienceGroupKey: group.group_key || item.audience_group_key || '',
    audienceGroupLabel: group.group_label || item.audience_group_label || group.group_key || '',
    audienceGroupKind: group.group_kind || '',
    description: group.description || '',
    resolutionSummary: group.resolution_summary || '',
    configJson: stringifyJsonForDisplay(item.config_json),
  };
};

export const normalizeNotificationAudienceGroup = (item = {}) => {
  const groupKey = item.group_key || '';
  const recipientListFilter = item.recipient_list_filter && typeof item.recipient_list_filter === 'object'
    ? item.recipient_list_filter
    : null;
  const categoryKey = recipientListFilter?.category_key || item.preference_category || '';
  const canListRecipients = item.can_list_recipients === true;

  return {
    groupKey,
    groupLabel: item.group_label || groupKey || '-',
    groupKind: item.group_kind || '',
    audienceType: item.audience_type || '',
    audienceKey: item.audience_key || '',
    description: item.description || '',
    resolutionSummary: item.resolution_summary || '',
    relatedEventKeys: Array.isArray(item.related_event_keys) ? item.related_event_keys : [],
    relatedPolicyCount: Number(item.related_policy_count || 0),
    recipientVisibility: item.recipient_visibility || '',
    recipientVisibilityLabel: notificationRecipientVisibilityLabels[item.recipient_visibility] ||
      item.recipient_visibility ||
      '-',
    preferenceCategory: item.preference_category || '',
    canListRecipients,
    recipientListFilter,
    recipientListHref: canListRecipients && categoryKey
      ? `/admin/notifications/recipients?tab=list&category_key=${encodeURIComponent(categoryKey)}`
      : '',
    groupHref: groupKey
      ? `/admin/notifications/recipients?tab=groups&group_key=${encodeURIComponent(groupKey)}`
      : '/admin/notifications/recipients?tab=groups',
  };
};

const normalizeNotificationPreference = (item = {}) => ({
  id: item.id || '',
  categoryKey: item.category_key || '',
  categoryLabel: notificationPreferenceCategoryLabels[item.category_key] || item.category_key || '-',
  audienceGroupKey: item.audience_group_key || '',
  audienceGroupLabel: item.audience_group_label || '',
  isEnabled: item.is_enabled === true,
  disabledAt: formatYowanAdminDateTime(item.disabled_at),
  createdAt: formatYowanAdminDateTime(item.created_at),
  updatedAt: formatYowanAdminDateTime(item.updated_at),
});

const notificationRecipientSourceKindLabels = {
  member: '会員',
  yowan_member: '会員',
  yowan_member_profile: '会員プロフィール',
  applicant: '問い合わせ・申込者',
  yowan_applicant: '問い合わせ・申込者',
  instructor: '講師',
  yowan_instructor: '講師',
  instructor_access: '講師ログイン権限',
  yowan_instructor_access: '講師ログイン権限',
  organizer: '主催者',
  yowan_organizer: '主催者',
  organizer_access: '主催者ログイン権限',
  yowan_organizer_access: '主催者ログイン権限',
  blog_author: 'ブログ執筆者',
  yowan_blog_author: 'ブログ執筆者',
  blog_author_access: 'ブログ執筆者権限',
  yowan_blog_author_access: 'ブログ執筆者権限',
  blog_post: 'ブログ記事',
  yowan_blog_post: 'ブログ記事',
  contact: '問い合わせ',
  general_inquiry: '一般問合せ',
  seminar_application: 'Seminar申込',
  class_application: '教室申込',
};

const buildNotificationRecipientSourceHref = ({
  sourceKind = '',
  sourceId = '',
  contextKind = '',
  contextId = '',
} = {}) => {
  if (['member', 'yowan_member', 'yowan_member_profile'].includes(sourceKind) && sourceId) {
    return `/admin/members/${sourceId}/edit`;
  }

  if (['member', 'yowan_member', 'yowan_member_profile'].includes(contextKind) && contextId) {
    return `/admin/members/${contextId}/edit`;
  }

  if (['blog_author_access', 'yowan_blog_author_access'].includes(sourceKind) && sourceId) {
    return `/admin/content/blog-authors/${sourceId}/edit`;
  }

  if (['blog_author_access', 'yowan_blog_author_access'].includes(contextKind) && contextId) {
    return `/admin/content/blog-authors/${contextId}/edit`;
  }

  return '';
};

const normalizeNotificationRecipientSource = (item = {}) => {
  const sourceKind = item.source_kind || '';
  const sourceId = item.source_id || '';
  const contextKind = item.context_kind || '';
  const contextId = item.context_id || '';
  const sourceDetailHref = item.source_detail_href ||
    item.sourceDetailHref ||
    item.detail_href ||
    item.detailHref ||
    buildNotificationRecipientSourceHref({ sourceKind, sourceId, contextKind, contextId });
  const contextDetailHref = item.context_detail_href ||
    item.contextDetailHref ||
    buildNotificationRecipientSourceHref({ contextKind, contextId });

  return {
    id: item.id || '',
    sourceKind,
    sourceKindLabel: notificationRecipientSourceKindLabels[sourceKind] || sourceKind || '',
    sourceId,
    contextKind,
    contextKindLabel: notificationRecipientSourceKindLabels[contextKind] || contextKind || '',
    contextId,
    sourceDetailHref,
    contextDetailHref,
    displayName: item.display_name || '',
    isPrimary: item.is_primary === true,
    createdAt: formatYowanAdminDateTime(item.created_at),
    updatedAt: formatYowanAdminDateTime(item.updated_at),
  };
};

export const normalizeNotificationRecipient = (item = {}) => {
  const id = item.id || '';
  const preferences = Array.isArray(item.preferences)
    ? item.preferences.map(normalizeNotificationPreference)
    : [];
  const sources = Array.isArray(item.sources)
    ? item.sources.map(normalizeNotificationRecipientSource)
    : [];

  return {
    id,
    email: item.email || '',
    maskedEmail: item.masked_email || '',
    displayName: item.display_name || '',
    isActive: item.is_active !== false,
    sources,
    preferences,
    preferenceSummary: preferences.length
      ? preferences.map((preference) => `${preference.categoryLabel}: ${preference.isEnabled ? '受信' : '停止'}`).join(' / ')
      : '-',
    sourceSummary: sources.length
      ? sources.map((source) => source.displayName || source.sourceKindLabel || source.sourceKind || source.sourceId).filter(Boolean).join(' / ')
      : '-',
    createdAt: formatYowanAdminDateTime(item.created_at),
    updatedAt: formatYowanAdminDateTime(item.updated_at),
    detailHref: id ? `/admin/notifications/recipients/${id}` : '/admin/notifications/recipients',
  };
};

export const normalizeNotificationPreferenceBundle = (payload = {}) => {
  const preferences = Array.isArray(payload.preferences)
    ? payload.preferences.map(normalizeNotificationPreference)
    : [];

  return {
    recipient: {
      id: payload.recipient?.id || '',
      maskedEmail: payload.recipient?.masked_email || '',
      isActive: payload.recipient?.is_active !== false,
    },
    preferences,
    preferencesByCategory: preferences.reduce((accumulator, preference) => {
      accumulator[preference.categoryKey] = preference;
      return accumulator;
    }, {}),
    requestedCategory: payload.requested_category || '',
  };
};

export const normalizeNotificationPolicy = (item = {}) => {
  const eventKey = item.event_key || '';
  const display = getNotificationDisplay(eventKey);
  const policyDisplay = notificationPolicyDisplay[eventKey] || {};
  const eventDisplay = getNotificationEventDisplayProjection(item, {
    ...display,
    ...policyDisplay,
  }, eventKey);
  const category = item.category || eventKey.split('.')[0] || display.categoryLabel;
  const decisionStatus = item.decision_status || 'undecided';
  const defaultAction = item.default_action || '';
  const defaultDecisionStatus = ['send', 'skip'].includes(defaultAction) ? defaultAction : 'undecided';

  return {
    eventKey,
    category,
    categoryLabel: eventDisplay.categoryLabel || notificationCategoryLabels[category] || category || display.categoryLabel,
    eventName: eventDisplay.eventLabel || policyDisplay.eventLabel || display.eventLabel,
    timingLabel: eventDisplay.timingLabel || policyDisplay.timingLabel || display.triggerPoint || eventKey,
    defaultAction,
    defaultDecisionStatus,
    defaultActionLabel: notificationDefaultActionLabels[defaultAction] || defaultAction || '-',
    decisionStatus,
    decisionStatusLabel: notificationDecisionStatusLabels[decisionStatus] || decisionStatus || '-',
    channelLabel: notificationChannelLabels[item.channel] || item.channel || '-',
    deliveryModeLabel: notificationDeliveryModeLabels[item.delivery_mode] || item.delivery_mode || '-',
    templateKey: item.template_key || '',
    audienceSummary: item.audience_summary || '',
    triggerPoint: eventDisplay.triggerPoint || display.triggerPoint,
    notes: item.notes || '',
    configJson: stringifyJsonForDisplay(item.config_json),
    audiences: Array.isArray(item.audiences) ? item.audiences.map(normalizeNotificationAudience) : [],
    detailHref: eventKey ? `/admin/notifications/policies/${eventKey}` : '/admin/notifications/policies',
  };
};

function getUploadFile(value) {
  if (!value) {
    return null;
  }

  if (typeof File !== 'undefined' && value instanceof File) {
    return value;
  }

  if (typeof value.length === 'number') {
    return value[0] || null;
  }

  return null;
}

const assertYowanImageUploadSize = (file) => {
  if (!file || Number(file.size || 0) <= YOWAN_IMAGE_UPLOAD_MAX_BYTES) {
    return;
  }

  const error = new Error(YOWAN_IMAGE_UPLOAD_SIZE_ERROR_MESSAGE);
  error.isYowanImageUploadSizeError = true;
  throw error;
};

const isYowanAcceptedImageUploadType = (file) => !file?.type || String(file.type).startsWith('image/');

const assertYowanImageUploadType = (file) => {
  if (isYowanAcceptedImageUploadType(file)) {
    return;
  }

  const error = new Error(YOWAN_IMAGE_UPLOAD_TYPE_ERROR_MESSAGE);
  error.isYowanImageUploadTypeError = true;
  throw error;
};

export const validateYowanImageUploadFile = (value) => {
  const file = getUploadFile(value);

  if (!file) {
    return true;
  }

  if (Number(file.size || 0) > YOWAN_IMAGE_UPLOAD_MAX_BYTES) {
    return YOWAN_IMAGE_UPLOAD_SIZE_ERROR_MESSAGE;
  }

  if (!isYowanAcceptedImageUploadType(file)) {
    return YOWAN_IMAGE_UPLOAD_TYPE_ERROR_MESSAGE;
  }

  return true;
};

export const buildContentPostPayload = (
  formValues = {},
  primaryImageId = normalizeNullableId(formValues.primary_image_id),
  { includeSlug = false } = {},
) => {
  const payload = {
    title: formValues.title || '',
    excerpt: formValues.excerpt || '',
    body: formValues.body || '',
    is_published: normalizeBoolean(formValues.isPublished),
    published_at: formValues.publishedAt || '',
    primary_image_id: primaryImageId,
  };

  if (Object.prototype.hasOwnProperty.call(formValues, 'authorId')) {
    payload.author_id = normalizeNullableId(formValues.authorId);
  }

  if (includeSlug) {
    payload.slug = String(formValues.slug || '').trim();
  }

  return payload;
};

const buildBlogAuthorPostPayload = (
  formValues = {},
  primaryImageId = normalizeNullableId(formValues.primary_image_id),
  { includeSlug = false, canPublish = false } = {},
) => {
  const payload = {
    title: formValues.title || '',
    excerpt: formValues.excerpt || '',
    body: formValues.body || '',
    primary_image_id: primaryImageId,
  };

  if (includeSlug) {
    payload.slug = String(formValues.slug || '').trim();
  }

  if (canPublish) {
    payload.is_published = normalizeBoolean(formValues.isPublished);
    payload.published_at = formValues.publishedAt || '';
  }

  return payload;
};

const buildNewsPostPayload = (formValues = {}, { includeSlug = false } = {}) => {
  const notificationScheduledAt = formValues.notificationScheduledAt || '';
  const payload = {
    title: formValues.title || '',
    excerpt: formValues.excerpt || '',
    body: formValues.body || '',
    is_published: normalizeBoolean(formValues.isPublished),
    published_at: notificationScheduledAt,
    notification_scheduled_at: notificationScheduledAt,
    notify_on_publish: true,
  };

  if (includeSlug) {
    payload.slug = String(formValues.slug || '').trim();
  }

  return payload;
};

export const buildFounderCassettePayload = (
  formValues = {},
  primaryImageId = normalizeNullableId(formValues.primary_image_id),
) => ({
  title: formValues.title || '',
  body: formValues.body || '',
  display_order: Number(formValues.displayOrder || 0),
  is_published: normalizeBoolean(formValues.isPublished),
  primary_image_id: primaryImageId,
});

export const buildReviewPayload = (formValues = {}) => ({
  title: formValues.title || '',
  reviewer_name: formValues.reviewerName || '',
  reviewer_title: formValues.reviewerTitle || '',
  reviewer_url: String(formValues.reviewerUrl || '').trim(),
  body: formValues.body || '',
  display_order: Number(formValues.displayOrder || 0),
  is_published: normalizeBoolean(formValues.isPublished),
});

export const buildReviewsHeaderPayload = (
  formValues = {},
  primaryImageId = normalizeNullableId(formValues.primary_image_id),
) => ({
  title: formValues.title || '',
  body: formValues.body || '',
  display_order: Number(formValues.displayOrder || 0),
  is_published: normalizeBoolean(formValues.isPublished),
  primary_image_id: primaryImageId,
});

export const buildPageStaticSlotPayload = (
  formValues = {},
  primaryImageId = normalizeNullableId(formValues.primary_image_id),
) => ({
  admin_label: formValues.adminLabel || formValues.admin_label || formValues.title || '',
  admin_description: formValues.adminDescription || formValues.admin_description || formValues.body || '',
  title: formValues.title || '',
  body: formValues.body || '',
  display_order: Number(formValues.displayOrder || 0),
  is_published: normalizeBoolean(formValues.isPublished),
  primary_image_id: primaryImageId,
});

export const fetchYowanAdminContentMediaAssets = async ({ usageScope, isActive = true } = {}) => {
  const payload = await getJson('/api/yowan/admin/content/media/', {
    auth: true,
    params: {
      usage_scope: usageScope || undefined,
      is_active: normalizeIsActiveQueryParam(isActive),
    },
  });

  const rawAssets = Array.isArray(payload?.results)
    ? payload.results
    : Array.isArray(payload)
      ? payload
      : [];

  return rawAssets.map(normalizeContentMediaAsset);
};

export const createYowanAdminContentMediaAsset = async ({
  usageScope,
  image,
  altText = '',
  caption = '',
}) => {
  assertYowanImageUploadSize(image);
  assertYowanImageUploadType(image);

  const formData = new FormData();

  formData.append('usage_scope', usageScope);
  formData.append('image', image);
  formData.append('alt_text', altText);
  formData.append('caption', caption);

  const payload = await postFormData('/api/yowan/admin/content/media/create/', formData);

  return normalizeContentMediaAsset(payload);
};

export const fetchYowanBlogAuthorMediaAssets = async ({ isActive = true } = {}) => {
  const payload = await getJson('/api/yowan/content/blog-author/media/', {
    auth: true,
    params: {
      is_active: normalizeIsActiveQueryParam(isActive),
    },
  });

  const rawAssets = Array.isArray(payload?.results)
    ? payload.results
    : Array.isArray(payload)
      ? payload
      : [];

  return rawAssets.map(normalizeContentMediaAsset);
};

export const createYowanBlogAuthorMediaAsset = async ({
  image,
  altText = '',
  caption = '',
}) => {
  assertYowanImageUploadSize(image);
  assertYowanImageUploadType(image);

  const formData = new FormData();

  formData.append('image', image);
  formData.append('alt_text', altText);
  formData.append('caption', caption);

  const payload = await postFormData('/api/yowan/content/blog-author/media/create/', formData);

  return normalizeContentMediaAsset(payload);
};

const resolvePrimaryImageId = async (formValues = {}, usageScope) => {
  const uploadFile = getUploadFile(formValues.primary_image_upload);

  if (!uploadFile) {
    return normalizeNullableId(formValues.primary_image_id);
  }

  const asset = await createYowanAdminContentMediaAsset({
    usageScope,
    image: uploadFile,
    altText: formValues.primary_image_alt_text || '',
    caption: formValues.primary_image_caption || '',
  });

  return asset.id;
};

const resolveBlogAuthorPrimaryImageId = async (formValues = {}) => {
  const uploadFile = getUploadFile(formValues.primary_image_upload);

  if (!uploadFile) {
    return normalizeNullableId(formValues.primary_image_id);
  }

  const asset = await createYowanBlogAuthorMediaAsset({
    image: uploadFile,
    altText: formValues.primary_image_alt_text || '',
    caption: formValues.primary_image_caption || '',
  });

  return asset.id;
};

export const fetchYowanAdminBlogList = async ({ page = 1, pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE } = {}) => {
  const payload = await getJson('/api/yowan/admin/content/blog/', {
    auth: true,
    params: { page, page_size: pageSize },
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map((item) => normalizeAdminPostRow(item, 'blog')),
  };
};

export const fetchYowanAdminBlogDetail = async (id) => {
  const payload = await getJson(`/api/yowan/admin/content/blog/${id}/`, { auth: true });
  return normalizeAdminPostDetail(payload, 'blog');
};

export const createYowanAdminBlog = async (formValues) => {
  const primaryImageId = await resolvePrimaryImageId(formValues, 'blog');
  const payload = await postJson(
    '/api/yowan/admin/content/blog/create/',
    buildContentPostPayload(formValues, primaryImageId, { includeSlug: true }),
  );
  invalidateYowanPublicReadCache();

  return payload;
};

export const updateYowanAdminBlog = async (id, formValues) => {
  const primaryImageId = await resolvePrimaryImageId(formValues, 'blog');
  const payload = await postJson(`/api/yowan/admin/content/blog/${id}/update/`, buildContentPostPayload(formValues, primaryImageId));
  invalidateYowanPublicReadCache();

  return payload;
};

export const normalizeYowanAdminBlogAuthorAccess = (item = {}) => {
  const author = normalizeBlogAuthorSummary(item.author || item.blog_author);
  const personSource =
    item.person ||
    item.person_ledger ||
    item.personLedger ||
    item.person_profile ||
    item.personProfile ||
    item.author_person ||
    item.authorPerson ||
    item.blog_author_person ||
    item.blogAuthorPerson ||
    author?.person ||
    null;
  const authSubject = item.auth_subject || item.authSubject || {};
  const authSubjectId = item.auth_subject_id || item.authSubjectId || authSubject.id || '';
  const onboarding = item.onboarding || {};
  const memberSource = item.member || item.yowan_member || item.member_profile || null;
  const member = memberSource ? normalizeYowanAdminMember(memberSource) : null;
  const memberAccessSource = item.member_access || item.memberAccess || null;
  const memberAccess = memberAccessSource
    ? normalizeYowanAdminMemberAccess({
        ...memberAccessSource,
        member: memberAccessSource.member || memberSource || {},
      })
    : null;

  return {
    id: item.id || '',
    email: item.email || item.login_email || item.auth_subject_email || item.authSubjectEmail || '',
    authSubjectId,
    authSubject: authSubjectId
      ? normalizeAdminAuthSubject({
          ...authSubject,
          id: authSubjectId,
          email: authSubject.email || item.email || item.login_email || item.auth_subject_email || item.authSubjectEmail || '',
          login_email: authSubject.login_email || item.login_email || item.email || item.auth_subject_email || item.authSubjectEmail || '',
        })
      : null,
    author,
    authorId: item.author_id || item.blog_author_id || author?.id || '',
    authorName: item.author_name || item.display_name || author?.displayName || '',
    profileUrl: item.profile_url || author?.profileUrl || '',
    personId: item.person_id || item.personId || author?.personId || personSource?.id || '',
    person: normalizeYowanPerson(personSource),
    canPublishBlog: item.can_publish_blog === true || item.canPublishBlog === true,
    isActive: item.is_active === true || item.isActive === true,
    member,
    memberAccess,
    canEnsureMembership: item.can_ensure_membership === true || item.canEnsureMembership === true,
    ensureMembershipEndpoint: item.ensure_membership_endpoint || item.ensureMembershipEndpoint || '/api/yowan/admin/members/ensure-membership/',
    invitedAt: formatYowanAdminDateTime(item.invited_at || item.invitedAt),
    lastInvitedAt: formatYowanAdminDateTime(item.last_invited_at || item.lastInvitedAt),
    acceptedAt: formatYowanAdminDateTime(item.accepted_at || item.acceptedAt),
    onboarding: {
      passwordSetupRequired: onboarding.password_setup_required === true || onboarding.passwordSetupRequired === true,
      latestRequestId: onboarding.latest_request_id || onboarding.latestRequestId || '',
      latestRequestStatus: onboarding.latest_request_status || onboarding.latestRequestStatus || '',
      latestRequestExpiresAt: formatYowanAdminDateTime(onboarding.latest_request_expires_at || onboarding.latestRequestExpiresAt),
    },
    inviteStatus: item.invite_status || item.inviteStatus || '',
    onboardingRequestId: item.onboarding_request_id || item.onboardingRequestId || '',
    createdAt: formatYowanAdminDateTime(item.created_at || item.createdAt),
    updatedAt: formatYowanAdminDateTime(item.updated_at || item.updatedAt),
    editHref: `/admin/content/blog-authors/${item.id}/edit`,
  };
};

const buildYowanPersonPayload = (values = {}) => ({
  display_name: String(values.personDisplayName || values.displayName || values.display_name || '').trim(),
  name_kana: String(values.personNameKana || values.nameKana || values.name_kana || '').trim(),
  email: String(values.personEmail || '').trim(),
  phone: String(values.personPhone || values.phone || '').trim(),
  organization: String(values.personOrganization || values.organization || '').trim(),
  role_title: String(values.personRoleTitle || values.roleTitle || values.role_title || '').trim(),
  internal_note: String(values.personInternalNote || values.internalNote || values.internal_note || '').trim(),
  is_active: values.personIsActive !== false,
});

const hasYowanPersonLedgerInput = (values = {}) => [
  values.personDisplayName,
  values.displayName,
  values.display_name,
  values.personNameKana,
  values.nameKana,
  values.name_kana,
  values.personEmail,
  values.personPhone,
  values.phone,
  values.personOrganization,
  values.organization,
  values.personRoleTitle,
  values.roleTitle,
  values.role_title,
  values.personInternalNote,
  values.internalNote,
  values.internal_note,
].some((value) => String(value || '').trim());

export const fetchYowanAdminPersonList = async ({
  page = 1,
  pageSize = 100,
  q = '',
  isActive = true,
} = {}) => {
  const payload = await getJson('/api/yowan/admin/access/people/', {
    auth: true,
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
      is_active: isActive === '' ? undefined : isActive,
    },
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizeYowanPerson).filter(Boolean),
  };
};

export const fetchYowanAdminPersonDetail = async (personId) => {
  const payload = await getJson(`/api/yowan/admin/access/people/${personId}/`, { auth: true });

  return normalizeYowanPerson(payload);
};

export const createYowanAdminPerson = async (values = {}) => {
  const payload = await postJson('/api/yowan/admin/access/people/create/', buildYowanPersonPayload(values));

  return normalizeYowanPerson(payload);
};

export const updateYowanAdminPerson = async (personId, values = {}) => {
  const payload = await postJson(`/api/yowan/admin/access/people/${personId}/update/`, buildYowanPersonPayload(values));

  return normalizeYowanPerson(payload);
};

export const buildYowanPersonOptions = (people = [], currentPerson = null) => {
  const normalizedCurrentPerson = normalizeYowanPerson(currentPerson);
  const normalizedPeople = people.map(normalizeYowanPerson).filter(Boolean);
  const mergedPeople = normalizedCurrentPerson?.id
    ? [
        normalizedCurrentPerson,
        ...normalizedPeople.filter((person) => person.id !== normalizedCurrentPerson.id),
      ]
    : normalizedPeople;

  return mergedPeople.map((person) => ({
    value: person.id,
    label: [
      person.displayName || person.id,
      person.email,
      person.organization,
    ].filter(Boolean).join(' / '),
    person,
  }));
};

export const resolveYowanAdminPersonId = async (values = {}, currentPerson = null) => {
  const personId = normalizeNullableId(values.personId || values.person_id);
  const shouldSavePerson = hasYowanPersonLedgerInput(values);

  if (personId) {
    if (shouldSavePerson) {
      await updateYowanAdminPerson(personId, values);
    }

    return personId;
  }

  if (!shouldSavePerson) {
    return null;
  }

  const fallbackDisplayName = currentPerson?.displayName || values.authorName || values.instructorName || values.organizerName || '';
  const person = await createYowanAdminPerson({
    ...values,
    personDisplayName: values.personDisplayName || fallbackDisplayName,
  });

  return person?.id || null;
};

const buildYowanAdminBlogAuthorPayload = (values = {}) => {
  const payload = {
    email: String(values.email || '').trim(),
    author_name: String(values.authorName || values.author_name || '').trim(),
    profile_url: String(values.profileUrl || values.profile_url || '').trim(),
    person_id: normalizeNullableId(values.personId || values.person_id),
    can_publish_blog: normalizeBoolean(values.canPublishBlog),
    is_active: normalizeBoolean(values.isActive),
    send_invite: values.sendInvite !== false,
  };
  const blogAuthorId = normalizeNullableId(values.blogAuthorId || values.blog_author_id);

  if (blogAuthorId) {
    payload.blog_author_id = blogAuthorId;
  }

  return payload;
};

const buildYowanAdminBlogAuthorUpdatePayload = (values = {}) => ({
  author_name: String(values.authorName || values.author_name || '').trim(),
  profile_url: String(values.profileUrl || values.profile_url || '').trim(),
  person_id: normalizeNullableId(values.personId || values.person_id),
  can_publish_blog: normalizeBoolean(values.canPublishBlog),
  is_active: normalizeBoolean(values.isActive),
});

export const fetchYowanAdminBlogAuthorAccessList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  q = '',
  isActive = '',
} = {}) => {
  const payload = await getJson('/api/yowan/admin/access/blog-authors/', {
    auth: true,
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
      is_active: isActive || undefined,
    },
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizeYowanAdminBlogAuthorAccess),
  };
};

export const fetchYowanAdminBlogAuthorAccessDetail = async (id) => {
  const payload = await getJson(`/api/yowan/admin/access/blog-authors/${id}/`, { auth: true });
  return normalizeYowanAdminBlogAuthorAccess(payload);
};

export const inviteYowanAdminBlogAuthorAccess = async (values = {}) => {
  const payload = await postJson(
    '/api/yowan/admin/access/blog-authors/invite/',
    buildYowanAdminBlogAuthorPayload(values),
  );

  return normalizeYowanAdminBlogAuthorAccess(payload);
};

export const updateYowanAdminBlogAuthorAccess = async (id, values = {}) => {
  const payload = await postJson(
    `/api/yowan/admin/access/blog-authors/${id}/update/`,
    buildYowanAdminBlogAuthorUpdatePayload(values),
  );

  return normalizeYowanAdminBlogAuthorAccess(payload);
};

export const deactivateYowanAdminBlogAuthorAccess = async (id) => {
  const payload = await postJson(`/api/yowan/admin/access/blog-authors/${id}/deactivate/`, {});

  return normalizeYowanAdminBlogAuthorAccess(payload);
};

export const fetchYowanBlogAuthorPostList = async ({ page = 1, pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE } = {}) => {
  const payload = await getJson('/api/yowan/content/blog-author/posts/', {
    auth: true,
    params: { page, page_size: pageSize },
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map((item) => ({
      ...normalizeAdminPostRow(item, 'blog'),
      editHref: `/blog-author/posts/${item.id}/edit`,
    })),
  };
};

export const fetchYowanBlogAuthorPostDetail = async (id) => {
  const payload = await getJson(`/api/yowan/content/blog-author/posts/${id}/`, { auth: true });
  return {
    ...normalizeAdminPostDetail(payload, 'blog'),
    editHref: `/blog-author/posts/${payload.id}/edit`,
  };
};

export const createYowanBlogAuthorPost = async (formValues, { canPublish = false } = {}) => {
  const primaryImageId = await resolveBlogAuthorPrimaryImageId(formValues);
  const payload = await postJson(
    '/api/yowan/content/blog-author/posts/create/',
    buildBlogAuthorPostPayload(formValues, primaryImageId, { includeSlug: true, canPublish }),
  );
  invalidateYowanPublicReadCache();

  return payload;
};

export const updateYowanBlogAuthorPost = async (id, formValues, { canPublish = false } = {}) => {
  const primaryImageId = await resolveBlogAuthorPrimaryImageId(formValues);
  const payload = await postJson(
    `/api/yowan/content/blog-author/posts/${id}/update/`,
    buildBlogAuthorPostPayload(formValues, primaryImageId, { canPublish }),
  );
  invalidateYowanPublicReadCache();

  return payload;
};

export const fetchYowanAdminNewsList = async ({ page = 1, pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE } = {}) => {
  const payload = await getJson('/api/yowan/admin/content/news/', {
    auth: true,
    params: { page, page_size: pageSize },
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map((item) => normalizeAdminPostRow(item, 'news')),
  };
};

export const fetchYowanAdminNewsDetail = async (id) => {
  const payload = await getJson(`/api/yowan/admin/content/news/${id}/`, { auth: true });
  return normalizeAdminPostDetail(payload, 'news');
};

export const createYowanAdminNews = async (formValues) => {
  const payload = await postJson(
    '/api/yowan/admin/content/news/create/',
    buildNewsPostPayload(formValues, { includeSlug: true }),
  );
  invalidateYowanPublicReadCache();

  return payload;
};

export const updateYowanAdminNews = async (id, formValues) => {
  const payload = await postJson(
    `/api/yowan/admin/content/news/${id}/update/`,
    buildNewsPostPayload(formValues),
  );
  invalidateYowanPublicReadCache();

  return payload;
};

export const fetchYowanAdminNotificationDispatchList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  status,
  recipientEmail,
} = {}) => {
  const params = {
    page,
    page_size: pageSize,
  };

  if (status) {
    params.status = status;
  }

  if (recipientEmail) {
    params.recipient_email = recipientEmail;
  }

  const payload = await getJson('/api/yowan/admin/notifications/dispatches/', {
    auth: true,
    params,
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizeNotificationDispatch),
  };
};

export const fetchYowanAdminNotificationDispatchDetail = async (id) => {
  const payload = await getJson(`/api/yowan/admin/notifications/dispatches/${id}/`, { auth: true });
  return normalizeNotificationDispatch(payload);
};

const normalizeYowanAdminMailSenderIdentity = (source = {}) => {
  const item = source || {};

  return {
    id: item.id || '',
    label: item.label || item.display_name || item.displayName || item.email || '',
    displayName: item.display_name || item.displayName || '',
    email: item.email || '',
    domain: item.domain || '',
    replyToEmail: item.reply_to_email || item.replyToEmail || '',
    isShared: item.is_shared === true || item.isShared === true,
    isDefault: item.is_default === true || item.isDefault === true,
    isActive: item.is_active !== false && item.isActive !== false,
    ownerOperatorAccessId: item.owner_operator_access_id || item.ownerOperatorAccessId || '',
    usageScope: item.usage_scope || item.usageScope || '',
  };
};

const normalizeYowanMailHeaderValue = (value) => {
  if (Array.isArray(value)) {
    return value.filter(Boolean).join(', ');
  }

  return value || '';
};

const normalizeYowanAdminMailMessage = (item = {}) => {
  const metadata = item.metadata && typeof item.metadata === 'object' ? item.metadata : {};

  return {
    id: item.id || '',
    direction: item.direction || '',
    fromEmail: item.from_email || item.fromEmail || '',
    fromName: item.from_name || item.fromName || '',
    toAddresses: Array.isArray(item.to_addresses) ? item.to_addresses : Array.isArray(item.toAddresses) ? item.toAddresses : [],
    ccAddresses: Array.isArray(item.cc_addresses) ? item.cc_addresses : Array.isArray(item.ccAddresses) ? item.ccAddresses : [],
    bccAddresses: Array.isArray(item.bcc_addresses) ? item.bcc_addresses : Array.isArray(item.bccAddresses) ? item.bccAddresses : [],
    subject: item.subject || '',
    textBody: item.text_body || item.textBody || '',
    htmlBody: item.html_body || item.htmlBody || '',
    sentAt: formatYowanAdminDateTime(item.sent_at || item.sentAt),
    receivedAt: formatYowanAdminDateTime(item.received_at || item.receivedAt),
    readAt: formatYowanAdminDateTime(item.read_at || item.readAt),
    deliveryStatus: item.delivery_status || item.deliveryStatus || '',
    sendTaskStatus: item.send_task_status || item.sendTaskStatus || metadata.send_task_status || metadata.sendTaskStatus || '',
    effectiveReplyToEmail: normalizeYowanMailHeaderValue(item.effective_reply_to_email || item.effectiveReplyToEmail || metadata.effective_reply_to_email || metadata.effectiveReplyToEmail || metadata.django_message_reply_to || metadata.djangoMessageReplyTo),
    actualFromEmail: normalizeYowanMailHeaderValue(item.actual_from_email || item.actualFromEmail || metadata.django_message_from_email || metadata.djangoMessageFromEmail),
    inboundMessageId: item.inbound_message_id || item.inboundMessageId || '',
    emailMessageId: item.email_message_id || item.emailMessageId || '',
    notificationDispatchId: item.notification_dispatch_id || item.notificationDispatchId || '',
    metadata,
    createdAt: formatYowanAdminDateTime(item.created_at || item.createdAt),
  };
};

const normalizeYowanAdminMailThread = (item = {}) => ({
  id: item.id || '',
  subject: item.subject || '',
  participantEmail: item.participant_email || item.participantEmail || '',
  participantName: item.participant_name || item.participantName || '',
  status: item.status || '',
  latestMessageAt: formatYowanAdminDateTime(item.latest_message_at || item.latestMessageAt),
  latestDirection: item.latest_direction || item.latestDirection || '',
  latestExcerpt: item.latest_excerpt || item.latestExcerpt || '',
  messageCount: Number(item.message_count ?? item.messageCount ?? 0),
  unreadCount: Number(item.unread_count ?? item.unreadCount ?? 0),
  needsResponse: item.needs_response === true || item.needsResponse === true,
  createdAt: formatYowanAdminDateTime(item.created_at || item.createdAt),
  updatedAt: formatYowanAdminDateTime(item.updated_at || item.updatedAt),
  messages: Array.isArray(item.messages) ? item.messages.map(normalizeYowanAdminMailMessage) : [],
});

const normalizeYowanAdminMailSummary = (item = {}) => ({
  unreadCount: Number(item.unread_count ?? item.unreadCount ?? 0),
  needsResponseCount: Number(item.needs_response_count ?? item.needsResponseCount ?? 0),
  openThreadCount: Number(item.open_thread_count ?? item.openThreadCount ?? 0),
});

const normalizeYowanAdminMailCreateResult = (payload = {}) => {
  const item = payload || {};

  if (item.mode === 'bulk') {
    const rawThreads = Array.isArray(item.threads) ? item.threads : [];
    const rawResults = Array.isArray(item.results) ? item.results : [];

    return {
      mode: 'bulk',
      createdCount: Number(item.created_count ?? item.createdCount ?? rawResults.length ?? rawThreads.length ?? 0),
      results: rawResults.map((item) => ({
        threadId: item.thread_id || item.threadId || '',
        participantEmail: item.participant_email || item.participantEmail || '',
        messageId: item.message_id || item.messageId || '',
        deliveryStatus: item.delivery_status || item.deliveryStatus || '',
      })),
      threads: rawThreads.map(normalizeYowanAdminMailThread),
    };
  }

  return {
    mode: 'single',
    thread: normalizeYowanAdminMailThread(item.thread || item),
  };
};

export const fetchYowanAdminMailSenderIdentities = async () => {
  const payload = await getJson('/api/yowan/admin/mail/sender-identities/', { auth: true });
  const results = Array.isArray(payload?.results) ? payload.results : Array.isArray(payload) ? payload : [];

  return results.map(normalizeYowanAdminMailSenderIdentity);
};

export const fetchYowanAdminMailThreadList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  q = '',
  status = '',
  participantEmail = '',
} = {}) => {
  const payload = await getJson('/api/yowan/admin/mail/threads/', {
    auth: true,
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
      status: status || undefined,
      participant_email: participantEmail || undefined,
    },
  });
  const envelope = normalizePaginationEnvelope(payload);
  const normalizedResults = envelope.results.map(normalizeYowanAdminMailThread);
  const shouldApplyLocalSlice = normalizedResults.length > pageSize;
  const localStartIndex = Math.max(0, (page - 1) * pageSize);

  return {
    ...envelope,
    results: shouldApplyLocalSlice
      ? normalizedResults.slice(localStartIndex, localStartIndex + pageSize)
      : normalizedResults,
  };
};

export const fetchYowanAdminMailThreadDetail = async (threadId) => {
  const payload = await getJson(`/api/yowan/admin/mail/threads/${threadId}/`, { auth: true });

  return normalizeYowanAdminMailThread(payload);
};

export const fetchYowanAdminMailSummary = async () => {
  const payload = await getJson('/api/yowan/admin/mail/summary/', { auth: true });

  return normalizeYowanAdminMailSummary(payload);
};

export const createYowanAdminMailThread = async ({
  senderIdentityId,
  to = [],
  cc = [],
  bcc = [],
  subject = '',
  body = '',
} = {}) => {
  const payload = await postJson('/api/yowan/admin/mail/threads/', {
    sender_identity_id: senderIdentityId,
    to,
    cc,
    bcc,
    subject,
    body,
  });

  return normalizeYowanAdminMailCreateResult(payload);
};

export const replyYowanAdminMailThread = async (threadId, {
  senderIdentityId,
  cc = [],
  bcc = [],
  body = '',
} = {}) => {
  const payload = await postJson(`/api/yowan/admin/mail/threads/${threadId}/reply/`, {
    sender_identity_id: senderIdentityId,
    cc,
    bcc,
    body,
  });

  return normalizeYowanAdminMailThread(payload);
};

export const updateYowanAdminMailThread = async (threadId, { status } = {}) => {
  const payload = await patchJson(`/api/yowan/admin/mail/threads/${threadId}/`, {
    status,
  });

  return normalizeYowanAdminMailThread(payload);
};

export const fetchYowanAdminNotificationRecipientList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  isActive,
  categoryKey,
  query,
} = {}) => {
  const params = {
    page,
    page_size: pageSize,
  };

  if (isActive === true || isActive === false) {
    params.is_active = isActive ? 'true' : 'false';
  }

  if (categoryKey) {
    params.category_key = categoryKey;
  }

  if (query) {
    params.q = query;
  }

  const payload = await getJson('/api/yowan/admin/notifications/recipients/', {
    auth: true,
    params,
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizeNotificationRecipient),
  };
};

export const fetchYowanAdminNotificationRecipientDetail = async (id) => {
  const payload = await getJson(`/api/yowan/admin/notifications/recipients/${id}/`, { auth: true });
  return normalizeNotificationRecipient(payload);
};

export const fetchYowanAdminNotificationAudienceGroupList = async ({
  page = 1,
  pageSize = 100,
} = {}) => {
  const payload = await getJson('/api/yowan/admin/notifications/audience-groups/', {
    auth: true,
    params: { page, page_size: pageSize },
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizeNotificationAudienceGroup),
  };
};

export const fetchYowanAdminNotificationAudienceGroupDetail = async (groupKey) => {
  const payload = await getJson(
    `/api/yowan/admin/notifications/audience-groups/${encodeURIComponent(groupKey)}/`,
    { auth: true },
  );

  return normalizeNotificationAudienceGroup(payload);
};

export const fetchYowanAdminFounderList = async () => {
  const payload = await getJson('/api/yowan/admin/content/founder/', { auth: true });
  const results = Array.isArray(payload?.results) ? payload.results : [];

  return {
    results: results.map(normalizeFounderCassette),
  };
};

export const fetchYowanAdminFounderDetail = async (id) => {
  const payload = await getJson(`/api/yowan/admin/content/founder/${id}/`, { auth: true });
  return normalizeFounderCassette(payload);
};

export const createYowanAdminFounderCassette = async (formValues) => {
  const primaryImageId = await resolvePrimaryImageId(formValues, 'founder');
  const payload = await postJson(
    '/api/yowan/admin/content/founder/create/',
    buildFounderCassettePayload(formValues, primaryImageId),
  );
  invalidateYowanPublicReadCache();

  return payload;
};

export const updateYowanAdminFounderCassette = async (id, formValues) => {
  const primaryImageId = await resolvePrimaryImageId(formValues, 'founder');
  const payload = await postJson(
    `/api/yowan/admin/content/founder/${id}/update/`,
    buildFounderCassettePayload(formValues, primaryImageId),
  );
  invalidateYowanPublicReadCache();

  return payload;
};

export const fetchYowanAdminReviewsList = async () => {
  const payload = await getJson('/api/yowan/admin/content/reviews/', { auth: true });
  const results = Array.isArray(payload?.results) ? payload.results : [];

  return {
    results: results.map((item) => normalizeReviewEntry(item, { defaultPublished: false })),
  };
};

export const fetchYowanAdminReviewDetail = async (id) => {
  const payload = await getJson(`/api/yowan/admin/content/reviews/${id}/`, { auth: true });
  return normalizeReviewEntry(payload, { defaultPublished: false });
};

export const createYowanAdminReview = async (formValues) => {
  const payload = await postJson('/api/yowan/admin/content/reviews/create/', buildReviewPayload(formValues));
  invalidateYowanPublicReadCache();

  return normalizeReviewEntry(payload, { defaultPublished: false });
};

export const updateYowanAdminReview = async (id, formValues) => {
  const payload = await postJson(`/api/yowan/admin/content/reviews/${id}/update/`, buildReviewPayload(formValues));
  invalidateYowanPublicReadCache();

  return normalizeReviewEntry(payload, { defaultPublished: false });
};

export const fetchYowanAdminReviewsSettings = async () => {
  const payload = await getJson('/api/yowan/admin/content/reviews/settings/', { auth: true });

  return {
    header: normalizeReviewsHeader(payload?.header),
  };
};

export const updateYowanAdminReviewsSettings = async (formValues) => {
  const primaryImageId = await resolvePrimaryImageId(formValues, 'reviews');
  const payload = await postJson(
    '/api/yowan/admin/content/reviews/settings/update/',
    buildReviewsHeaderPayload(formValues, primaryImageId),
  );
  invalidateYowanPublicReadCache();

  return {
    header: normalizeReviewsHeader(payload?.header),
  };
};

export const fetchYowanAdminPageContentsList = async () => {
  const payload = await getJson('/api/yowan/admin/content/page-static-slots/', { auth: true });
  const rawSlots = Array.isArray(payload?.results)
    ? payload.results
    : Array.isArray(payload)
      ? payload
      : [];

  return { results: rawSlots.map(normalizePageContentSlot) };
};

export const fetchYowanAdminPageContentDetail = async (pageKey, slotKey) => {
  const payload = await getJson(
    `/api/yowan/admin/content/page-static-slots/${pageKey}/${slotKey}/`,
    { auth: true },
  );

  return normalizePageContentSlot(payload);
};

export const updateYowanAdminPageContentSlot = async (pageKey, slotKey, formValues = {}) => {
  const slotKeyValue = buildPageContentSlotKey(pageKey, slotKey);
  const metadata = PAGE_CONTENT_SLOT_METADATA[slotKeyValue] || {};
  const usageScope = formValues.usageScope || metadata.usageScope || 'shared';
  const primaryImageId = await resolvePrimaryImageId(formValues, usageScope);
  const payload = await postJson(
    `/api/yowan/admin/content/page-static-slots/${pageKey}/${slotKey}/update/`,
    buildPageStaticSlotPayload(formValues, primaryImageId),
  );
  invalidateYowanPublicReadCache();

  return normalizePageContentSlot(payload);
};

export const fetchYowanPublicBlogList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_PUBLIC_PAGE_SIZE,
  bypassCache = false,
} = {}) => {
  const payload = await getPublicCachedJson('/api/yowan/content/blog/', {
    params: { page, page_size: pageSize },
    bypassCache,
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizePublicBlogEntry),
  };
};

export const fetchYowanPublicBlogDetail = async (slug, { bypassCache = false } = {}) => {
  const payload = await getPublicCachedJson(`/api/yowan/content/blog/${slug}/`, { bypassCache });
  return normalizePublicBlogDetail(payload);
};

export const fetchYowanPublicFounder = async ({ bypassCache = false } = {}) => {
  const payload = await getPublicCachedJson('/api/yowan/content/founder/', { bypassCache });
  const cassettes = Array.isArray(payload?.cassettes) ? payload.cassettes : [];

  return {
    pageKey: payload?.page_key || 'founder',
    cassettes: cassettes.map(normalizeFounderCassette),
  };
};

export const fetchYowanPublicReviews = async ({ bypassCache = false } = {}) => {
  const payload = await getPublicCachedJson('/api/yowan/content/reviews/', { bypassCache });
  const results = Array.isArray(payload?.results) ? payload.results : [];

  return {
    header: normalizeReviewsHeader(payload?.header),
    results: results.map(normalizeReviewEntry),
  };
};

export const fetchYowanPublicPageStaticSlots = async (pageKey, { bypassCache = false } = {}) => {
  const normalizedPageKey = pageKey || 'top';
  const payload = await getPublicCachedJson(
    `/api/yowan/content/page-static-slots/${normalizedPageKey}/`,
    { bypassCache },
  );
  const results = Array.isArray(payload?.results) ? payload.results : [];

  return {
    pageKey: payload?.page_key || payload?.pageKey || normalizedPageKey,
    results: results.map(normalizePublicPageStaticSlot),
  };
};

export const fetchYowanPublicTopPageSlots = (options = {}) =>
  fetchYowanPublicPageStaticSlots('top', options);

export const fetchYowanPublicSitePageSlots = (options = {}) =>
  fetchYowanPublicPageStaticSlots('site', options);

export const fetchYowanAdminNotificationPolicyList = async () => {
  const payload = await getJson('/api/yowan/admin/notifications/policies/', {
    auth: true,
    params: { page_size: 100 },
  });
  const rawPolicies = Array.isArray(payload?.results)
    ? payload.results
    : Array.isArray(payload)
      ? payload
      : [];

  return { results: rawPolicies.map(normalizeNotificationPolicy) };
};

export const fetchYowanAdminNotificationPolicyDetail = async (eventKey) => {
  const payload = await getJson(
    `/api/yowan/admin/notifications/policies/${encodeURIComponent(eventKey)}/`,
    { auth: true },
  );

  return normalizeNotificationPolicy(payload);
};

export const updateYowanAdminNotificationPolicyDecision = async (eventKey, decisionStatus) => {
  const payload = await postJson(
    `/api/yowan/admin/notifications/policies/${encodeURIComponent(eventKey)}/decision/`,
    { decision_status: decisionStatus },
  );

  return normalizeNotificationPolicy(payload);
};

const normalizeAdminAuthSubject = (item = {}) => ({
  id: item.id || '',
  email: item.email || item.login_email || '',
  loginEmail: item.login_email || item.email || '',
  userEmail: item.user_email || item.userEmail || '',
  isActive: item.is_active === true,
  passwordSetupRequired: item.password_setup_required === true,
  createdAt: formatYowanAdminDateTime(item.created_at),
  updatedAt: formatYowanAdminDateTime(item.updated_at),
});

const onboardingRequestStatusLabels = {
  draft: '未送信',
  sent: '送信済み',
  send_failed: '送信失敗',
  opened: '本人確認中',
  submitted: '提出済み',
  completed: '完了',
  expired: '期限切れ',
  canceled: '取消済み',
};

const normalizeYowanOnboardingRequestSummary = (item = null) => {
  if (!item) {
    return null;
  }

  const statusValue = item.status || '';
  const requestKind = item.request_kind || item.requestKind || '';

  return {
    id: item.id || '',
    requestKind,
    requestKindLabel: onboardingRequestKindLabels[requestKind] || requestKind || '-',
    targetKind: item.target_kind || item.targetKind || '',
    email: item.email || '',
    status: statusValue,
    statusLabel: onboardingRequestStatusLabels[statusValue] || statusValue || '-',
    expiresAt: formatYowanAdminDateTime(item.expires_at || item.expiresAt),
    sentAt: formatYowanAdminDateTime(item.sent_at || item.sentAt),
    submittedAt: formatYowanAdminDateTime(item.submitted_at || item.submittedAt),
    completedAt: formatYowanAdminDateTime(item.completed_at || item.completedAt),
    resendAvailable: item.resend_available === true || item.resendAvailable === true,
  };
};

const profileJsonToTextareaValue = (value) => {
  if (!value) {
    return '{}';
  }

  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return '{}';
  }
};

const parseProfileJsonValue = (value) => {
  if (!value || !String(value).trim()) {
    return {};
  }

  if (typeof value === 'object') {
    return value;
  }

  return JSON.parse(value);
};

export const normalizeYowanAdminMember = (item = {}) => {
  const account = item.account || {};
  const lifecycleStatus = item.lifecycle_status || 'active';
  const latestOnboardingRequest = normalizeYowanOnboardingRequestSummary(item.latest_onboarding_request);
  const memberAccessSource = item.member_access || item.memberAccess || null;
  const operatorAccessSource = item.operator_access || item.operatorAccess || null;
  const instructorProfileSource = item.instructor_profile || item.instructorProfile || null;
  const organizerProfileSource = item.organizer_profile || item.organizerProfile || null;
  const memberAccess = memberAccessSource
    ? normalizeYowanAdminMemberAccess({
        ...memberAccessSource,
        member_id: memberAccessSource.member_id || memberAccessSource.memberId || item.id,
        member: memberAccessSource.member || item,
      })
    : null;
  const operatorAccess = operatorAccessSource
    ? normalizeYowanAdminOperatorAccess({
        ...operatorAccessSource,
        member_id: operatorAccessSource.member_id || operatorAccessSource.memberId || item.id,
        member: operatorAccessSource.member || item,
      })
    : null;

  return {
    id: item.id || '',
    name: item.name || '',
    nameKana: item.name_kana || '',
    email: item.email || '',
    phone: item.phone || '',
    postalCode: item.postal_code || '',
    address: item.address || '',
    note: item.note || '',
    profileJson: profileJsonToTextareaValue(item.profile_json),
    lifecycleStatus,
    isActive: item.is_active === true,
    accountEmail: account.email || account.login_email || '',
    accountIsActive: account.is_active === true,
    accountPasswordSetupRequired: account.password_setup_required === true,
    latestOnboardingRequest,
    memberAccess,
    operatorAccess,
    instructorProfile: instructorProfileSource
      ? {
          id: instructorProfileSource.id || instructorProfileSource.instructor_id || instructorProfileSource.instructorId || '',
          isActive: instructorProfileSource.is_active !== false && instructorProfileSource.isActive !== false,
        }
      : null,
    organizerProfile: organizerProfileSource
      ? {
          id: organizerProfileSource.id || organizerProfileSource.organizer_id || organizerProfileSource.organizerId || '',
          isActive: organizerProfileSource.is_active !== false && organizerProfileSource.isActive !== false,
        }
      : null,
    createdAt: formatYowanAdminDateTime(item.created_at),
    updatedAt: formatYowanAdminDateTime(item.updated_at),
    editHref: `/admin/members/${item.id}/edit`,
  };
};

const buildYowanAdminMemberPayload = (values = {}, { authSubjectId, includeProfileJson = false } = {}) => {
  const lifecycleStatus = values.lifecycleStatus || values.lifecycle_status || 'active';
  const payload = {
    name: values.name || '',
    name_kana: values.nameKana || values.name_kana || '',
    email: values.email || '',
    phone: values.phone || '',
    postal_code: values.postalCode || values.postal_code || '',
    address: values.address || '',
    note: values.note || '',
    lifecycle_status: lifecycleStatus,
  };

  if (values.isActive !== undefined || values.is_active !== undefined) {
    payload.is_active = normalizeBoolean(values.isActive ?? values.is_active);
  }

  if (includeProfileJson) {
    payload.profile_json = parseProfileJsonValue(values.profileJson || values.profile_json);
  }

  if (authSubjectId) {
    payload.auth_subject = authSubjectId;
  }

  return payload;
};

export const fetchYowanAdminMemberList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  q = '',
  lifecycleStatus = '',
} = {}) => {
  const payload = await getJson('/api/yowan/admin/members/', {
    auth: true,
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
      lifecycle_status: lifecycleStatus || undefined,
    },
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizeYowanAdminMember),
  };
};

export const fetchYowanAdminMemberDetail = async (id) => {
  const payload = await getJson(`/api/yowan/admin/members/${id}/`, { auth: true });
  return normalizeYowanAdminMember(payload);
};

export const createYowanAdminAuthSubject = async ({ email, password, isActive = true } = {}) => {
  const payload = await postJson('/api/yowan/admin/auth/subjects/create/', {
    email,
    password,
    is_active: isActive,
  });

  return normalizeAdminAuthSubject(payload);
};

export const createYowanAdminMember = async (values = {}) => {
  const authSubject = await createYowanAdminAuthSubject({
    email: values.email,
    password: values.password,
    isActive: true,
  });
  const payload = await postJson(
    '/api/yowan/admin/members/create/',
    buildYowanAdminMemberPayload(values, { authSubjectId: authSubject.id, includeProfileJson: true }),
  );

  return normalizeYowanAdminMember(payload);
};

export const createYowanAdminMemberWithAccount = async (values = {}) => {
  const payload = await postJson('/api/yowan/admin/members/create-with-account/', {
    name: values.name || '',
    name_kana: values.nameKana || values.name_kana || '',
    email: values.email || '',
    phone: values.phone || '',
    postal_code: values.postalCode || values.postal_code || '',
    address: values.address || '',
    note: values.note || '',
    profile_json: {},
    lifecycle_status: values.lifecycleStatus || values.lifecycle_status || 'active',
    send_password_setup: values.sendPasswordSetup !== false,
  });

  const member = normalizeYowanAdminMember({
    ...payload.member,
    account: payload.auth_subject,
    latest_onboarding_request: payload.onboarding_request,
  });

  return {
    member,
    authSubject: normalizeAdminAuthSubject(payload.auth_subject),
    memberAccess: normalizeYowanAdminMemberAccess(payload.member_access),
    onboardingRequest: normalizeYowanOnboardingRequestSummary(payload.onboarding_request),
  };
};

export const ensureYowanAdminMemberMembership = async (values = {}) => {
  const payload = await postJson('/api/yowan/admin/members/ensure-membership/', {
    auth_subject_id: values.authSubjectId || values.auth_subject_id || undefined,
    email: values.email || undefined,
    name: values.name || '',
    name_kana: values.nameKana || values.name_kana || '',
    phone: values.phone || '',
    postal_code: values.postalCode || values.postal_code || '',
    address: values.address || '',
    note: values.note || '',
    profile_json: values.profileJson || values.profile_json || {},
    send_password_setup: values.sendPasswordSetup !== false,
  });

  const member = normalizeYowanAdminMember({
    ...payload.member,
    account: payload.auth_subject,
    latest_onboarding_request: payload.onboarding_request,
  });

  return {
    member,
    authSubject: normalizeAdminAuthSubject(payload.auth_subject),
    memberAccess: normalizeYowanAdminMemberAccess(payload.member_access),
    onboardingStatus: payload.onboarding_status || payload.onboardingStatus || '',
    onboardingRequest: normalizeYowanOnboardingRequestSummary(payload.onboarding_request),
    createdMember: payload.created_member === true || payload.createdMember === true,
    reactivatedMember: payload.reactivated_member === true || payload.reactivatedMember === true,
    createdMemberAccess: payload.created_member_access === true || payload.createdMemberAccess === true,
    reactivatedMemberAccess: payload.reactivated_member_access === true || payload.reactivatedMemberAccess === true,
    raw: payload,
  };
};

export const resendYowanAdminOnboardingRequest = async (id) => {
  const payload = await postJson(`/api/yowan/admin/onboarding-requests/${id}/resend/`, {});

  return normalizeYowanOnboardingRequestSummary(payload);
};

export const createYowanAdminMemberPasswordSetupRequest = async (memberId) => {
  const payload = await postJson('/api/yowan/admin/onboarding-requests/create/', {
    request_kind: 'member_account_setup',
    member_id: memberId,
    send_now: true,
  });

  return normalizeYowanOnboardingRequestSummary(payload);
};

export const createYowanAdminMemberProfileCompletionRequest = async (memberId) => {
  const payload = await postJson('/api/yowan/admin/onboarding-requests/create/', {
    request_kind: 'member_profile_completion',
    member_id: memberId,
    send_now: true,
  });

  return normalizeYowanOnboardingRequestSummary(payload);
};

export const normalizeYowanPublicOnboardingRequest = (item = {}) => {
  const requestKind = item.request_kind || item.requestKind || '';
  const targetKind = item.target_kind || item.targetKind || '';
  const status = item.status || '';
  const memberRegistrationResult = item.member_registration_result || item.memberRegistrationResult || null;

  return {
    id: item.id || '',
    requestKind,
    requestKindLabel: onboardingRequestKindLabels[requestKind] || requestKind || '-',
    targetKind,
    targetKindLabel: onboardingTargetKindLabels[targetKind] || targetKind || '-',
    email: item.email || '',
    status,
    statusLabel: onboardingRequestStatusLabels[status] || status || '-',
    expiresAt: formatYowanAdminDateTime(item.expires_at || item.expiresAt),
    memberRegistrationResult: memberRegistrationResult
      ? {
          memberId: memberRegistrationResult.member_id || memberRegistrationResult.memberId || '',
          memberAccessId: memberRegistrationResult.member_access_id || memberRegistrationResult.memberAccessId || '',
          authSubjectId: memberRegistrationResult.auth_subject_id || memberRegistrationResult.authSubjectId || '',
          passwordSetupRequestId:
            memberRegistrationResult.password_setup_request_id
            || memberRegistrationResult.passwordSetupRequestId
            || '',
        }
      : null,
  };
};

const buildYowanPublicOnboardingSubmitPayload = (values = {}) => ({
  name: values.name || '',
  name_kana: values.nameKana || values.name_kana || '',
  email: values.email || '',
  gender: values.gender || '',
  age: values.age ? Number(values.age) : null,
  phone: values.phone || '',
  postal_code: values.postalCode || values.postal_code || '',
  address: values.address || '',
  note: values.note || '',
  payload_json: {},
});

export const fetchYowanPublicOnboardingRequest = async (token) => {
  const payload = await getJson(`/api/yowan/onboarding-requests/${encodeURIComponent(token)}/`);

  return normalizeYowanPublicOnboardingRequest(payload);
};

export const submitYowanPublicOnboardingRequest = async (token, values = {}) => {
  const payload = await postJson(
    `/api/yowan/onboarding-requests/${encodeURIComponent(token)}/submit/`,
    buildYowanPublicOnboardingSubmitPayload(values),
    { auth: false },
  );

  return normalizeYowanPublicOnboardingRequest(payload);
};

const appendRecaptchaTokenIfPresent = (payload, values = {}) => {
  const recaptchaToken = values.recaptcha_token || values.recaptchaToken || '';

  return recaptchaToken
    ? {
        ...payload,
        recaptcha_token: recaptchaToken,
      }
    : payload;
};

const buildYowanPublicContactInquiryPayload = (values = {}) =>
  appendRecaptchaTokenIfPresent(
    {
      name: values.name || '',
      name_kana: values.nameKana || values.name_kana || '',
      email: values.email || '',
      phone: values.phone || '',
      message: values.message || '',
      news_opt_in: values.news_opt_in === true || values.newsOptIn === true,
    },
    values,
  );

export const submitYowanPublicContactInquiry = async (values = {}) =>
  postJson(
    '/api/yowan/contact/inquiries/create/',
    buildYowanPublicContactInquiryPayload(values),
    { auth: false },
  );

const buildYowanPublicNewsSignupPayload = (values = {}) =>
  appendRecaptchaTokenIfPresent(
    {
      email: values.email || '',
      name: values.name || '',
      name_kana: values.nameKana || values.name_kana || '',
      attended: values.attended || '',
      source: values.source || '',
      message: values.message || '',
    },
    values,
  );

export const submitYowanPublicNewsSignup = async (values = {}) =>
  postJson(
    '/api/yowan/notifications/news-signup/',
    buildYowanPublicNewsSignupPayload(values),
    { auth: false },
  );

const normalizeOptionalDateValue = (value) => {
  const normalizedValue = String(value || '').trim();

  return /^\d{4}-\d{2}-\d{2}$/.test(normalizedValue) ? normalizedValue : undefined;
};

const buildYowanPublicClassApplicationPayload = (values = {}) =>
  appendRecaptchaTokenIfPresent(
    {
      name: values.name || '',
      name_kana: values.nameKana || values.name_kana || '',
      email: values.email || '',
      phone: values.phone || '',
      message: values.message || '',
      desired_date: normalizeOptionalDateValue(values.desiredDate || values.desired_date || values.preferred_date),
      participation_scope: values.participationScope || values.participation_scope || undefined,
      slot_ids: Array.isArray(values.slotIds || values.slot_ids) ? (values.slotIds || values.slot_ids) : undefined,
    },
    values,
  );

export const submitYowanPublicClassScheduleApplication = async (scheduleId, values = {}) => {
  const payload = await postJson(
    `/api/yowan/classes/schedules/${scheduleId}/apply/`,
    buildYowanPublicClassApplicationPayload(values),
    { auth: false },
  );

  return {
    detail: payload?.detail || '',
    application: normalizeYowanClassApplication(payload?.application || payload?.class_application || payload),
    applicant: payload?.applicant || null,
  };
};

const seminarApplicationStatusLabels = {
  applied: '受付済み',
  canceled: 'キャンセル',
};

const seminarParticipationScopeLabels = {
  series: 'シリーズ通し',
  event: '講習会通し',
  slots: 'コマ参加',
  slot: 'コマ参加',
  mixed: '選択参加',
};

const uniqYowanDisplayLabels = (labels = []) => {
  const seen = new Set();

  return labels
    .map((label) => String(label || '').trim())
    .filter(Boolean)
    .filter((label) => {
      if (seen.has(label)) {
        return false;
      }
      seen.add(label);
      return true;
    });
};

const splitYowanDisplayLabelText = (label = '') => uniqYowanDisplayLabels(
  String(label || '')
    .split(/\s+\/\s+/)
    .map((value) => value.trim()),
);

const getYowanSeminarApplicationSlotDisplayLabel = (slotLike = {}) => {
  if (typeof slotLike === 'string') {
    return slotLike;
  }

  if (!slotLike || typeof slotLike !== 'object') {
    return '';
  }

  const normalizedSlot = normalizeYowanSeminarSlot(slotLike);
  const label = slotLike.label ||
    slotLike.display_label ||
    slotLike.displayLabel ||
    slotLike.selection_label ||
    slotLike.selectionLabel ||
    slotLike.slot_label ||
    slotLike.slotLabel ||
    slotLike.covered_slot_label ||
    slotLike.coveredSlotLabel ||
    '';
  const title = normalizedSlot.title ||
    slotLike.title ||
    slotLike.slot_title ||
    slotLike.slotTitle ||
    '';
  const datetimeLabel = normalizedSlot.datetimeLabel ||
    slotLike.datetime_label ||
    slotLike.datetimeLabel ||
    '';

  return label || [title, datetimeLabel].filter(Boolean).join(' / ');
};

const getYowanSeminarApplicationBreakdownEntryLabel = (entry = {}) => {
  if (typeof entry === 'string') {
    return entry;
  }

  if (!entry || typeof entry !== 'object') {
    return '';
  }

  const directLabel = entry.label ||
    entry.display_label ||
    entry.displayLabel ||
    entry.selection_label ||
    entry.selectionLabel ||
    entry.summary ||
    entry.text ||
    '';

  if (directLabel) {
    return directLabel;
  }

  const eventTitle = entry.event_title ||
    entry.eventTitle ||
    entry.seminar_title ||
    entry.seminarTitle ||
    entry.title ||
    '';
  const scope = entry.participation_scope || entry.participationScope || '';
  const rawSlots = [
    ...(Array.isArray(entry.slots) ? entry.slots : []),
    ...(Array.isArray(entry.selected_slots) ? entry.selected_slots : []),
    ...(Array.isArray(entry.selectedSlots) ? entry.selectedSlots : []),
    ...(Array.isArray(entry.covered_slots) ? entry.covered_slots : []),
    ...(Array.isArray(entry.coveredSlots) ? entry.coveredSlots : []),
    ...(Array.isArray(entry.event_slots) ? entry.event_slots : []),
    ...(Array.isArray(entry.eventSlots) ? entry.eventSlots : []),
  ];
  const slotLabels = uniqYowanDisplayLabels(rawSlots.map(getYowanSeminarApplicationSlotDisplayLabel));

  if (slotLabels.length) {
    return eventTitle ? `${eventTitle}: ${slotLabels.join('、')}` : slotLabels.join('、');
  }

  if (eventTitle && (scope === 'event' || scope === 'series')) {
    return `${eventTitle} 通し`;
  }

  return eventTitle;
};

const getYowanSeminarApplicationSelectionBreakdownLabels = (selectionBreakdown) => {
  if (!selectionBreakdown) {
    return [];
  }

  if (typeof selectionBreakdown === 'string') {
    return splitYowanDisplayLabelText(selectionBreakdown);
  }

  if (Array.isArray(selectionBreakdown)) {
    return uniqYowanDisplayLabels(selectionBreakdown.map(getYowanSeminarApplicationBreakdownEntryLabel));
  }

  if (typeof selectionBreakdown !== 'object') {
    return [];
  }

  const directLabel = selectionBreakdown.summary ||
    selectionBreakdown.label ||
    selectionBreakdown.display_label ||
    selectionBreakdown.displayLabel ||
    '';

  if (directLabel) {
    return splitYowanDisplayLabelText(directLabel);
  }

  const eventEntries = [
    ...(Array.isArray(selectionBreakdown.events) ? selectionBreakdown.events : []),
    ...(Array.isArray(selectionBreakdown.event_selections) ? selectionBreakdown.event_selections : []),
    ...(Array.isArray(selectionBreakdown.eventSelections) ? selectionBreakdown.eventSelections : []),
    ...(Array.isArray(selectionBreakdown.seminars) ? selectionBreakdown.seminars : []),
    ...(Array.isArray(selectionBreakdown.seminar_selections) ? selectionBreakdown.seminar_selections : []),
    ...(Array.isArray(selectionBreakdown.seminarSelections) ? selectionBreakdown.seminarSelections : []),
  ];
  const slotEntries = [
    ...(Array.isArray(selectionBreakdown.slots) ? selectionBreakdown.slots : []),
    ...(Array.isArray(selectionBreakdown.selected_slots) ? selectionBreakdown.selected_slots : []),
    ...(Array.isArray(selectionBreakdown.selectedSlots) ? selectionBreakdown.selectedSlots : []),
    ...(Array.isArray(selectionBreakdown.covered_slots) ? selectionBreakdown.covered_slots : []),
    ...(Array.isArray(selectionBreakdown.coveredSlots) ? selectionBreakdown.coveredSlots : []),
  ];
  const labels = [
    ...eventEntries.map(getYowanSeminarApplicationBreakdownEntryLabel),
    ...slotEntries.map(getYowanSeminarApplicationSlotDisplayLabel),
  ];

  return uniqYowanDisplayLabels(labels);
};

const getYowanSeminarApplicationCoveredSlots = (item = {}) => {
  const rawSlots = [
    ...(Array.isArray(item.slots) ? item.slots : []),
    ...(Array.isArray(item.covered_slots) ? item.covered_slots : []),
    ...(Array.isArray(item.coveredSlots) ? item.coveredSlots : []),
    ...(Array.isArray(item.selected_slots) ? item.selected_slots : []),
    ...(Array.isArray(item.selectedSlots) ? item.selectedSlots : []),
    ...(Array.isArray(item.event_slots) ? item.event_slots : []),
    ...(Array.isArray(item.eventSlots) ? item.eventSlots : []),
  ];

  const seen = new Set();

  return rawSlots
    .map((slotLike) => {
      if (typeof slotLike === 'string') {
        return {
          id: slotLike,
          title: '',
          datetimeLabel: '',
          locationLabel: '',
          roomName: '',
          label: slotLike,
        };
      }

      const normalizedSlot = normalizeYowanSeminarSlot(slotLike || {});
      const label = getYowanSeminarApplicationSlotDisplayLabel(slotLike);
      const slotId = normalizedSlot.id || slotLike.event_slot_id || slotLike.eventSlotId || slotLike.slot_id || slotLike.slotId || '';

      return {
        ...normalizedSlot,
        id: slotId,
        label: label || normalizedSlot.datetimeLabel || normalizedSlot.title || slotId,
      };
    })
    .filter((slot) => slot.id || slot.label)
    .filter((slot) => {
      const key = slot.id || slot.label;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
};

const getYowanSeminarApplicationCoveredSlotLabels = (item = {}) => {
  const coveredSlots = getYowanSeminarApplicationCoveredSlots(item);

  return uniqYowanDisplayLabels(coveredSlots.map((slot) => slot.label));
};

const normalizeYowanSeminarPublicHref = (href = '') => {
  const value = String(href || '').trim();

  if (!value) {
    return '';
  }

  const seriesMatch = value.match(/\/api\/yowan\/seminars\/series\/([^/]+)\/?$/);
  if (seriesMatch?.[1]) {
    return `/seminars/${seriesMatch[1]}`;
  }

  const seminarMatch = value.match(/\/api\/yowan\/seminars\/([^/]+)\/?$/);
  if (seminarMatch?.[1]) {
    return `/seminars/${seminarMatch[1]}`;
  }

  return value;
};

const normalizeYowanSeminarApplication = (item = {}) => {
  const applicant = item.applicant || {};
  const snapshot = getYowanSeminarApplicationSnapshot(item);
  const currentApplicant = buildYowanSeminarApplicationCurrentApplicant(item, applicant);
  const sameEmailMemberGuard = normalizeYowanSameEmailMemberGuard(
    item.same_email_member_guard || item.sameEmailMemberGuard || {},
  );
  const directMemberLink = item.direct_member_link || item.directMemberLink || {};
  const observations = getClassApplicationObservationSource(item, applicant)
    .map(normalizeYowanClassApplicationIdentityObservation);
  const slot = normalizeYowanSeminarSlot(item.event_slot || item.eventSlot || item.slot || {});
  const eventRecord = item.event || item.seminar || item.yowan_event || item.yowanEvent || {};
  const eventSeries = normalizeYowanSeminarSeries(item.event_series || item.eventSeries || item.series || {});
  const statusValue = item.status || '';
  const participationScope = item.participation_scope || item.participationScope || (slot.id ? 'slot' : '');
  const submittedName = item.submitted_name || item.submittedName || snapshot.name || snapshot.submitted_name || snapshot.submittedName || '';
  const submittedNameKana = item.submitted_name_kana || item.submittedNameKana || snapshot.name_kana || snapshot.nameKana || snapshot.submitted_name_kana || snapshot.submittedNameKana || '';
  const submittedEmail = item.submitted_email || item.submittedEmail || snapshot.email || snapshot.submitted_email || snapshot.submittedEmail || '';
  const submittedPhone = item.submitted_phone || item.submittedPhone || snapshot.phone || snapshot.submitted_phone || snapshot.submittedPhone || '';
  const applicantName = submittedName || item.name || item.applicant_name || item.applicantName || currentApplicant.name;
  const applicantNameKana = submittedNameKana || item.name_kana || item.applicant_name_kana || item.applicantNameKana || currentApplicant.nameKana;
  const applicantEmail = submittedEmail || item.email || item.applicant_email || item.applicantEmail || currentApplicant.email;
  const applicantPhone = submittedPhone || item.phone || item.applicant_phone || item.applicantPhone || currentApplicant.phone;
  const slotFee = item.applied_fee ?? item.appliedFee ?? item.slot_fee ?? item.slotFee ?? slot.slotFee;
  const selectionBreakdown = item.selection_breakdown || item.selectionBreakdown || null;
  const selectionSummary = item.selection_summary || item.selectionSummary || '';
  const links = item.links || {};
  const slotLabel = [slot.datetimeLabel, slot.locationLabel || slot.roomName]
    .filter(Boolean)
    .join(' / ');
  const selectionSummaryLabels = splitYowanDisplayLabelText(selectionSummary);
  const selectionBreakdownLabels = getYowanSeminarApplicationSelectionBreakdownLabels(selectionBreakdown);
  const coveredSlots = getYowanSeminarApplicationCoveredSlots(item);
  const coveredSlotLabels = getYowanSeminarApplicationCoveredSlotLabels(item);
  const scopeLabel = seminarParticipationScopeLabels[participationScope] || participationScope || '';
  const slotParticipationLabels = selectionSummaryLabels.length
    ? selectionSummaryLabels
    : selectionBreakdownLabels.length
      ? selectionBreakdownLabels
      : coveredSlotLabels.length
        ? coveredSlotLabels
        : slotLabel
          ? [slotLabel]
          : scopeLabel
            ? [scopeLabel]
            : [];
  const slotParticipationLabel = slotParticipationLabels.join(' / ') || '-';
  const applicantId = item.applicant_id || item.applicantId || applicant.id || '';
  const hasDistinctCurrentApplicant = Boolean(
    currentApplicant.id &&
    [currentApplicant.name, currentApplicant.nameKana, currentApplicant.email, currentApplicant.phone].some(Boolean) &&
    (
      (currentApplicant.name && applicantName && currentApplicant.name !== applicantName) ||
      (currentApplicant.nameKana && applicantNameKana && currentApplicant.nameKana !== applicantNameKana) ||
      (currentApplicant.email && applicantEmail && currentApplicant.email !== applicantEmail) ||
      (currentApplicant.phone && applicantPhone && currentApplicant.phone !== applicantPhone)
    ),
  );
  const currentApplicantDifferences = currentApplicant.id
    ? buildYowanSeminarApplicationCurrentApplicantDifferences({
      applicantName,
      applicantNameKana,
      applicantEmail,
      applicantPhone,
      currentApplicant,
    })
    : [];
  const directMemberId = directMemberLink.member_id || directMemberLink.memberId || directMemberLink.id || '';
  const directMemberHref = directMemberLink.href ||
    directMemberLink.detail_href ||
    directMemberLink.detailHref ||
    (directMemberId ? `/admin/members/${directMemberId}/edit` : '');
  const memberStatus = buildYowanSeminarApplicationMemberStatus({
    directMemberLink,
    directMemberId,
    sameEmailMemberGuard,
  });

  return {
    id: item.id || '',
    applicantId,
    applicantName,
    applicantNameKana,
    applicantEmail,
    applicantPhone,
    currentApplicant,
    currentApplicantName: currentApplicant.name,
    currentApplicantNameKana: currentApplicant.nameKana,
    currentApplicantEmail: currentApplicant.email,
    currentApplicantPhone: currentApplicant.phone,
    hasDistinctCurrentApplicant,
    currentApplicantDifferences,
    sameEmailMemberGuard,
    directMemberLink: {
      memberId: directMemberId,
      memberName: directMemberLink.member_name || directMemberLink.memberName || directMemberLink.name || '',
      memberEmail: directMemberLink.member_email || directMemberLink.memberEmail || directMemberLink.email || '',
      href: directMemberHref,
      identityProofLabel: '本人証明ではありません',
    },
    memberStatus,
    identityObservations: observations,
    hasIdentityObservations: observations.length > 0 || item.has_identity_observations === true || item.hasIdentityObservations === true,
    classificationLabel: applicant.applicant_type === 'application' || applicant.applicantType === 'application'
      ? '申込'
      : applicant.applicant_type || applicant.applicantType || '-',
    eventId: item.event_id || item.eventId || eventRecord.id || '',
    eventTitle: eventRecord.title || item.event_title || item.eventTitle || '',
    eventSubtitle: eventRecord.subtitle || item.event_subtitle || item.eventSubtitle || '',
    eventSeries,
    eventSeriesId: item.event_series_id || item.eventSeriesId || eventSeries?.id || '',
    participationScope,
    participationScopeLabel: seminarParticipationScopeLabels[participationScope] || participationScope || '-',
    selectionBreakdown,
    selectionSummary,
    slotId: item.event_slot_id || item.eventSlotId || slot.id || '',
    slotLabel,
    coveredSlots,
    slotParticipationLabels,
    slotParticipationLabel,
    slotFeeLabel: formatYowanFeeLabel(slotFee) || '-',
    desiredDate: item.desired_date || item.desiredDate || '',
    waitlistRequested: item.waitlist_requested === true || item.waitlistRequested === true,
    canCancel: item.can_cancel === true || item.canCancel === true,
    cancelUnavailableReason: item.cancel_unavailable_reason || item.cancelUnavailableReason || '',
    cancelUnavailableReasonLabel: item.cancel_unavailable_reason_label ||
      item.cancelUnavailableReasonLabel ||
      item.cancel_unavailable_label ||
      item.cancelUnavailableLabel ||
      '',
    isPast: item.is_past === true || item.isPast === true,
    isUpcoming: item.is_upcoming === true || item.isUpcoming === true,
    publicDetailHref: normalizeYowanSeminarPublicHref(
      links.public_detail ||
        links.publicDetail ||
        item.public_detail_url ||
        item.publicDetailUrl ||
        eventSeries?.publicDetailHref ||
        '',
    ),
    message: item.message || '',
    note: item.note || '',
    applicationStatus: statusValue,
    statusLabel: seminarApplicationStatusLabels[statusValue] || statusValue || '-',
    createdAt: formatYowanAdminDateTime(item.applied_at || item.appliedAt || item.created_at || item.createdAt),
    updatedAt: formatYowanAdminDateTime(item.updated_at || item.updatedAt),
    applicantHref: applicantId ? `/admin/applicants/${applicantId}` : '',
    eventHref: (item.event_id || item.eventId || eventRecord.id) ? `/admin/events/${item.event_id || item.eventId || eventRecord.id}` : '',
    detailHref: item.id ? `/admin/events/seminars/applications/${item.id}` : '',
  };
};

const normalizeSeminarGenderValue = (value) => {
  const normalizedValue = String(value || '').trim().toLowerCase();
  const genderMap = {
    男性: 'male',
    male: 'male',
    女性: 'female',
    female: 'female',
    その他: 'other',
    回答しない: 'other',
    other: 'other',
  };

  return genderMap[normalizedValue] || '';
};

const normalizeSeminarAgeValue = (value) => {
  const matched = String(value || '').match(/\d+/);

  return matched ? Number.parseInt(matched[0], 10) : undefined;
};

const buildYowanPublicSeminarApplicationPayload = (values = {}) =>
  appendRecaptchaTokenIfPresent(
    {
      name: values.name || '',
      name_kana: values.nameKana || values.name_kana || '',
      email: values.email || '',
      gender: normalizeSeminarGenderValue(values.gender),
      age: normalizeSeminarAgeValue(values.age || values.ageRange || values.age_range),
      phone: values.phone || '',
      message: values.message || '',
      desired_date: normalizeOptionalDateValue(values.desiredDate || values.desired_date || values.preferred_date),
      participation_scope: values.participationScope || values.participation_scope || undefined,
      event_selections: Array.isArray(values.eventSelections || values.event_selections)
        ? (values.eventSelections || values.event_selections)
        : undefined,
      slot_ids: Array.isArray(values.slotIds || values.slot_ids)
        ? (values.slotIds || values.slot_ids)
        : undefined,
    },
    values,
  );

export const submitYowanPublicSeminarSeriesApplication = async (seriesId, values = {}) => {
  const payload = await postJson(
    `/api/yowan/seminars/series/${seriesId}/apply/`,
    buildYowanPublicSeminarApplicationPayload(values),
    { auth: false },
  );

  return normalizeYowanSeminarApplication(payload);
};

export const submitYowanPublicSeminarSlotApplication = async (slotId, values = {}) => {
  const payload = await postJson(
    `/api/yowan/seminars/event-slots/${slotId}/apply/`,
    buildYowanPublicSeminarApplicationPayload(values),
    { auth: false },
  );

  return normalizeYowanSeminarApplication(payload);
};

export const updateYowanAdminMember = async (id, values = {}) => {
  const payload = await postJson(
    `/api/yowan/admin/members/${id}/update/`,
    buildYowanAdminMemberPayload(values),
  );

  return normalizeYowanAdminMember(payload);
};

const applicantTypeLabels = {
  inquiry: '問合せ',
  application: '申込',
};

const applicantSourceKindLabels = {
  general_inquiry: '一般問合せ',
  event_application: 'Event申込',
  yowan_class: '教室',
  yowan_class_schedule: '教室日程',
  seminar_event: 'Seminar',
  seminar_slot: 'Seminar枠',
  seminar_application: 'Seminar申込',
  blog_author: 'ブログ執筆者',
  yowan_blog_author: 'ブログ執筆者',
  blog_author_access: 'ブログ執筆者権限',
  yowan_blog_author_access: 'ブログ執筆者権限',
  blog_post: 'ブログ記事',
  yowan_blog_post: 'ブログ記事',
  manual: '管理者作成',
  other: 'その他',
};

const applicantInvitationStatusLabels = {
  not_sent: '未送信',
  sent: '送信済み',
  completed: '完了',
  expired: '期限切れ',
};

const buildYowanApplicantSourceHref = (item = {}) => {
  const sourceDetail = item.source_detail || item.sourceDetail || {};
  const detailHref = sourceDetail.detail_href || sourceDetail.detailHref || '';
  const detailId = sourceDetail.detail_id || sourceDetail.detailId || '';
  const detailKind = sourceDetail.kind || '';
  const sourceKind = item.source_kind || item.sourceKind || '';
  const sourceId = item.source_id || item.sourceId || '';

  if (detailHref) {
    return detailHref;
  }

  if (detailKind === 'class_application' && detailId) {
    return `/admin/events/classes/applications/${detailId}`;
  }

  if (detailKind === 'seminar_application' && detailId) {
    return `/admin/events/seminars/applications/${detailId}`;
  }

  if (!sourceKind || !sourceId) {
    return '';
  }

  if (sourceKind === 'yowan_class') {
    return `/admin/events/${sourceId}/edit`;
  }

  if (sourceKind === 'seminar_event') {
    return `/admin/events/${sourceId}/edit`;
  }

  return '';
};

const normalizeYowanApplicantSourceDetail = (item = {}) => {
  const sourceDetail = item.source_detail || item.sourceDetail || {};
  const kind = sourceDetail.kind || '';
  const status = sourceDetail.status || '';
  const detailId = sourceDetail.detail_id || sourceDetail.detailId || '';
  const detailHref = sourceDetail.detail_href || sourceDetail.detailHref || (
    kind === 'class_application' && detailId
      ? `/admin/events/classes/applications/${detailId}`
      : kind === 'seminar_application' && detailId
        ? `/admin/events/seminars/applications/${detailId}`
        : ''
  );

  if (!sourceDetail || !Object.keys(sourceDetail).length) {
    return {
      kind: '',
      label: '',
      detailId: '',
      detailHref: '',
      title: '',
      scheduleLabel: '',
      desiredDate: '',
      message: '',
      status: '',
      statusLabel: '',
      createdAt: '',
    };
  }

  return {
    kind,
    label: sourceDetail.label || '',
    detailId,
    detailHref,
    title: sourceDetail.title || '',
    scheduleLabel: sourceDetail.schedule_label || sourceDetail.scheduleLabel || sourceDetail.slot_label || sourceDetail.slotLabel || '',
    desiredDate: sourceDetail.desired_date || sourceDetail.desiredDate || '',
    message: sourceDetail.message || sourceDetail.note || '',
    status,
    statusLabel: sourceDetail.status_label ||
      sourceDetail.statusLabel ||
      (kind === 'seminar_application' ? seminarApplicationStatusLabels[status] : classApplicationStatusLabels[status]) ||
      status ||
      '',
    createdAt: formatYowanAdminDateTime(sourceDetail.created_at || sourceDetail.createdAt),
  };
};

const memberRegistrationReasonLabels = {
  inactive_applicant: '停止中の問い合わせ・申込者です',
  already_member: '同じメールアドレスの会員が存在するため送信できません',
  email_already_member: '同じメールアドレスの会員が存在するため送信できません',
  member_exists_for_email: '同じメールアドレスの会員が存在するため送信できません',
  pending_member_registration_request: '送信済みの会員登録フォームが有効です。本人の入力完了または有効期限切れ後に再送できます。',
  expired_member_registration_request: '前回送信した会員登録フォームは期限切れです。再送できます。',
  inactive_auth_subject: '認証アカウントが停止中です',
  inactive_member: '会員が停止中です',
  conflicting_user_subject: '同じメールアドレスの認証情報に競合があります',
};

const normalizeYowanApplicantMemberRegistration = (item = {}) => {
  if (!item) {
    return {
      canSend: false,
      reason: '',
      reasonLabel: '',
      detail: '',
      email: '',
      memberId: '',
      authSubjectId: '',
      latestRequestId: '',
      latestRequestStatus: '',
      latestRequestStatusLabel: '',
      latestRequestExpiresAt: '',
    };
  }

  const reason = item.reason || '';
  const latestRequestStatus = item.latest_request_status || item.latestRequestStatus || '';

  return {
    canSend: item.can_send === true || item.canSend === true,
    reason,
    reasonLabel: memberRegistrationReasonLabels[reason] || reason || '',
    detail: item.detail || '',
    email: item.email || '',
    memberId: item.member_id || item.memberId || '',
    authSubjectId: item.auth_subject_id || item.authSubjectId || '',
    latestRequestId: item.latest_request_id || item.latestRequestId || '',
    latestRequestStatus,
    latestRequestStatusLabel: onboardingRequestStatusLabels[latestRequestStatus] || latestRequestStatus || '',
    latestRequestExpiresAt: formatYowanAdminDateTime(
      item.latest_request_expires_at || item.latestRequestExpiresAt,
    ),
  };
};

export const normalizeYowanAdminApplicant = (item = {}) => {
  const member = normalizeYowanMemberSummary(item.member || {});
  const applicantType = item.applicant_type || item.applicantType || '';
  const sourceKind = item.source_kind || item.sourceKind || '';
  const invitationStatus = item.onboarding_invitation_status || item.onboardingInvitationStatus || '';
  const latestOnboardingRequest = normalizeYowanOnboardingRequestSummary(item.latest_onboarding_request);
  const identityObservations = getClassApplicationObservationSource(item, {})
    .map(normalizeYowanClassApplicationIdentityObservation);
  const sourceDetail = normalizeYowanApplicantSourceDetail(item);
  const sourceLabel = item.source_label || item.sourceLabel || sourceDetail.label || '';

  return {
    id: item.id || '',
    memberId: item.member_id || item.memberId || member.id || '',
    member,
    name: item.name || '',
    nameKana: item.name_kana || item.nameKana || '',
    email: item.email || '',
    gender: item.gender || '',
    age: item.age ?? '',
    phone: item.phone || '',
    applicantType,
    applicantTypeLabel: applicantTypeLabels[applicantType] || applicantType || '-',
    classificationLabel: item.member_id || item.memberId || member.id ? '会員' : '候補者',
    sourceKind,
    sourceKindLabel: applicantSourceKindLabels[sourceKind] || sourceKind || '-',
    sourceId: item.source_id || item.sourceId || '',
    sourceLabel,
    targetLabel: sourceLabel || applicantSourceKindLabels[sourceKind] || '-',
    sourceDetailLabel: sourceLabel || applicantSourceKindLabels[sourceKind] || '関連内容',
    sourceDetailHref: sourceDetail.detailHref || buildYowanApplicantSourceHref(item),
    sourceDetail,
    onboardingInvitationStatus: invitationStatus,
    onboardingInvitationStatusLabel: applicantInvitationStatusLabels[invitationStatus] || invitationStatus || '-',
    onboardingInvitationSentAt: formatYowanAdminDateTime(
      item.onboarding_invitation_sent_at || item.onboardingInvitationSentAt,
    ),
    latestOnboardingRequest,
    memberRegistration: normalizeYowanApplicantMemberRegistration(
      item.member_registration || item.memberRegistration,
    ),
    identityObservations,
    hasIdentityObservations: identityObservations.length > 0,
    note: item.note || '',
    isActive: item.is_active === true,
    promotedAt: formatYowanAdminDateTime(item.promoted_at || item.promotedAt),
    createdAt: formatYowanAdminDateTime(item.created_at || item.createdAt),
    updatedAt: formatYowanAdminDateTime(item.updated_at || item.updatedAt),
    detailHref: `/admin/applicants/${item.id}`,
  };
};

export const fetchYowanAdminApplicantList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  q = '',
  applicantType = '',
  isActive = '',
} = {}) => {
  const payload = await getJson('/api/yowan/admin/applicants/', {
    auth: true,
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
      applicant_type: applicantType || undefined,
      is_active: isActive || undefined,
    },
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizeYowanAdminApplicant),
  };
};

export const fetchYowanAdminApplicantDetail = async (id) => {
  const payload = await getJson(`/api/yowan/admin/applicants/${id}/`, { auth: true });
  return normalizeYowanAdminApplicant(payload);
};

export const fetchYowanAdminApplicantMemberRegistrationPreflight = async (id) => {
  const payload = await getJson(`/api/yowan/admin/applicants/${id}/member-registration/preflight/`, {
    auth: true,
  });

  return normalizeYowanApplicantMemberRegistration(payload.member_registration || payload);
};

export const sendYowanAdminApplicantMemberRegistration = async (id) => {
  const payload = await postJson(`/api/yowan/admin/applicants/${id}/member-registration/send/`, {});

  return normalizeYowanAdminApplicant(payload);
};

export const sendYowanAdminApplicantOnboardingInvitation = sendYowanAdminApplicantMemberRegistration;

export const fetchYowanAdminFacilityList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  q = '',
  isActive,
  active,
} = {}) => {
  const payload = await getJson('/api/yowan/admin/events/facilities/', {
    auth: true,
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
      is_active: normalizeIsActiveQueryParam(isActive ?? active),
    },
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizeYowanAdminFacility),
  };
};

export const fetchYowanAdminRoomList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  q = '',
  facilityId = '',
  isActive,
  active,
} = {}) => {
  const payload = await getJson('/api/yowan/admin/events/rooms/', {
    auth: true,
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
      facility_id: facilityId || undefined,
      is_active: normalizeIsActiveQueryParam(isActive ?? active),
    },
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizeYowanAdminRoom),
  };
};

export const fetchYowanAdminInstructorList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  q = '',
  isActive,
  active,
} = {}) => {
  const payload = await getJson('/api/yowan/admin/events/instructors/', {
    auth: true,
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
      is_active: normalizeIsActiveQueryParam(isActive ?? active),
    },
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizeYowanAdminInstructor),
  };
};

export const fetchYowanAdminOrganizerList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  q = '',
  isActive,
  active,
} = {}) => {
  const payload = await getJson('/api/yowan/admin/events/organizers/', {
    auth: true,
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
      is_active: normalizeIsActiveQueryParam(isActive ?? active),
    },
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizeYowanAdminOrganizer),
  };
};

export const createYowanAdminEventMediaAsset = async ({
  usageScope,
  image,
  altText = '',
  caption = '',
}) => {
  assertYowanImageUploadSize(image);
  assertYowanImageUploadType(image);

  const formData = new FormData();

  formData.append('usage_scope', usageScope);
  formData.append('image', image);
  formData.append('storage_key', '');
  formData.append('alt_text', altText);
  formData.append('caption', caption);
  formData.append('is_active', 'true');

  const payload = await postFormData('/api/yowan/admin/events/media/create/', formData);

  return normalizeYowanEventMediaAsset(payload);
};

const resolveYowanEventImageId = async ({
  formValues = {},
  usageScope,
  defaultAltText = '',
  fileFieldName = 'image',
  idFieldName = 'primary_image_id',
} = {}) => {
  const uploadFile = getUploadFile(formValues[fileFieldName]);

  if (!uploadFile) {
    return normalizeNullableId(formValues[idFieldName]);
  }

  const asset = await createYowanAdminEventMediaAsset({
    usageScope,
    image: uploadFile,
    altText: defaultAltText,
  });

  return asset?.id || null;
};

const resolveYowanEventPrimaryImageId = async (formValues = {}, usageScope, defaultAltText = '') =>
  resolveYowanEventImageId({
    formValues,
    usageScope,
    defaultAltText,
    fileFieldName: 'image',
    idFieldName: 'primary_image_id',
  });

const resolveYowanEventDetailTopImageId = async (formValues = {}, usageScope, defaultAltText = '') =>
  resolveYowanEventImageId({
    formValues,
    usageScope,
    defaultAltText,
    fileFieldName: 'detailTopImage',
    idFieldName: 'detail_top_image_id',
  });

const buildYowanAdminFacilityPayload = (
  values = {},
  primaryImageId = normalizeNullableId(values.primary_image_id),
) => ({
  facility_name: values.facilityName || '',
  short_name: values.shortName || '',
  address: values.address || '',
  phone: values.phone || '',
  gmap: values.gmap || '',
  url: values.url || '',
  access_note: values.accessNote || '',
  public_location_label: values.publicLocationLabel || '',
  private_location_note: values.privateLocationNote || '',
  primary_image_id: primaryImageId,
  is_active: normalizeBoolean(values.isActive),
  remarks: values.remarks || '',
  rooms: Array.isArray(values.rooms)
    ? values.rooms
        .filter((room) => room && (room.roomName || room.id))
        .map((room) => ({
          ...(normalizeNullableId(room.id) ? { id: normalizeNullableId(room.id) } : {}),
          room_name: room.roomName || '',
          capacity: Number(room.capacity || 0),
          is_active: normalizeBoolean(room.isActive),
          remarks: room.remarks || '',
        }))
    : [],
});

const buildYowanAdminRoomPayload = (values = {}) => ({
  facility_id: normalizeNullableId(values.facilityId),
  room_name: values.roomName || '',
  capacity: Number(values.capacity || 0),
  is_active: normalizeBoolean(values.isActive),
  remarks: values.remarks || '',
});

const normalizeYowanSpecialtyList = (value) => {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry).trim()).filter(Boolean);
  }

  return String(value || '')
    .split(/[、,/]/)
    .map((entry) => entry.trim())
    .filter(Boolean);
};

const buildYowanAdminInstructorPayload = (
  values = {},
  primaryImageId = normalizeNullableId(values.primary_image_id),
) => ({
  member: normalizeNullableId(values.memberId),
  person_id: normalizeNullableId(values.personId || values.person_id),
  instructor_name: values.instructorName || '',
  instructor_email: values.instructorEmail || '',
  personal_url: values.webLinkWebsite || values.personalUrl || '',
  web_links: buildYowanInstructorWebLinksPayload(values),
  role_label: values.roleLabel || '',
  specialties: normalizeYowanSpecialtyList(values.specialties),
  biography: values.biography || '',
  career: values.career || '',
  primary_image_id: primaryImageId,
  public_profile_enabled: normalizeBoolean(values.publicProfileEnabled),
  is_active: normalizeBoolean(values.isActive),
  remarks: values.remarks || '',
});

const buildYowanAdminOrganizerPayload = (
  values = {},
  primaryImageId = normalizeNullableId(values.primary_image_id),
) => ({
  member: normalizeNullableId(values.memberId),
  person_id: normalizeNullableId(values.personId || values.person_id),
  organizer_name: values.organizerName || '',
  organizer_type: values.organizerType || 'individual',
  representative_name: values.representativeName || '',
  contact_email: values.contactEmail || '',
  contact_phone: values.contactPhone || '',
  website_url: values.websiteUrl || '',
  description: values.description || '',
  primary_image_id: primaryImageId,
  public_profile_enabled: normalizeBoolean(values.publicProfileEnabled),
  is_active: normalizeBoolean(values.isActive),
  remarks: values.remarks || '',
});

export const fetchYowanAdminFacilityDetail = async (facilityId) => {
  const payload = await getJson(`/api/yowan/admin/events/facilities/${facilityId}/`, { auth: true });

  return normalizeYowanAdminFacility(payload);
};

export const createYowanAdminFacility = async (values = {}) => {
  const primaryImageId = await resolveYowanEventPrimaryImageId(values, 'facility', values.facilityName);
  const payload = await postJson(
    '/api/yowan/admin/events/facilities/create/',
    buildYowanAdminFacilityPayload(values, primaryImageId),
  );

  return normalizeYowanAdminFacility(payload);
};

export const updateYowanAdminFacility = async (facilityId, values = {}) => {
  const primaryImageId = await resolveYowanEventPrimaryImageId(values, 'facility', values.facilityName);
  const payload = await postJson(
    `/api/yowan/admin/events/facilities/${facilityId}/update/`,
    buildYowanAdminFacilityPayload(values, primaryImageId),
  );

  return normalizeYowanAdminFacility(payload);
};

export const fetchYowanAdminRoomDetail = async (roomId) => {
  const payload = await getJson(`/api/yowan/admin/events/rooms/${roomId}/`, { auth: true });

  return normalizeYowanAdminRoom(payload);
};

export const updateYowanAdminRoom = async (roomId, values = {}) => {
  const payload = await postJson(
    `/api/yowan/admin/events/rooms/${roomId}/update/`,
    buildYowanAdminRoomPayload(values),
  );

  return normalizeYowanAdminRoom(payload);
};

export const fetchYowanAdminInstructorDetail = async (instructorId) => {
  const payload = await getJson(`/api/yowan/admin/events/instructors/${instructorId}/`, { auth: true });

  return normalizeYowanAdminInstructor(payload);
};

export const createYowanAdminInstructor = async (values = {}) => {
  const primaryImageId = await resolveYowanEventPrimaryImageId(values, 'instructor', values.instructorName);
  const payload = await postJson(
    '/api/yowan/admin/events/instructors/',
    buildYowanAdminInstructorPayload(values, primaryImageId),
  );

  return normalizeYowanAdminInstructor(payload);
};

export const updateYowanAdminInstructor = async (instructorId, values = {}) => {
  const primaryImageId = await resolveYowanEventPrimaryImageId(values, 'instructor', values.instructorName);
  const payload = await postJson(
    `/api/yowan/admin/events/instructors/${instructorId}/update/`,
    buildYowanAdminInstructorPayload(values, primaryImageId),
  );

  return normalizeYowanAdminInstructor(payload);
};

export const fetchYowanAdminOrganizerDetail = async (organizerId) => {
  const payload = await getJson(`/api/yowan/admin/events/organizers/${organizerId}/`, { auth: true });

  return normalizeYowanAdminOrganizer(payload);
};

export const createYowanAdminOrganizer = async (values = {}) => {
  const primaryImageId = await resolveYowanEventPrimaryImageId(values, 'organizer', values.organizerName);
  const payload = await postJson(
    '/api/yowan/admin/events/organizers/',
    buildYowanAdminOrganizerPayload(values, primaryImageId),
  );

  return normalizeYowanAdminOrganizer(payload);
};

export const updateYowanAdminOrganizer = async (organizerId, values = {}) => {
  const primaryImageId = await resolveYowanEventPrimaryImageId(values, 'organizer', values.organizerName);
  const payload = await postJson(
    `/api/yowan/admin/events/organizers/${organizerId}/update/`,
    buildYowanAdminOrganizerPayload(values, primaryImageId),
  );

  return normalizeYowanAdminOrganizer(payload);
};

export const inviteYowanAdminInstructorAccess = async ({ instructorId, email = '', sendInvite = true } = {}) => {
  const payload = await postJson('/api/yowan/admin/access/instructor/invite/', {
    instructor_id: instructorId,
    email: email || undefined,
    send_invite: sendInvite,
  });

  return normalizeYowanStakeholderActionPayload(payload, ['instructor_access', 'instructorAccess'], 'instructor');
};

export const deactivateYowanAdminInstructorAccess = async (accessId) => {
  const payload = await postJson(`/api/yowan/admin/access/instructor/${accessId}/deactivate/`, {});

  return normalizeYowanStakeholderActionPayload(payload, ['instructor_access', 'instructorAccess'], 'instructor');
};

export const reactivateYowanAdminInstructorAccess = async (accessId) => {
  const payload = await postJson(`/api/yowan/admin/access/instructor/${accessId}/update/`, {
    is_active: true,
  });

  return normalizeYowanStakeholderActionPayload(payload, ['instructor_access', 'instructorAccess'], 'instructor');
};

export const inviteYowanAdminOrganizerAccess = async ({ organizerId, email = '', sendInvite = true } = {}) => {
  const payload = await postJson('/api/yowan/admin/access/organizer/invite/', {
    organizer_id: organizerId,
    email: email || undefined,
    send_invite: sendInvite,
  });

  return normalizeYowanStakeholderActionPayload(payload, ['organizer_access', 'organizerAccess'], 'organizer');
};

export const deactivateYowanAdminOrganizerAccess = async (accessId) => {
  const payload = await postJson(`/api/yowan/admin/access/organizer/${accessId}/deactivate/`, {});

  return normalizeYowanStakeholderActionPayload(payload, ['organizer_access', 'organizerAccess'], 'organizer');
};

export const reactivateYowanAdminOrganizerAccess = async (accessId) => {
  const payload = await postJson(`/api/yowan/admin/access/organizer/${accessId}/update/`, {
    is_active: true,
  });

  return normalizeYowanStakeholderActionPayload(payload, ['organizer_access', 'organizerAccess'], 'organizer');
};

const toYowanPositiveInteger = (value) => {
  if (value === undefined || value === null || value === '') {
    return 0;
  }

  const numberValue = Number(value);

  return Number.isNaN(numberValue) ? 0 : Math.max(0, Math.floor(numberValue));
};

const toYowanOptionalPositiveInteger = (value) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const numberValue = Number(value);

  return Number.isNaN(numberValue) ? null : Math.max(0, Math.floor(numberValue));
};

const buildYowanLocationDisclosurePayload = (values = {}) => ({
  facility_id: normalizeNullableId(values.facilityId),
  room_id: normalizeNullableId(values.roomId),
  location_display_mode: values.locationDisplayMode || 'facility',
  public_location_label: values.publicLocationLabel || '',
  public_location_note: values.publicLocationNote || '',
  private_location_note: values.privateLocationNote || '',
});

const buildYowanClassLocationDisclosurePayload = (values = {}) => ({
  facility_id: normalizeNullableId(values.facilityId),
  room_id: normalizeNullableId(values.roomId),
  location_is_public: values.locationIsPublic === undefined ? true : normalizeBoolean(values.locationIsPublic),
  public_location_label: values.publicLocationLabel || '',
  public_location_note: values.publicLocationNote || '',
  private_location_note: values.privateLocationNote || '',
});

const buildYowanClassSchedulePayload = (values = {}) => ({
  ...(normalizeNullableId(values.id) ? { id: normalizeNullableId(values.id) } : {}),
  schedule_kind: values.scheduleKind || 'weekly',
  schedule_label: values.scheduleLabel || '',
  weekday: values.weekday || 'monday',
  start_time: values.startTime || values.standardStartAt || '',
  end_time: values.endTime || values.standardEndAt || '',
  ...buildYowanClassLocationDisclosurePayload(values),
  capacity: toYowanPositiveInteger(values.capacity ?? values.standardCapacity),
  fee: toYowanPositiveInteger(values.fee ?? values.standardSlotFee),
  instructor_id: normalizeNullableId(values.instructorId ?? values.standardInstructorId),
  is_active: values.isActive === undefined ? true : normalizeBoolean(values.isActive),
  remarks: values.remarks || '',
});

const buildYowanClassSchedulesPayload = (values = {}) => {
  if (Array.isArray(values.schedules) && values.schedules.length) {
    return values.schedules
      .filter((schedule) => schedule && (schedule.startTime || schedule.standardStartAt || schedule.id))
      .map(buildYowanClassSchedulePayload);
  }

  return [
    buildYowanClassSchedulePayload({
      id: values.standardScheduleId,
      scheduleKind: values.standardScheduleKind,
      scheduleLabel: values.standardScheduleLabel,
      weekday: values.weekday,
      standardStartAt: values.standardStartAt,
      standardEndAt: values.standardEndAt,
      facilityId: values.standardFacilityId,
      roomId: values.standardRoomId,
      locationIsPublic: values.standardLocationIsPublic,
      publicLocationLabel: values.standardPublicLocationLabel,
      publicLocationNote: values.standardPublicLocationNote,
      privateLocationNote: values.standardPrivateLocationNote,
      standardCapacity: values.standardCapacity,
      standardSlotFee: values.standardSlotFee,
      standardInstructorId: values.standardInstructorId,
      isActive: values.standardIsActive === undefined ? true : values.standardIsActive,
      remarks: values.standardRemarks,
    }),
  ];
};

const buildYowanAdminClassPayload = (
  values = {},
  primaryImageId = normalizeNullableId(values.primary_image_id),
  detailTopImageId = normalizeNullableId(values.detail_top_image_id),
  { includePublicSlug = true } = {},
) => {
  const publicSlug = includePublicSlug ? resolveYowanPublicSlugPayload(values) : undefined;

  return {
    title: values.title || '',
    ...(publicSlug === undefined ? {} : { public_slug: publicSlug }),
    guidance: values.guidance || '',
    summary: values.summary || '',
    calendar_url: values.calendarUrl || '',
    show_instructor_profiles: normalizeBoolean(values.showInstructorProfiles),
    primary_image_id: primaryImageId,
    detail_top_image_id: detailTopImageId,
    organizer_id: normalizeNullableId(values.organizerId),
    is_published: normalizeBoolean(values.isPublished),
    is_active: values.isActive === undefined ? true : normalizeBoolean(values.isActive),
    schedules: buildYowanClassSchedulesPayload(values),
  };
};

const buildYowanSeminarSlotPayload = (values = {}) => ({
  ...(normalizeNullableId(values.id) ? { id: normalizeNullableId(values.id) } : {}),
  title: values.title || values.slotTitle || '',
  start_at: values.startAt || '',
  end_at: values.endAt || '',
  address: values.address || '',
  room_name: values.roomName || '',
  ...buildYowanLocationDisclosurePayload(values),
  things_to_bring: values.thingsToBring || values.things_to_bring || '',
  capacity: toYowanPositiveInteger(values.capacity),
  slot_fee: toYowanPositiveInteger(values.slotFee),
  is_open: values.isOpen === undefined ? true : normalizeBoolean(values.isOpen),
  is_active: values.isActive === undefined ? true : normalizeBoolean(values.isActive),
  show_instructor_profile: normalizeBoolean(values.showInstructorProfile),
  instructor: values.instructor || '',
  instructor_profile_id: normalizeNullableId(values.instructorId || values.instructorProfileId),
});

const buildYowanAdminSeminarPayload = (
  values = {},
  primaryImageId = normalizeNullableId(values.primary_image_id),
  { includeSeriesFields = true, includePublicSlug = true } = {},
) => {
  const seriesKind = values.seriesKind === 'grouped' ? 'grouped' : 'standalone';
  const existingSeriesId = normalizeNullableId(values.seriesId || values.series_id);
  const publicSlug = includePublicSlug ? resolveYowanPublicSlugPayload(values) : undefined;
  const seriesPayload = seriesKind === 'grouped'
    ? {
        series_title: values.seriesTitle || values.title || '',
        series_subtitle: values.seriesSubtitle || '',
        series_organizer_id: normalizeNullableId(values.seriesOrganizerId || values.organizerId),
        series_full_participation_fee: toYowanOptionalPositiveInteger(values.seriesFullParticipationFee),
      }
    : {};

  return {
    title: values.title || '',
    ...(publicSlug === undefined ? {} : { public_slug: publicSlug }),
    subtitle: values.subtitle || '',
    guidance: values.guidance || '',
    summary: values.summary || '',
    event_type: 'spot',
    schedule_rule_json: values.scheduleRuleJson || {},
    full_participation_fee: toYowanOptionalPositiveInteger(values.fullParticipationFee),
    image: values.imageUrl || '',
    primary_image_id: primaryImageId,
    organizer_id: normalizeNullableId(values.organizerId),
    ...(includeSeriesFields ? {
      ...(existingSeriesId
        ? { series_id: existingSeriesId }
        : {
            series_kind: seriesKind,
            ...seriesPayload,
          }),
    } : {}),
    is_published: normalizeBoolean(values.isPublished),
    accepts_applications: values.acceptsApplications === undefined ? true : normalizeBoolean(values.acceptsApplications),
    is_active: values.isActive === undefined ? true : normalizeBoolean(values.isActive),
    slots: Array.isArray(values.slots)
      ? values.slots
          .filter((slot) => slot && (slot.startAt || slot.id))
          .map(buildYowanSeminarSlotPayload)
      : [],
  };
};

export const createYowanAdminClass = async (values = {}) => {
  const primaryImageId = await resolveYowanEventPrimaryImageId(values, 'class', values.title);
  const detailTopImageId = await resolveYowanEventDetailTopImageId(values, 'class', values.title);
  const payload = await postJson(
    '/api/yowan/admin/classes/create/',
    buildYowanAdminClassPayload(values, primaryImageId, detailTopImageId),
  );

  invalidateYowanPublicReadCache();
  return normalizeYowanClass(payload);
};

export const updateYowanAdminClass = async (classId, values = {}) => {
  const primaryImageId = await resolveYowanEventPrimaryImageId(values, 'class', values.title);
  const detailTopImageId = await resolveYowanEventDetailTopImageId(values, 'class', values.title);
  const payload = await postJson(
    `/api/yowan/admin/classes/${classId}/update/`,
    buildYowanAdminClassPayload(values, primaryImageId, detailTopImageId, { includePublicSlug: false }),
  );

  invalidateYowanPublicReadCache();
  return normalizeYowanClass(payload);
};

export const createYowanAdminSeminar = async (values = {}) => {
  const primaryImageId = await resolveYowanEventPrimaryImageId(values, 'seminar', values.title);
  const payload = await postJson(
    '/api/yowan/admin/seminars/create/',
    buildYowanAdminSeminarPayload(values, primaryImageId),
  );

  invalidateYowanPublicReadCache();
  return normalizeYowanSeminar(payload);
};

export const createYowanAdminSeminarSeries = async (values = {}) => {
  const seminars = Array.isArray(values.seminars)
    ? values.seminars.filter((seminar) => seminar && (seminar.title || seminar.guidance || seminar.slots?.length))
    : [];
  const seminarPayloads = await Promise.all(
    seminars.map(async (seminar, index) => {
      const seminarTitle = seminar.title || `Seminar ${index + 1}`;
      const primaryImageId = await resolveYowanEventPrimaryImageId(seminar, 'seminar', seminarTitle);

      return buildYowanAdminSeminarPayload(
        {
          ...seminar,
          eventType: 'spot',
          isPublished: seminar.isPublished === undefined ? values.isPublished : seminar.isPublished,
          acceptsApplications: seminar.acceptsApplications === undefined ? true : seminar.acceptsApplications,
          isActive: seminar.isActive === undefined ? true : seminar.isActive,
        },
        primaryImageId,
        { includeSeriesFields: false },
      );
    }),
  );
  const payload = await postJson('/api/yowan/admin/seminars/create-series/', {
    series_kind: 'grouped',
    series_title: values.seriesTitle || '',
    public_slug: resolveYowanPublicSlugPayload(values) ?? '',
    series_subtitle: values.seriesSubtitle || '',
    series_summary: values.seriesSummary || '',
    series_organizer_id: normalizeNullableId(values.seriesOrganizerId),
    series_full_participation_fee: toYowanOptionalPositiveInteger(values.seriesFullParticipationFee),
    series_primary_image_id: await resolveYowanEventPrimaryImageId({
      primary_image_id: values.series_primary_image_id || values.seriesPrimaryImageId || '',
      image: values.seriesImage,
    }, 'seminar', values.seriesTitle || ''),
    seminars: seminarPayloads,
  });

  invalidateYowanPublicReadCache();
  return {
    series: normalizeYowanSeminarSeries(payload.series || {}),
    events: Array.isArray(payload.events) ? payload.events.map(normalizeYowanSeminar) : [],
  };
};

export const updateYowanAdminSeminar = async (seminarId, values = {}) => {
  const primaryImageId = await resolveYowanEventPrimaryImageId(values, 'seminar', values.title);
  const payload = await postJson(
    `/api/yowan/admin/seminars/${seminarId}/update/`,
    buildYowanAdminSeminarPayload(values, primaryImageId, { includeSeriesFields: false, includePublicSlug: false }),
  );

  invalidateYowanPublicReadCache();
  return normalizeYowanSeminar(payload);
};

export const cancelYowanAdminSeminar = async (seminarId) => {
  const payload = await postJson(`/api/yowan/admin/seminars/${seminarId}/delete/`, {});

  invalidateYowanPublicReadCache();
  return normalizeYowanSeminar(payload.event || payload.seminar || payload);
};

export const reactivateYowanAdminSeminar = async (seminarId) => {
  const payload = await postJson(`/api/yowan/admin/seminars/${seminarId}/reactivate/`, {});

  invalidateYowanPublicReadCache();
  return normalizeYowanSeminar(payload.event || payload.seminar || payload);
};

export const fetchYowanPublicClassList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_PUBLIC_PAGE_SIZE,
  q = '',
  bypassCache = false,
} = {}) => {
  const payload = await getPublicCachedJson('/api/yowan/classes/', {
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
    },
    bypassCache,
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizeYowanClass),
  };
};

export const fetchYowanPublicClassDetail = async (classId, { bypassCache = false } = {}) => {
  const payload = await getPublicCachedJson(`/api/yowan/classes/${classId}/`, { bypassCache });

  return normalizeYowanClass(payload);
};

export const hydrateYowanPublicClassDetails = async (items = [], { concurrency = 4 } = {}) => {
  const sourceItems = Array.isArray(items) ? items : [];
  const hydratedItems = [...sourceItems];
  const queue = sourceItems
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item?.id && !item.schedules?.length);
  const workerCount = Math.max(1, Math.min(concurrency, queue.length || 1));
  let cursor = 0;

  const hydrateNext = async () => {
    const currentIndex = cursor;
    cursor += 1;

    if (currentIndex >= queue.length) {
      return;
    }

    const { item, index } = queue[currentIndex];

    try {
      hydratedItems[index] = await fetchYowanPublicClassDetail(item.id);
    } catch {
      hydratedItems[index] = item;
    }

    await hydrateNext();
  };

  await Promise.all(Array.from({ length: workerCount }, hydrateNext));

  return hydratedItems;
};

export const fetchYowanPublicSeminarList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_PUBLIC_PAGE_SIZE,
  q = '',
  bypassCache = false,
} = {}) => {
  const payload = await getPublicCachedJson('/api/yowan/seminars/series/', {
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
    },
    bypassCache,
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizeYowanSeminar),
  };
};

export const fetchYowanPublicSeminarSeriesList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_PUBLIC_PAGE_SIZE,
  q = '',
  bypassCache = false,
} = {}) => {
  const payload = await getPublicCachedJson('/api/yowan/seminars/series/', {
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
    },
    bypassCache,
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizeYowanSeminar),
  };
};

const hasYowanSeminarCardDetail = (item = {}) => (
  Boolean(
    item.nextSlotLabel ||
    item.cardDatetime ||
    item.cardLocation ||
    item.cardInstructor ||
    item.slots?.some(hasYowanSeminarScheduleDisplayData) ||
    item.seminarSections?.some((section) => section.slots?.some(hasYowanSeminarScheduleDisplayData)),
  )
);

export const hydrateYowanPublicSeminarDetails = async (items = [], { concurrency = 4 } = {}) => {
  const sourceItems = Array.isArray(items) ? items : [];
  const hydratedItems = [...sourceItems];
  const queue = sourceItems
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item?.id && !hasYowanSeminarCardDetail(item));
  const workerCount = Math.max(1, Math.min(concurrency, queue.length || 1));
  let cursor = 0;

  const hydrateNext = async () => {
    const currentIndex = cursor;
    cursor += 1;

    if (currentIndex >= queue.length) {
      return;
    }

    const { item, index } = queue[currentIndex];

    try {
      hydratedItems[index] = await fetchYowanPublicSeminarDetail(item.id);
    } catch {
      hydratedItems[index] = item;
    }

    await hydrateNext();
  };

  await Promise.all(Array.from({ length: workerCount }, hydrateNext));

  return hydratedItems;
};

export const fetchYowanPublicSeminarDetail = async (seminarId, { bypassCache = false } = {}) => {
  try {
    const payload = await getPublicCachedJson(`/api/yowan/seminars/series/${seminarId}/`, { bypassCache });

    return normalizeYowanSeminar(payload, { preferredEventId: seminarId });
  } catch (error) {
    if (error?.response?.status !== 404) {
      throw error;
    }

    const legacyPayload = await getPublicCachedJson(`/api/yowan/seminars/${seminarId}/`, { bypassCache });
    const seriesId = legacyPayload?.series?.id ||
      legacyPayload?.event_series?.id ||
      legacyPayload?.eventSeries?.id ||
      legacyPayload?.series_id ||
      legacyPayload?.seriesId ||
      '';

    if (seriesId && seriesId !== seminarId) {
      const seriesPayload = await getPublicCachedJson(`/api/yowan/seminars/series/${seriesId}/`, { bypassCache });

      return normalizeYowanSeminar(seriesPayload, { preferredEventId: seminarId });
    }

    return normalizeYowanSeminar(legacyPayload, { preferredEventId: seminarId });
  }
};

export const fetchYowanPublicSeminarSeriesDetail = async (seriesId, { bypassCache = false } = {}) => {
  const payload = await getPublicCachedJson(`/api/yowan/seminars/series/${seriesId}/`, { bypassCache });

  return normalizeYowanSeminar(payload);
};

export const fetchYowanAdminClassList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  q = '',
  isActive,
  active,
} = {}) => {
  const payload = await getJson('/api/yowan/admin/classes/', {
    auth: true,
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
      is_active: normalizeIsActiveQueryParam(isActive ?? active),
    },
  });
  const envelope = normalizePaginationEnvelope(payload);
  const results = envelope.results.map(normalizeYowanClass);
  const detailsById = new Map();
  let nextIndex = 0;
  const workerCount = Math.min(4, results.length);
  const hydrateNext = async () => {
    while (nextIndex < results.length) {
      const row = results[nextIndex];
      nextIndex += 1;

      if (!row?.id) {
        continue;
      }

      try {
        const detailPayload = await getJson(`/api/yowan/admin/classes/${row.id}/`, { auth: true });
        detailsById.set(row.id, normalizeYowanClass(detailPayload));
      } catch (error) {
        detailsById.set(row.id, null);
      }
    }
  };

  if (workerCount > 0) {
    await Promise.all(Array.from({ length: workerCount }, hydrateNext));
  }

  return {
    ...envelope,
    results: results.map((row) => {
      const detail = detailsById.get(row.id);

      return detail
        ? {
            ...row,
            schedules: detail.schedules?.length ? detail.schedules : row.schedules,
            schedulesCount: row.schedulesCount || detail.schedulesCount,
            standardInstructor: row.standardInstructor || detail.standardInstructor,
            area: row.area || detail.area,
            datetime: row.datetime || detail.datetime,
          }
        : row;
    }),
  };
};

export const fetchYowanAdminSeminarList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  q = '',
  isActive,
  active,
} = {}) => {
  const payload = await getJson('/api/yowan/admin/seminars/', {
    auth: true,
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
      is_active: normalizeIsActiveQueryParam(isActive ?? active),
    },
  });
  const envelope = normalizePaginationEnvelope(payload);
  const results = envelope.results.map(normalizeYowanSeminar);
  const rowsNeedingSlotDetail = results.filter((row) => !row.nextSlotLabel || !row.standardInstructor);
  const detailsById = new Map();
  let nextIndex = 0;
  const workerCount = Math.min(4, rowsNeedingSlotDetail.length);
  const hydrateNext = async () => {
    while (nextIndex < rowsNeedingSlotDetail.length) {
      const row = rowsNeedingSlotDetail[nextIndex];
      nextIndex += 1;

      if (!row?.id) {
        continue;
      }

      try {
        const detailPayload = await getJson(`/api/yowan/admin/seminars/${row.id}/`, { auth: true });
        detailsById.set(row.id, normalizeYowanSeminar(detailPayload));
      } catch (error) {
        detailsById.set(row.id, null);
      }
    }
  };

  if (workerCount > 0) {
    await Promise.all(Array.from({ length: workerCount }, hydrateNext));
  }

  const mergeAdminSeminarListSlots = (listSlots = [], detailSlots = []) => {
    const countSlots = listSlots.filter((slot) => slot.hasApplicationsCount);

    if (!detailSlots.length || !countSlots.length) {
      return detailSlots.length ? detailSlots : listSlots;
    }

    const countSlotsById = new Map(
      countSlots
        .filter((slot) => slot.id)
        .map((slot) => [String(slot.id), slot]),
    );
    const detailSlotIds = new Set(detailSlots.map((slot) => String(slot.id)).filter(Boolean));
    const mergedDetailSlots = detailSlots.map((detailSlot) => {
      const countSlot = countSlotsById.get(String(detailSlot.id));

      if (!countSlot || detailSlot.hasApplicationsCount) {
        return detailSlot;
      }

      return {
        ...detailSlot,
        hasApplicationsCount: true,
        applicationsCount: countSlot.applicationsCount,
      };
    });
    const countOnlySlots = countSlots.filter((slot) => slot.id && !detailSlotIds.has(String(slot.id)));

    return [...mergedDetailSlots, ...countOnlySlots];
  };

  return {
    ...envelope,
    results: results.map((row) => {
      const detail = detailsById.get(row.id);

      return detail
        ? {
            ...row,
            slots: mergeAdminSeminarListSlots(row.slots, detail.slots),
            slotsCount: row.slotsCount || detail.slotsCount,
            nextSlot: row.nextSlot || detail.nextSlot,
            nextSlotLabel: row.nextSlotLabel || detail.nextSlotLabel,
            standardInstructor: row.standardInstructor || detail.standardInstructor,
            area: row.area || detail.area,
            datetime: row.datetime || detail.datetime,
          }
        : row;
    }),
  };
};

export const fetchYowanAdminSeminarSeriesList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  q = '',
  isActive,
  active,
  seriesKind = 'grouped',
} = {}) => {
  const payload = await getJson('/api/yowan/admin/seminars/series/', {
    auth: true,
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
      is_active: normalizeIsActiveQueryParam(isActive ?? active),
      series_kind: seriesKind || undefined,
    },
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizeYowanSeminarSeries).filter(Boolean),
  };
};

export const fetchYowanAdminClassDetail = async (classId) => {
  const payload = await getJson(`/api/yowan/admin/classes/${classId}/`, { auth: true });

  return normalizeYowanClass(payload);
};

export const fetchYowanAdminClassApplicationList = async ({
  classId,
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  q = '',
  status,
} = {}) => {
  const payload = await getJson(`/api/yowan/admin/classes/${classId}/applications/`, {
    auth: true,
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
      status: status || undefined,
    },
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    classRecord: payload?.class ? normalizeYowanClass(payload.class) : null,
    results: envelope.results.map(normalizeYowanClassApplication),
  };
};

export const fetchYowanAdminClassApplicationDetail = async (applicationId) => {
  const payload = await getJson(`/api/yowan/admin/classes/applications/${applicationId}/`, { auth: true });

  return normalizeYowanClassApplication(payload?.application || payload?.class_application || payload);
};

export const updateYowanAdminClassApplication = async (applicationId, values = {}) => {
  const payload = await postJson(
    `/api/yowan/admin/classes/applications/${applicationId}/update/`,
    {
      status: values.status || undefined,
      note: values.note || '',
    },
  );

  return normalizeYowanClassApplication(payload?.application || payload?.class_application || payload);
};

export const fetchYowanAdminSeminarDetail = async (seminarId) => {
  const payload = await getJson(`/api/yowan/admin/seminars/${seminarId}/`, { auth: true });

  return normalizeYowanSeminar(payload);
};

export const fetchYowanAdminSeminarSeriesDetail = async (seriesId) => {
  const payload = await getJson(`/api/yowan/admin/seminars/series/${seriesId}/`, { auth: true });

  return normalizeYowanSeminarSeries(payload);
};

export const updateYowanAdminSeminarSeries = async (seriesId, values = {}) => {
  const primaryImageId = await resolveYowanEventPrimaryImageId({
    primary_image_id: values.primary_image_id || values.primaryImageId || '',
    image: values.image,
  }, 'seminar', values.title);
  const payload = await postJson(
    `/api/yowan/admin/seminars/series/${seriesId}/update/`,
    {
      series_kind: values.seriesKind || 'grouped',
      title: values.title || '',
      subtitle: values.subtitle || '',
      summary: values.summary || '',
      organizer_id: normalizeNullableId(values.organizerId),
      full_participation_fee: toYowanOptionalPositiveInteger(values.fullParticipationFee),
      primary_image_id: primaryImageId,
      is_active: values.isActive === undefined ? true : normalizeBoolean(values.isActive),
    },
  );

  invalidateYowanPublicReadCache();
  return normalizeYowanSeminarSeries(payload);
};

export const fetchYowanAdminSeminarApplicationList = async ({
  seminarId,
  status = 'applied',
} = {}) => {
  const payload = await getJson(`/api/yowan/admin/seminars/${seminarId}/applications/`, {
    auth: true,
    params: {
      status: status || undefined,
    },
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    count: envelope.count || envelope.results.length,
    results: envelope.results.map(normalizeYowanSeminarApplication),
  };
};

export const fetchYowanAdminSeminarApplicationDetail = async (applicationId) => {
  const payload = await getJson(`/api/yowan/admin/seminars/applications/${applicationId}/`, { auth: true });

  return normalizeYowanSeminarApplication(payload?.application || payload?.seminar_application || payload);
};

export const fetchYowanRelatedSeminarList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  q = '',
} = {}) => {
  const payload = await getJson('/api/yowan/events/related/seminars/', {
    auth: true,
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
    },
  });
  const envelope = Array.isArray(payload)
    ? {
        count: payload.length,
        next: null,
        previous: null,
        results: payload,
      }
    : normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizeYowanSeminar),
  };
};

export const fetchYowanRelatedSeminarDetail = async (seminarId) => {
  const [detailResult, applicationsResult] = await Promise.allSettled([
    getJson(`/api/yowan/events/related/seminars/${seminarId}/`, { auth: true }),
    getJson(`/api/yowan/events/related/seminars/${seminarId}/applications/`, {
      auth: true,
      params: {
        page: 1,
        page_size: 1,
      },
    }),
  ]);

  if (detailResult.status === 'rejected') {
    throw detailResult.reason;
  }

  const record = normalizeYowanSeminar(detailResult.value);

  if (applicationsResult.status !== 'fulfilled') {
    return record;
  }

  const applicationsPayload = applicationsResult.value;
  const rawApplications = Array.isArray(applicationsPayload?.applications || applicationsPayload?.seminar_applications)
    ? applicationsPayload.applications || applicationsPayload.seminar_applications
    : null;
  const normalizedApplicationsEnvelope = (rawApplications || Array.isArray(applicationsPayload))
    ? null
    : normalizePaginationEnvelope(applicationsPayload);
  const applicationsEnvelope = rawApplications
    ? {
        count: rawApplications.length,
      }
    : Array.isArray(applicationsPayload)
      ? {
          count: applicationsPayload.length,
        }
      : {
          ...normalizedApplicationsEnvelope,
          count: normalizedApplicationsEnvelope.count ||
            (Array.isArray(normalizedApplicationsEnvelope.results) ? normalizedApplicationsEnvelope.results.length : 0),
        };
  const applicationsCount = Number(applicationsEnvelope.count ?? 0);

  return {
    ...record,
    applicationsCount: Math.max(record.applicationsCount || 0, applicationsCount),
  };
};

export const fetchYowanRelatedSeminarApplicationList = async ({
  seminarId,
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  status,
} = {}) => {
  const payload = await getJson(`/api/yowan/events/related/seminars/${seminarId}/applications/`, {
    auth: true,
    params: {
      page,
      page_size: pageSize,
      status: status || undefined,
    },
  });
  const rawResults = Array.isArray(payload?.applications || payload?.seminar_applications)
    ? payload.applications || payload.seminar_applications
    : null;
  const envelope = rawResults
    ? {
        count: rawResults.length,
        next: null,
        previous: null,
        results: rawResults,
      }
    : Array.isArray(payload)
      ? {
          count: payload.length,
          next: null,
          previous: null,
          results: payload,
        }
      : normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    seminar: payload?.seminar ? normalizeYowanSeminar(payload.seminar) : null,
    count: envelope.count || envelope.results.length,
    results: envelope.results.map(normalizeYowanSeminarApplication),
  };
};

const normalizeYowanRelatedProfilePayload = (payload = {}) => {
  const instructorItems = Array.isArray(payload.instructors)
    ? payload.instructors
    : Array.isArray(payload.instructor_profiles)
      ? payload.instructor_profiles
      : Array.isArray(payload.instructorProfiles)
        ? payload.instructorProfiles
      : payload.instructor
        ? [payload.instructor]
        : payload.instructor_profile
          ? [payload.instructor_profile]
          : payload.instructorProfile
            ? [payload.instructorProfile]
        : [];
  const organizerItems = Array.isArray(payload.organizers)
    ? payload.organizers
    : Array.isArray(payload.organizer_profiles)
      ? payload.organizer_profiles
      : Array.isArray(payload.organizerProfiles)
        ? payload.organizerProfiles
      : payload.organizer
        ? [payload.organizer]
        : payload.organizer_profile
          ? [payload.organizer_profile]
          : payload.organizerProfile
            ? [payload.organizerProfile]
        : [];

  return {
    instructors: instructorItems.map(normalizeYowanAdminInstructor),
    organizers: organizerItems.map(normalizeYowanAdminOrganizer),
    hasInstructorProfile: instructorItems.length > 0,
    hasOrganizerProfile: organizerItems.length > 0,
  };
};

const buildYowanRelatedInstructorProfilePayload = (values = {}) => {
  const hasWebsiteField = Object.prototype.hasOwnProperty.call(values, 'webLinkWebsite');

  return {
    instructor_name: values.instructorName || '',
    instructor_email: values.instructorEmail || '',
    personal_url: hasWebsiteField ? values.webLinkWebsite || '' : values.personalUrl || '',
    web_links: buildYowanInstructorWebLinksPayload(values),
    role_label: values.roleLabel || '',
    specialties: normalizeYowanSpecialtyList(values.specialties),
    biography: values.biography || '',
    career: values.career || '',
  };
};

const buildYowanRelatedOrganizerProfilePayload = (values = {}) => ({
  organizer_name: values.organizerName || '',
  organizer_type: values.organizerType || 'individual',
  representative_name: values.representativeName || '',
  contact_email: values.contactEmail || '',
  contact_phone: values.contactPhone || '',
  website_url: values.websiteUrl || '',
  description: values.description || '',
});

const buildYowanProfileImageFormData = (image) => {
  assertYowanImageUploadSize(image);
  assertYowanImageUploadType(image);

  const formData = new FormData();
  formData.append('image', image);

  return formData;
};

export const fetchYowanRelatedClassList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  q = '',
} = {}) => {
  const payload = await getJson('/api/yowan/events/related/classes/', {
    auth: true,
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
    },
  });
  const envelope = Array.isArray(payload)
    ? {
        count: payload.length,
        next: null,
        previous: null,
        results: payload,
      }
    : normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizeYowanClass),
  };
};

export const fetchYowanRelatedClassDetail = async (classId) => {
  const payload = await getJson(`/api/yowan/events/related/classes/${classId}/`, { auth: true });

  return normalizeYowanClass(payload);
};

export const fetchYowanRelatedClassApplicationList = async ({
  classId,
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  status,
} = {}) => {
  const payload = await getJson(`/api/yowan/events/related/classes/${classId}/applications/`, {
    auth: true,
    params: {
      page,
      page_size: pageSize,
      status: status || undefined,
    },
  });
  const rawResults = Array.isArray(payload?.applications || payload?.class_applications)
    ? payload.applications || payload.class_applications
    : null;
  const envelope = rawResults
    ? {
        count: rawResults.length,
        next: null,
        previous: null,
        results: rawResults,
      }
    : Array.isArray(payload)
      ? {
          count: payload.length,
          next: null,
          previous: null,
          results: payload,
        }
      : normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    classRecord: payload?.class ? normalizeYowanClass(payload.class) : null,
    count: envelope.count || envelope.results.length,
    results: envelope.results.map(normalizeYowanClassApplication),
  };
};

export const fetchYowanRelatedProfile = async () => {
  const payload = await getJson('/api/yowan/events/related/profile/', { auth: true });

  return normalizeYowanRelatedProfilePayload(payload);
};

export const updateYowanRelatedInstructorProfile = async (instructorId, values = {}) => {
  const payload = await postJson(
    `/api/yowan/events/related/profile/instructors/${instructorId}/update/`,
    buildYowanRelatedInstructorProfilePayload(values),
  );

  return normalizeYowanAdminInstructor(payload?.instructor || payload?.profile || payload);
};

export const updateYowanRelatedInstructorProfileImage = async (instructorId, image) => {
  const payload = await postFormData(
    `/api/yowan/events/related/profile/instructors/${instructorId}/image/`,
    buildYowanProfileImageFormData(image),
  );
  const profilePayload = payload?.instructor || payload?.instructor_profile || payload?.profile;

  if (profilePayload) {
    return normalizeYowanAdminInstructor(profilePayload);
  }

  return {
    primary_image: normalizeYowanEventMediaAsset(payload?.primary_image || payload?.image || payload?.asset || payload),
  };
};

export const updateYowanRelatedOrganizerProfile = async (organizerId, values = {}) => {
  const payload = await postJson(
    `/api/yowan/events/related/profile/organizers/${organizerId}/update/`,
    buildYowanRelatedOrganizerProfilePayload(values),
  );

  return normalizeYowanAdminOrganizer(payload?.organizer || payload?.profile || payload);
};

export const updateYowanRelatedOrganizerProfileImage = async (organizerId, image) => {
  const payload = await postFormData(
    `/api/yowan/events/related/profile/organizers/${organizerId}/image/`,
    buildYowanProfileImageFormData(image),
  );
  const profilePayload = payload?.organizer || payload?.organizer_profile || payload?.profile;

  if (profilePayload) {
    return normalizeYowanAdminOrganizer(profilePayload);
  }

  return {
    primary_image: normalizeYowanEventMediaAsset(payload?.primary_image || payload?.image || payload?.asset || payload),
  };
};

export const fetchYowanAdminSeminarParticipantMailPreview = async ({
  seminarId,
  scope = 'seminar',
  slotId,
  applicationId,
} = {}) => {
  const payload = await getJson(`/api/yowan/admin/seminars/${seminarId}/participant-mail/compose/`, {
    auth: true,
    params: {
      scope,
      slot_id: slotId || undefined,
      application_id: applicationId || undefined,
    },
  });

  return normalizeYowanSeminarParticipantMailPayload(payload);
};

export const sendYowanAdminSeminarParticipantMail = async (seminarId, values = {}) => {
  const payload = await postJson(
    `/api/yowan/admin/seminars/${seminarId}/participant-mail/send/`,
    {
      scope: values.scope || 'seminar',
      slot_id: values.slotId || values.slot_id || undefined,
      application_id: values.applicationId || values.application_id || undefined,
      subject: values.subject || '',
      body: values.body || '',
    },
  );

  return normalizeYowanSeminarParticipantMailPayload(payload);
};

export const cancelYowanAdminSeminarApplication = async (applicationId) => {
  const payload = await postJson(`/api/yowan/admin/seminars/applications/${applicationId}/cancel/`, {});

  return normalizeYowanSeminarApplication(payload);
};

export const fetchYowanCurrentMemberSeminarApplications = async ({
  period,
  status,
} = {}) => {
  const payload = await getJson('/api/yowan/member/seminar-applications/', {
    auth: true,
    params: {
      period: period || undefined,
      status: status || undefined,
    },
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    count: envelope.count || envelope.results.length,
    results: envelope.results.map(normalizeYowanSeminarApplication),
  };
};

export const cancelYowanCurrentMemberSeminarApplication = async (applicationId) => {
  const payload = await postJson(`/api/yowan/member/seminar-applications/${applicationId}/cancel/`, {});

  return normalizeYowanSeminarApplication(payload);
};

function normalizeYowanMemberSummary(item = {}) {
  return {
    id: item.id || '',
    name: item.name || '',
    nameKana: item.name_kana || item.nameKana || '',
    email: item.email || '',
  };
}

export const normalizeYowanAdminMemberAccess = (item = {}) => {
  const member = normalizeYowanMemberSummary(item.member || {});

  return {
    id: item.id || '',
    memberId: item.member_id || item.memberId || member.id || '',
    member,
    accountName: member.name || '',
    accountEmail: member.email || '',
    qualificationKey: item.qualification_key || item.qualificationKey || 'member',
    qualificationLabel: '会員',
    isActive: item.is_active === true || item.isActive === true,
    createdAt: formatYowanAdminDateTime(item.created_at || item.createdAt),
    updatedAt: formatYowanAdminDateTime(item.updated_at || item.updatedAt),
    editHref: `/admin/access/member/${item.id}/edit`,
  };
};

const buildYowanAdminMemberAccessPayload = (values = {}) => ({
  member_id: values.memberId || values.member_id || '',
  is_active: normalizeBoolean(values.isActive),
});

const memberHasEventProfile = (member = {}, profileKind = '') => {
  if (profileKind === 'instructor') {
    return Boolean(member.instructorProfile?.id);
  }

  if (profileKind === 'organizer') {
    return Boolean(member.organizerProfile?.id);
  }

  return false;
};

export const fetchYowanAdminMemberOptions = async ({
  q = '',
  pageSize = 100,
  profileKind = '',
  includeMemberId = '',
} = {}) => {
  const payload = await fetchYowanAdminMemberList({
    page: 1,
    pageSize,
    q,
    lifecycleStatus: '',
  });

  return payload.results
    .filter((member) => {
      if (!profileKind) {
        return true;
      }

      return member.id === includeMemberId || !memberHasEventProfile(member, profileKind);
    })
    .map((member) => ({
      value: member.id,
      label: `${member.name || '氏名未設定'} / ${member.email || '-'}`,
    }));
};

export const fetchYowanAdminMemberAccessList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  q = '',
  isActive = '',
  qualificationKey = 'member',
} = {}) => {
  const payload = await getJson('/api/yowan/admin/access/member/', {
    auth: true,
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
      is_active: isActive || undefined,
      qualification_key: qualificationKey || undefined,
    },
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizeYowanAdminMemberAccess),
  };
};

export const fetchYowanAdminMemberAccessDetail = async (id) => {
  const payload = await getJson(`/api/yowan/admin/access/member/${id}/`, { auth: true });
  return normalizeYowanAdminMemberAccess(payload);
};

export const createYowanAdminMemberAccess = async (values = {}) => {
  const payload = await postJson(
    '/api/yowan/admin/access/member/create/',
    buildYowanAdminMemberAccessPayload(values),
  );

  return normalizeYowanAdminMemberAccess(payload);
};

export const updateYowanAdminMemberAccess = async (id, values = {}) => {
  const payload = await postJson(
    `/api/yowan/admin/access/member/${id}/update/`,
    { is_active: normalizeBoolean(values.isActive) },
  );

  return normalizeYowanAdminMemberAccess(payload);
};

export const deactivateYowanAdminMemberAccess = async (id) => {
  const payload = await postJson(`/api/yowan/admin/access/member/${id}/deactivate/`, {});

  return normalizeYowanAdminMemberAccess(payload);
};

const operatorRoleLabels = {
  admin: '管理者',
};

const normalizeYowanOperatorProfile = (profile = {}) => ({
  id: profile.id || '',
  name: profile.name || '',
  nameKana: profile.name_kana || profile.nameKana || '',
  email: profile.email || '',
  phone: profile.phone || '',
  postalCode: profile.postal_code || profile.postalCode || '',
  address: profile.address || '',
  note: profile.note || '',
});

const resolveYowanOperatorEmail = ({ source = {}, item = {}, profile = {}, member = {} } = {}) => {
  const authSubject = source.auth_subject || source.authSubject || item.auth_subject || item.authSubject || {};
  const account = source.account || item.account || {};

  return profile.email ||
    member.email ||
    source.email ||
    source.login_email ||
    source.loginEmail ||
    source.auth_subject_email ||
    source.authSubjectEmail ||
    item.email ||
    item.login_email ||
    item.loginEmail ||
    item.auth_subject_email ||
    item.authSubjectEmail ||
    authSubject.email ||
    authSubject.login_email ||
    authSubject.loginEmail ||
    account.email ||
    account.login_email ||
    account.loginEmail ||
    '';
};

export const normalizeYowanAdminOperatorAccess = (item = {}) => {
  const source = item.operator_access || item.operatorAccess || item;
  const member = normalizeYowanMemberSummary(source.member || item.member || {});
  const profileSource = source.profile || item.profile || source.member || item.member || {};
  const profile = normalizeYowanOperatorProfile(profileSource);
  const profileMissing = source.profile_missing === true || source.profileMissing === true
    || item.profile_missing === true || item.profileMissing === true;
  const roleKey = source.role_key || source.roleKey || source.role || 'admin';
  const accountName = profile.name || member.name || '';
  const accountEmail = resolveYowanOperatorEmail({ source, item, profile, member });
  const mailSenderIdentitySource = source.mail_sender_identity || source.mailSenderIdentity || item.mail_sender_identity || item.mailSenderIdentity || null;

  return {
    id: source.id || '',
    memberId: source.member_id || source.memberId || profile.id || member.id || '',
    member,
    profile,
    profileMissing,
    accountName,
    accountEmail,
    name: profile.name,
    nameKana: profile.nameKana,
    email: profile.email,
    phone: profile.phone,
    postalCode: profile.postalCode,
    address: profile.address,
    note: profile.note,
    roleKey,
    roleLabel: operatorRoleLabels[roleKey] || roleKey || '-',
    operatorRoles: [roleKey].filter(Boolean),
    isActive: source.is_active === true || source.isActive === true,
    createdAt: formatYowanAdminDateTime(source.created_at || source.createdAt),
    updatedAt: formatYowanAdminDateTime(source.updated_at || source.updatedAt),
    editHref: `/admin/access/operator/${source.id}/edit`,
    profileEditHref: `/admin/operators/${source.id}/edit`,
    mailSenderIdentity: normalizeYowanAdminMailSenderIdentity(mailSenderIdentitySource),
  };
};

const buildYowanAdminOperatorAccessPayload = (values = {}) => {
  const operatorRoles = Array.isArray(values.operatorRoles) ? values.operatorRoles : [];
  const roleKey = values.roleKey || values.role_key || operatorRoles[0] || 'admin';

  return {
    member_id: values.memberId || values.member_id || '',
    role_key: roleKey,
    is_active: normalizeBoolean(values.isActive),
  };
};

const buildYowanAdminOperatorProfilePayload = (values = {}) => ({
  name: values.name || '',
  name_kana: values.nameKana || values.name_kana || '',
  email: values.email || '',
  phone: values.phone || '',
  postal_code: values.postalCode || values.postal_code || '',
  address: values.address || '',
  note: values.note || '',
});

export const fetchYowanAdminOperatorAccessList = async ({
  page = 1,
  pageSize = YOWAN_CONTENT_ADMIN_PAGE_SIZE,
  q = '',
  isActive = '',
  roleKey = 'admin',
} = {}) => {
  const payload = await getJson('/api/yowan/admin/access/operator/', {
    auth: true,
    params: {
      page,
      page_size: pageSize,
      q: q || undefined,
      is_active: isActive || undefined,
      role_key: roleKey || undefined,
    },
  });
  const envelope = normalizePaginationEnvelope(payload);

  return {
    ...envelope,
    results: envelope.results.map(normalizeYowanAdminOperatorAccess),
  };
};

export const fetchYowanAdminOperatorAccessDetail = async (id) => {
  const payload = await getJson(`/api/yowan/admin/access/operator/${id}/`, { auth: true });
  return normalizeYowanAdminOperatorAccess(payload);
};

export const createYowanAdminOperatorAccess = async (values = {}) => {
  const payload = await postJson(
    '/api/yowan/admin/access/operator/create/',
    buildYowanAdminOperatorAccessPayload(values),
  );

  return normalizeYowanAdminOperatorAccess(payload);
};

export const updateYowanAdminOperatorAccess = async (id, values = {}) => {
  const payload = await postJson(
    `/api/yowan/admin/access/operator/${id}/update/`,
    {
      role_key: buildYowanAdminOperatorAccessPayload(values).role_key,
      is_active: normalizeBoolean(values.isActive),
    },
  );

  return normalizeYowanAdminOperatorAccess(payload);
};

export const updateYowanAdminOperatorProfile = async (id, values = {}) => {
  const payload = await postJson(
    `/api/yowan/admin/access/operator/${id}/profile/update/`,
    buildYowanAdminOperatorProfilePayload(values),
  );

  return normalizeYowanAdminOperatorAccess(payload);
};

const normalizeYowanAdminOperatorMailSenderIdentityPayload = (payload = {}) => ({
  operatorAccessId: payload.operator_access_id || payload.operatorAccessId || '',
  identity: normalizeYowanAdminMailSenderIdentity(payload.identity),
  allowedSenderDomains: Array.isArray(payload.allowed_sender_domains)
    ? payload.allowed_sender_domains
    : Array.isArray(payload.allowedSenderDomains)
      ? payload.allowedSenderDomains
      : [],
  allowedReplyToDomains: Array.isArray(payload.allowed_reply_to_domains)
    ? payload.allowed_reply_to_domains
    : Array.isArray(payload.allowedReplyToDomains)
      ? payload.allowedReplyToDomains
      : [],
});

const buildYowanAdminOperatorMailSenderIdentityPayload = (values = {}) => ({
  label: String(values.label || '').trim(),
  display_name: String(values.displayName || values.display_name || '').trim(),
  email: String(values.email || '').trim(),
  reply_to_email: String(values.replyToEmail || values.reply_to_email || '').trim(),
  is_active: normalizeBoolean(values.isActive),
  is_default: values.isDefault !== false,
});

export const fetchYowanAdminOperatorMailSenderIdentity = async (id) => {
  const payload = await getJson(`/api/yowan/admin/access/operator/${id}/mail-sender-identity/`, { auth: true });

  return normalizeYowanAdminOperatorMailSenderIdentityPayload(payload);
};

export const updateYowanAdminOperatorMailSenderIdentity = async (id, values = {}) => {
  const payload = await postJson(
    `/api/yowan/admin/access/operator/${id}/mail-sender-identity/update/`,
    buildYowanAdminOperatorMailSenderIdentityPayload(values),
  );

  return normalizeYowanAdminOperatorMailSenderIdentityPayload(payload);
};

export const deactivateYowanAdminOperatorAccess = async (id) => {
  const payload = await postJson(`/api/yowan/admin/access/operator/${id}/deactivate/`, {});

  return normalizeYowanAdminOperatorAccess(payload);
};

const normalizeYowanCurrentMemberProfile = (item = {}) => ({
  id: item.id || '',
  name: item.name || '',
  nameKana: item.name_kana || '',
  email: item.email || '',
  phone: item.phone || '',
  postalCode: item.postal_code || '',
  address: item.address || '',
  note: item.note || '',
  profileJson: item.profile_json || {},
  lifecycleStatus: item.lifecycle_status || '',
  isActive: item.is_active === true,
  updatedAt: formatYowanAdminDateTime(item.updated_at),
});

export const isYowanMemberProfileNotFoundError = (error) =>
  error?.response?.status === 404 && error?.response?.data?.code === 'member_not_found';

export const buildYowanCurrentMemberProfilePayload = (values = {}) => ({
  name: values.name || '',
  name_kana: values.name_kana || '',
  phone: values.phone || '',
  postal_code: values.postal_code || '',
  address: values.address || '',
  note: values.note || '',
});

export const fetchYowanCurrentMemberProfile = async () => {
  const payload = await getJson('/api/yowan/member/me/', { auth: true });
  return normalizeYowanCurrentMemberProfile(payload);
};

export const updateYowanCurrentMemberProfile = async (values = {}) => {
  const payload = await patchJson('/api/yowan/member/me/', buildYowanCurrentMemberProfilePayload(values));
  return normalizeYowanCurrentMemberProfile(payload);
};

export const createYowanCurrentMemberProfile = async (values = {}) => {
  const payload = await postJson('/api/yowan/member/me/profile/', buildYowanCurrentMemberProfilePayload(values));
  return normalizeYowanCurrentMemberProfile(payload);
};

export const fetchYowanMemberNotificationPreferences = async () => {
  const payload = await getJson('/api/yowan/member/notification-preferences/', { auth: true });
  return normalizeNotificationPreferenceBundle(payload);
};

export const updateYowanMemberNotificationPreferences = async ({ news, blog } = {}) => {
  const payload = {};

  if (typeof news === 'boolean') {
    payload.news = news;
  }

  if (typeof blog === 'boolean') {
    payload.blog = blog;
  }

  const responsePayload = await postJson('/api/yowan/member/notification-preferences/', payload);
  return normalizeNotificationPreferenceBundle(responsePayload);
};

export const fetchYowanAccountNotificationPreferences = async () => {
  const payload = await getJson('/api/yowan/account/notification-preferences/', { auth: true });
  return normalizeNotificationPreferenceBundle(payload);
};

export const updateYowanAccountNotificationPreferences = async ({ news, blog } = {}) => {
  const payload = {};

  if (typeof news === 'boolean') {
    payload.news = news;
  }

  if (typeof blog === 'boolean') {
    payload.blog = blog;
  }

  const responsePayload = await postJson('/api/yowan/account/notification-preferences/', payload);
  return normalizeNotificationPreferenceBundle(responsePayload);
};

export const fetchYowanNotificationUnsubscribe = async ({ token, category } = {}) => {
  const payload = await getJson('/api/yowan/member/notification-unsubscribe/', {
    params: {
      token,
      category,
    },
  });

  return normalizeNotificationPreferenceBundle(payload);
};

export const updateYowanNotificationUnsubscribe = async ({ token, category, all = false } = {}) => {
  const payload = {
    token,
  };

  if (all) {
    payload.all = true;
  } else if (category) {
    payload.category = category;
  }

  const responsePayload = await postJson('/api/yowan/member/notification-unsubscribe/', payload, {
    auth: false,
  });

  return normalizeNotificationPreferenceBundle(responsePayload);
};

const normalizeYowanStagingDomainFallbackState = (payload = {}) => {
  const config = payload.config && typeof payload.config === 'object' ? payload.config : {};
  const enabledValue = config.enabled;
  const normalizedTarget = String(config.target || '').trim().toLowerCase();
  const allowedTargets = Array.isArray(payload.allowed_targets || payload.allowedTargets)
    ? (payload.allowed_targets || payload.allowedTargets)
      .map((target) => String(target || '').trim().toLowerCase())
      .filter(Boolean)
    : [];
  const normalizedAllowedTargets = allowedTargets.length ? allowedTargets : ['touzen', 'yowan'];

  return {
    appName: payload.app_name || payload.appName || '',
    environment: payload.environment || '',
    config: {
      enabled: enabledValue === true || enabledValue === 'true'
        ? true
        : enabledValue === false || enabledValue === 'false'
          ? false
          : null,
      target: normalizedTarget === 'yowan' || normalizedTarget === 'touzen' ? normalizedTarget : '',
    },
    allowedTargets: normalizedAllowedTargets,
  };
};

export const fetchYowanAdminStagingDomainFallbackState = async () => {
  const payload = await getJson('/api/yowan/admin/staging/herokuapp-domain-fallback/', { auth: true });
  return normalizeYowanStagingDomainFallbackState(payload);
};
